import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader } from "../../components";
import { puedeVerERv2 } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { getConvertedImporte, getRateForOpp } from "../../utils";
import { calcularComisionesFijasMensuales } from "../../utils/comisionEdificiosUtil";
import { crearConvertirAMonedaLocal } from "../../utils/convertirAMonedaLocal";
import { getOperacionesFactoring } from "../../utils/factoring";
import { interesesFactoringPorMes } from "../../utils/interesesFactoring";
import { useExchangeRates } from "../../utils/useExchangeRates";
import { BloqueBTable } from "./components/BloqueBTable";
import { BloqueCTable } from "./components/BloqueCTable";
import { BloqueDTable } from "./components/BloqueDTable";
import { CostosFijosTable } from "./components/CostosFijosTable";
import { DetalleComisionComercialModal } from "./components/DetalleComisionComercialModal";
import { DetalleComisionEdificiosModal } from "./components/DetalleComisionEdificiosModal";
import { DetalleFacturacionModal } from "./components/DetalleFacturacionModal";
import { DetalleFacturasModal } from "./components/DetalleFacturasModal";
import { DetalleGestionContenidosModal } from "./components/DetalleGestionContenidosModal";
import { DetalleHardwareIngresosModal } from "./components/DetalleHardwareIngresosModal";
import { DetalleIntermediacionModal } from "./components/DetalleIntermediacionModal";
import { EBITDAResultadoTable } from "./components/EBITDAResultadoTable";
import { ERHeader } from "./components/ERHeader";
import { InteresesModal } from "./components/InteresesModal";
import { InversionActivosTable } from "./components/InversionActivosTable";
import { TipoCambioTable } from "./components/TipoCambioTable";
// Costos fijos del v2: los 12 del v1 + Servicios Online, más las constantes que el v2
// copió del ER v1 en la Fase 1a (2026-10-01, independización — ver ./constants.js). Se
// importan de acá y NO del ER v1 — el v1 está congelado y no puede ganar una fila.
import {
  camposCostosFijosV2 as camposCostosFijos,
  nombresCostosFijosV2 as nombresCostosFijos,
  meses,
  mesesCompletos,
  windowArray,
  interesesDesglosePorPais,
  interesesHoldingPorPrestamo,
  impuestosMensualesUSD,
  interesesMensualesUSD,
} from "./constants";
import { convertirRegistrosAUSD } from "./conversionER";
import { agregarCostosER } from "./costosER";
import {
  acumular,
  combinarEnUSD,
  convertirSerieMensualAUSD,
  calcularCostoSoftwareCarteleria,
  calcularEbitdaMensual,
  calcularEbitdaMensualAnoAnterior,
  calcularCostosVariablesAnoAnterior,
  sumarUltimosNMeses,
  sumarUltimosNMesesEnUSD,
  sumarUltimosNMesesSimple,
  calcularYtdAnualizado,
  calcularVariacionPorcentual,
  calcularVariacionRatio,
  construirFilasER,
  HOJAS_ER,
  NOMBRES_COSTOS_FIJOS_ER,
  usdCuentaCostoConFallbackManual,
  usdSerieManual,
  usdSoftwareDSA,
  usdSoftwareDSC,
  usdRRHH,
} from "./estadoResultadosER";
// FASE 1a (2026-10-01): el v2 ya NO importa hooks del ER v1 — son copias propias acá abajo,
// idénticas al v1 al momento de copiarlas (docs/decisiones/2026-10-01-reingenieria-ebitda-fase1a.md).
// El ER v1 sigue congelado y no se toca.
import { useBTLCalcs } from "./hooks/useBTLCalcs";
import { useContratosCalcs } from "./hooks/useContratosCalcs";
import { useERFormatting } from "./hooks/useERFormatting";
import { useERv2Data } from "./hooks/useERv2Data";
import { useGestionCalcs } from "./hooks/useGestionCalcs";
import { useHardwareCalcs } from "./hooks/useHardwareCalcs";
import { usePublicidadCalcs } from "./hooks/usePublicidadCalcs";
import { useRRHHCalculations } from "./hooks/useRRHHCalculations";
import {
  registrosIngresoA,
  registrosHardware,
  registrosGestion,
  registrosBTL,
  registrosCostoIntermediacion,
  registrosComisionEdificiosA,
  registrosComisionComercialA,
  registrosComisionComercialVentas,
  registrosComisionComercialContratos,
  registrosComisionEdificiosBTL,
  construirVentaLeavesChile,
} from "./ingresosER";
import { interesesMensualER, registrosInteresesFactoring } from "./interesesER";
import { contarPantallasPublicidad, contarPantallasCarteleria, repartirPorPantallas } from "./pantallasOperativas";
import { agregarAMesesLocal, agregarAMesesUSD } from "./registroFinancieroER";
import styles from "./stylesERv2.module.scss";

const ZEROS = Array(12).fill(0);
const VACIO = [];

// El país sale de la sesión, igual que el v1. El año arranca en 2026 (primer año con
// contabilidad en la plataforma) pero el selector es libre.
const ANO_INICIAL = 2026;

// Desde este año el costo sale de las facturas; antes, de las tablas manuales del v1.
const ANO_INICIO_FACTURAS = 2026;

const sumar = (...arrays) => ZEROS.map((_, i) => arrays.reduce((acc, a) => acc + ((a && a[i]) || 0), 0));
const restar = (base, ...arrays) =>
  ZEROS.map((_, i) => (base[i] || 0) - arrays.reduce((a, b) => a + ((b && b[i]) || 0), 0));

export const ERv2 = () => {
  const { id: userId, pais, rol } = useAuthContext();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const [selectedYear, setSelectedYear] = useState(ANO_INICIAL);
  const [verEnDolares, setVerEnDolares] = useState(false);
  const [verChileYPeru, setVerChileYPeru] = useState(false);
  const [verUltimos12Meses, setVerUltimos12Meses] = useState(false);
  const [verAcumulados, setVerAcumulados] = useState(false);
  const [verUnitEconomics, setVerUnitEconomics] = useState(false);
  const [verInversionActivos, setVerInversionActivos] = useState(false);
  const [bloqueACollapsed, setBloqueACollapsed] = useState(true);
  const [bloqueBCollapsed, setBloqueBCollapsed] = useState(true);
  const [bloqueCCollapsed, setBloqueCCollapsed] = useState(true);
  const [bloqueDCollapsed, setBloqueDCollapsed] = useState(true);
  const [costosFijosCollapsed, setCostosFijosCollapsed] = useState(true);
  const [rrhhCollapsed, setRrhhCollapsed] = useState(true);
  // Mismo default que el v1: EBIT y Resultado arrancan colapsados.
  const [ebitCollapsed, setEbitCollapsed] = useState(true);
  const [resultadoCollapsed, setResultadoCollapsed] = useState(true);
  const [inversionCollapsed, setInversionCollapsed] = useState(true); // mismo default que el v1
  const [detalleFacturas, setDetalleFacturas] = useState(null);
  // Detalle de facturación (ventas) de la fila "A - Publicidad" por mes: { mesIndex } (o null).
  const [detalleFacturacion, setDetalleFacturacion] = useState(null);
  // Detalle de ingresos del header "C - Gestión & Contenidos" por mes: { mesIndex } (o null).
  const [detalleGestion, setDetalleGestion] = useState(null);
  // Detalle de ingresos del header "B - Hardware" por mes: { monthIndex, negocios } (o null).
  const [detalleHardwareIngresos, setDetalleHardwareIngresos] = useState(null);
  // Detalle de "Costo intermediación" (Bloque A) por mes: { mesIndex } (o null).
  const [detalleIntermediacion, setDetalleIntermediacion] = useState(null);
  // Detalle de "Comisión edificios" (Bloque A) por mes: { mesIndex } (o null).
  const [detalleComisionEdificios, setDetalleComisionEdificios] = useState(null);
  // Detalle de "Comisión comercial" (Bloque A) por mes: { mesIndex } (o null).
  const [detalleComisionComercial, setDetalleComisionComercial] = useState(null);
  // Detalle de la línea Intereses por mes: { mesIndex, mesLabel } (o null).
  const [detalleIntereses, setDetalleIntereses] = useState(null);
  // Operaciones de factoring por país (Fase 3): alimentan la línea Intereses debajo del
  // EBIT. Se cargan de ambos países porque el modo región combina Chile+Perú.
  const [opsFactoringChile, setOpsFactoringChile] = useState([]);
  const [opsFactoringPeru, setOpsFactoringPeru] = useState([]);

  // El fetch de factoring (useEffect abajo) no tenía flag de loading: arranca true y
  // pasa a false cuando resuelve (o falla), para que no dispare el render con la tabla
  // a medio calcular.
  const [isFactoringLoading, setIsFactoringLoading] = useState(true);

  const monthsToShow = selectedYear < currentYear ? 12 : currentMonth;
  // Con LTM se muestran los 12 meses de la ventana, no solo los transcurridos.
  const mesesVisibles = verUltimos12Meses ? 12 : monthsToShow;

  const {
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    users,
    proveedoresPorId,
    negociosCarteleria,
    salesBTL,
    salesBTLAnoAnterior,
    salesBTLOtroPais,
    oportunidadesChile,
    oportunidadesPeru,
    contratosChile,
    contratosPeru,
    clientesChile,
    clientesPeru,
    intermediariosChile,
    intermediariosPeru,
    edificiosChile,
    edificiosPeru,
    snapMapChile,
    snapMapPeru,
    pctMapChile,
    pctMapPeru,
    costosMapChile,
    costosMapPeru,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    salesBTLChile,
    salesBTLPeru,
    costosGestionData,
    depreciacionMensual,
    amortizacionMensual,
    depreciacionMensualAnoAnterior,
    amortizacionMensualAnoAnterior,
    depreciacionMensualChile,
    depreciacionMensualPeru,
    amortizacionMensualChile,
    amortizacionMensualPeru,
    facturasSII,
    facturasExternas,
    boletasHonorarios,
    facturasSIIChile,
    facturasSIIPeru,
    facturasExternasChile,
    facturasExternasPeru,
    boletasHonorariosChile,
    boletasHonorariosPeru,
    inversionMensual,
    inversionMensualChile,
    inversionMensualPeru,
    valorActivosMensual,
    valorActivosMensualChile,
    valorActivosMensualPeru,
    empleadosRRHHChile,
    empleadosRRHHPeru,
    manual,
    dtesSinVincular,
    siiVinculacionesMap,
    facturasEmitidasChile,
    lineaIdATipo,
    datosCombinadosCargados,
    isLoading,
  } = useERv2Data(pais, selectedYear);

  // Mismo flag que el v1 (ER/index.js:407).
  const modoCombinado = verChileYPeru && datosCombinadosCargados;

  const {
    loaded: exchangeRatesLoaded,
    dolarFallback,
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarMensualChile,
    dolarMensualPeru,
    dolarMensualOtroPais,
    dolarMensualPromedio,
    dolarMensualPromedioAnoAnterior,
    dolarMensualPromedioChile,
    dolarMensualPromedioPeru,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    getUFValue,
    backup: backupTC,
  } = useExchangeRates(pais, selectedYear);

  // Solo no-null en dev server real (localhost, `npm start`) — null en producción y en test.
  const entradasTC = backupTC ? Object.entries(backupTC) : [];
  const seriesTCRespaldo = entradasTC
    .filter(([, info]) => info.usado)
    .map(([serie, info]) => ({ serie, hasta: info.hasta }));

  // PASO 3 (Criterio 1), punto 1 — hallazgo: `formatCurrency` convierte CADA celda con
  // `dolarMensual[monthIndex]` — en modo normal `monthIndex` es el mes calendario real, pero en
  // LTM (`verUltimos12Meses`) el índice `i` que le pasa cada fila es un SLOT de la ventana (que
  // puede representar un mes del año ANTERIOR), no el mes calendario de `dolarMensual`. El TC
  // crudo del slot ≠ el TC real del mes que ese slot representa — mismo patrón H4/H5 (ya
  // corregido para Intereses/EBIT/Resultado con arrays USD precomputados), pero acá se corrige
  // en la RAÍZ: se le pasa a `useERFormatting` el TC YA VENTANEADO (mismo mecanismo que `vTC`,
  // más abajo, pero movido acá arriba porque `dolarMensual`/`dolarMensualAnoAnterior` y
  // `verUltimos12Meses`/`monthsToShow` ya están disponibles) — así TODA celda de TODA fila que
  // pase por `formatCurrency` queda corregida de una vez, sin tocar cada fila una por una.
  const dolarMensualParaFormato = verUltimos12Meses
    ? windowArray(dolarMensual, dolarMensualAnoAnterior, monthsToShow)
    : dolarMensual;

  const { moneda, formatCurrency, localString } = useERFormatting(
    pais,
    verEnDolares,
    false,
    dolarMensualParaFormato,
    monthsToShow,
    dolarFallback
  );

  // Misma fuente que el v1 (src/utils/convertirAMonedaLocal.js). La necesitan los
  // bloques B y C para reconocer el INGRESO del mes.
  const convertirAMonedaLocal = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValue]
  );

  // Variantes POR PAÍS para el modo región. `getDolarValue` está atado al país de la sesión
  // (useExchangeRates.js:76), así que convertir las externas en US$ de Chile desde una sesión
  // Perú las pasaría por el TC del sol y las aplastaría a ~1/253 de su valor. El hook ya
  // expone `getDolarValueChile` / `getDolarValuePeru`: cada pata usa el suyo.
  const convertirAMonedaLocalChile = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue: getDolarValueChile,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValueChile]
  );
  const convertirAMonedaLocalPeru = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue: getDolarValuePeru,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValuePeru]
  );

  // Bloque C: antes de 2026 se lee la tabla manual como el v1; desde 2026 va en cero
  // (el software de carteleria sale del estimado y "Otros" no tiene cuenta que lo
  // alimente). Una sola fuente por mes.
  const getCostoGestionValue = useCallback(
    // Misma firma y mismos filtros que el v1 (ER/index.js:328), incluido negocio_id:
    // sin ese filtro se toma la primera fila del mes y el valor sale distinto.
    (monthIndex, field, negocioId = null) => {
      if (selectedYear >= ANO_INICIO_FACTURAS) return 0;
      const row = (costosGestionData || []).find(
        (c) => Number(c.mes) === monthIndex + 1 && Number(c.ano) === selectedYear && c.negocio_id === negocioId
      );
      return Number(row?.[field]) || 0;
    },
    [costosGestionData, selectedYear]
  );

  // FASE 1b (2026-10-01): gemelo de arriba para el año anterior — lo necesita
  // `calcularCostosVariablesAnoAnterior` (PASO 3.7) para `ebitdaMensualAnoAnterior`. Misma
  // firma y mismo corte ANO_INICIO_FACTURAS que la
  // versión del año en curso; fuente propia del v2 (`manual.gestionAA`, ya cargada por
  // useERv2Data), nunca el v1.
  const getCostoGestionValueAnoAnterior = useCallback(
    (monthIndex, field, negocioId = null) => {
      const anoAnterior = selectedYear - 1;
      if (anoAnterior >= ANO_INICIO_FACTURAS) return 0;
      const row = (manual.gestionAA || []).find(
        (c) => Number(c.mes) === monthIndex + 1 && Number(c.ano) === anoAnterior && c.negocio_id === negocioId
      );
      return Number(row?.[field]) || 0;
    },
    [manual.gestionAA, selectedYear]
  );

  // FASE 5a (reingeniería del EBITDA, 2026-10-06): hoisted desde donde vivían de siempre (justo
  // antes de `construirLeavesUsdCriterio1Chile`, mucho más abajo) — `chileVentaLeavesSesion`
  // (siguiente bloque) los necesita ANTES que los hooks viejos de A-D (`usePublicidadCalcs`/etc,
  // sección siguiente). Pura relocación, cero cambio de valor: mismas 2 expresiones de siempre.
  const dolarFallbackPais = dolarFallback[pais];
  const contratosSesion = pais === "Chile" ? contratosChile : contratosPeru;

  // FASE 5a+5b — fuente ÚNICA (`construirVentaLeavesChile`, `ingresosER.js`) de las 8 filas-hoja
  // de A/B/C/D "atadas a la venta" (ingreso ABCD + Costo intermediación + Comisión edificios A +
  // Comisión comercial A + Comisión edificios BTL), SOLO Chile: antes, el `.local` de estas filas
  // salía de los hooks viejos (`usePublicidadCalcs`/`useContratosCalcs`/`useHardwareCalcs`/
  // `useGestionCalcs`/`useBTLCalcs`) y el `.usd` de estas mismas funciones de registros —
  // divergían en silencio apenas una cambiara sin la otra (como pasó con PASO 5/3.9, que solo
  // tocó el `.usd`). FASE 5b (este commit) retira el `usarSII: false` explícito de 5a: el default
  // de la función (`ano >= 2026`) activa el cutover — Chile 2026+ factura desde SII vinculada,
  // 2025 y antes sin cambio, en `.local` Y `.usd` A LA VEZ (misma fuente, 10.1).
  // Perú no cambia (sigue en los hooks viejos, `pais !== "Chile"` → `null`).
  const chileVentaLeavesSesion = useMemo(() => {
    if (pais !== "Chile") return null;
    return construirVentaLeavesChile(selectedYear, {
      oportunidadesParaPComun: oportunidades,
      contratosParaPComun: contratosSesion,
      salesBTLParaPComun: salesBTL,
      negociosParaPComun: negociosCarteleria,
      getDolarValueParaPComun: getDolarValue,
      dolarFallbackPaisParaPComun: dolarFallbackPais,
      clientesParaPComun: clientes,
      intermediariosParaPComun: intermediarios,
      edificiosParaPComun: edificios,
      pctMapParaPComun: pctMap,
      snapMapParaPComun: snapMap,
      costosMapParaPComun: costosMap,
      getUFValue,
      convertirAMonedaLocal,
      dolarMensualPeru,
      dolarFallback,
      facturasEmitidasChile,
      lineaIdATipo,
    });
  }, [
    pais,
    selectedYear,
    oportunidades,
    contratosSesion,
    salesBTL,
    negociosCarteleria,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    edificios,
    pctMap,
    snapMap,
    costosMap,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
    facturasEmitidasChile,
    lineaIdATipo,
  ]);

  // ─── A — Publicidad (pauta) ─────────────────────────────────
  const {
    valoresMensuales: pubIngresos,
    valoresMensualesAnoAnterior: pubIngresosAA,
    valoresMensualesComisionAgenciaAnoAnterior: pubAgenciaAA,
    valoresMensualesComisionBrokerAnoAnterior: pubBrokerAA,
    valoresMensualesTakeAnoAnterior: pubTakeAA,
    valoresMensualesApxAnoAnterior: pubApxAA,
    valoresMensualesComisionesAnoAnterior: pubEdificiosAA,
    valoresMensualesComisionComercialAnoAnterior: pubComercialAA,
    valoresMensualesComisionAgencia: pubAgencia,
    valoresMensualesComisionBroker: pubBroker,
    valoresMensualesTake: pubTake,
    valoresMensualesApx: pubApx,
    valoresMensualesComisiones: pubEdificios,
    valoresMensualesComisionComercial: pubComercial,
  } = usePublicidadCalcs({
    pais,
    selectedYear,
    monthsToShow,
    currentYear,
    modoCombinado,
    oportunidades,
    oportunidadesChile,
    oportunidadesPeru,
    clientes,
    clientesChile,
    clientesPeru,
    intermediarios,
    intermediariosChile,
    intermediariosPeru,
    edificios,
    edificiosChile,
    edificiosPeru,
    snapMap,
    snapMapChile,
    snapMapPeru,
    pctMap,
    pctMapChile,
    pctMapPeru,
    costosMap,
    costosMapChile,
    costosMapPeru,
    users,
    isOportunidadesLoading: isLoading,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
  });

  // ─── Contratos multi-mes (se suman a publicidad) ────────────
  const {
    ingresosContratosMensual,
    ingresosContratosMensualAnoAnterior,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionAgenciaContratosMensualAnoAnterior,
    comisionBrokerContratosMensualAnoAnterior,
    comisionApxContratosMensualAnoAnterior,
    comisionComercialContratosMensualAnoAnterior,
    comisionEdificiosContratosMensual,
    comisionAgenciaContratosMensual,
    comisionBrokerContratosMensual,
    comisionApxContratosMensual,
    comisionComercialContratosMensual,
  } = useContratosCalcs({
    contratosChile,
    contratosPeru,
    selectedYear,
    modoCombinado,
    dolarFallback,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    dolarMensualChile,
    dolarMensualPeru,
    pais,
    edificios,
    edificiosChile,
    edificiosPeru,
    snapMap,
    snapMapChile,
    snapMapPeru,
    pctMap,
    pctMapChile,
    pctMapPeru,
    clientes,
    clientesChile,
    clientesPeru,
    intermediarios,
    intermediariosChile,
    intermediariosPeru,
  });

  // ─── B — Hardware (ingresos + los 4 costos manuales, incluido año anterior) ───
  const {
    ingresosHardwareMensual: ingresosHardwareMensualHook,
    ingresosHardwareMensualAnoAnterior,
    compraHardwareMensual,
    instalacionMensual,
    materialesMensual,
    otrosCostosMensual,
    // FASE 1b (2026-10-01): el hook v1 (retirado en 3.7) YA los calculaba (recibe
    // `costosHardwareFlexAnoAnterior` más abajo) — index.js no los destructuraba y el hook
    // recibía ZEROS en su lugar.
    // Se renombran "Manual" acá porque todavía falta aplicarles el mismo switch porAno que ya
    // aplica el año en curso (ver bCompraHardware/bInstalacion/bMateriales/bOtrosHardware).
    compraHardwareMensualAnoAnterior: compraHardwareMensualAnoAnteriorManual,
    instalacionMensualAnoAnterior: instalacionMensualAnoAnteriorManual,
    materialesMensualAnoAnterior: materialesMensualAnoAnteriorManual,
    otrosCostosMensualAnoAnterior: otrosCostosMensualAnoAnteriorManual,
    compraHardwareMensualChile,
    instalacionMensualChile,
    materialesMensualChile,
    otrosCostosMensualChile,
    compraHardwareMensualPeru,
    instalacionMensualPeru,
    materialesMensualPeru,
    otrosCostosMensualPeru,
  } = useHardwareCalcs({
    pais,
    selectedYear,
    monthsToShow,
    negociosCarteleria,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    costosHardwareFlex: manual.hardwareFlex || VACIO,
    costosHardwareFlexAnoAnterior: manual.hardwareFlexAA || VACIO,
    costosHardwareFlexChile: manual.porPais?.Chile?.hardwareFlex || VACIO,
    costosHardwareFlexPeru: manual.porPais?.["Perú"]?.hardwareFlex || VACIO,
    costosGestionData,
    getUFValue,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
  });

  // ─── C — Gestión & Contenidos (solo ingresos) ───────────────
  const {
    ingresosGestionContenidosMensual: ingresosGestionContenidosMensualHook,
    ingresosGestionContenidosMensualAnoAnterior,
    detalleGestionContenidosPorMes,
    ytdIngresosGestionContenidos,
    calcularCostoSoftwareEstimado,
    calcularCostoSoftwareEstimadoAnoAnterior,
    ytdSoftwareDS,
    ytdOtros,
  } = useGestionCalcs({
    pais,
    selectedYear,
    monthsToShow,
    currentYear,
    modoCombinado,
    negociosCarteleria,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    clientes,
    edificios,
    costosGestionData,
    costosPublicidadData: Array(12).fill(null),
    convertirAMonedaLocal,
    getCostoGestionValue,
    getDolarValue,
    dolarMensualPeru,
    dolarMensualChile,
    dolarMensualPromedio,
    dolarMensualPromedioAnoAnterior,
    dolarFallback,
    modalRefresh: 0,
  });

  // ─── D — BTL (ingresos + comisión edificios + otros costos manuales, incl. año anterior) ───
  const {
    ingresosBTLMensual: ingresosBTLMensualHook,
    ingresosBTLMensualAnoAnterior,
    comisionEdificiosBTLMensual: comisionEdificiosBTLMensualHook,
    comisionEdificiosBTLMensualAnoAnterior,
    otrosCostosBTLMensual,
    // FASE 1b (2026-10-01): mismo caso que el hardware — el hook ya lo calculaba (recibe
    // `salesBTLAnoAnterior`), index.js no lo destructuraba. Falta el switch porAno (ver otrosBTL).
    otrosCostosBTLMensualAnoAnterior: otrosCostosBTLMensualAnoAnteriorManual,
    otrosCostosBTLMensualChile,
    otrosCostosBTLMensualPeru,
  } = useBTLCalcs({
    salesBTL,
    salesBTLAnoAnterior,
    salesBTLChile,
    salesBTLPeru,
    salesBTLOtroPais,
    selectedYear,
    monthsToShow,
    modoCombinado,
  });

  // ─── RRHH ───────────────────────────────────────────────────
  const {
    empleadosRRHH,
    isEmpleadosRRHHLoading,
    getSueldoBaseVigente,
    getSueldoBaseVigenteParaAno,
    getSueldoBaseVigenteChileFijo,
    rrhhMensualConBeneficios,
    rrhhMensualConBeneficiosAnoAnterior,
    rrhhMensualPeru,
  } = useRRHHCalculations(pais, selectedYear, monthsToShow, empleadosRRHHChile, empleadosRRHHPeru);

  // ─── Combinaciones (mismo orden que ER/index.js:1585-1728) ──
  const comisionesFijasMensual = useMemo(
    () => calcularComisionesFijasMensuales(edificios, selectedYear, getDolarValue),
    [edificios, selectedYear, getDolarValue]
  );
  // FASE 1b (2026-10-01): gemelo de `comisionesFijasMensual`, un año atrás — faltaba por
  // completo en `valoresMensualesComisionesAnoAnterior` (el hook v1, retirado en 3.7, recibía
  // solo pubEdificiosAA + comisionEdificiosContratosMensualAnoAnterior, sin la pata de comisión
  // fija por edificio). Misma función pura `calcularComisionesFijasMensuales`, year-1.
  const comisionesFijasMensualAnoAnterior = useMemo(
    () => calcularComisionesFijasMensuales(edificios, selectedYear - 1, getDolarValue),
    [edificios, selectedYear, getDolarValue]
  );

  const facturacionAHook = useMemo(
    () => sumar(pubIngresos, ingresosContratosMensual),
    [pubIngresos, ingresosContratosMensual]
  );

  const costoIntermediacionHook = useMemo(
    () =>
      sumar(
        pubAgencia,
        comisionAgenciaContratosMensual,
        pubBroker,
        comisionBrokerContratosMensual,
        pubTake,
        pubApx,
        comisionApxContratosMensual
      ),
    [
      pubAgencia,
      comisionAgenciaContratosMensual,
      pubBroker,
      comisionBrokerContratosMensual,
      pubTake,
      pubApx,
      comisionApxContratosMensual,
    ]
  );

  const comisionEdificiosHook = useMemo(
    () => sumar(pubEdificios, comisionEdificiosContratosMensual, comisionesFijasMensual),
    [pubEdificios, comisionEdificiosContratosMensual, comisionesFijasMensual]
  );

  const comisionComercialHook = useMemo(
    () => sumar(pubComercial?.valores, comisionComercialContratosMensual),
    [pubComercial, comisionComercialContratosMensual]
  );

  // FASE 5a (2026-10-06) — override para Chile: estas 8 variables (4 de acá + las 4 que siguen,
  // `ingresosHardwareMensual`/`ingresosGestionContenidosMensual`/`ingresosBTLMensual`/
  // `comisionEdificiosBTLMensual`, cada una renombrada a `*Hook` en su declaración/destructure de
  // origen) dejan de ser el hook viejo para Chile — pasan a `chileVentaLeavesSesion.local` (misma
  // fuente que ya usa el `.usd` de `filas`, 10.1). Perú sigue en el hook de siempre, sin cambio.
  const facturacionA = pais === "Chile" ? chileVentaLeavesSesion.local["A - Publicidad"] : facturacionAHook;
  const costoIntermediacion =
    pais === "Chile" ? chileVentaLeavesSesion.local["A - Publicidad / Costo intermediación"] : costoIntermediacionHook;
  const comisionEdificios =
    pais === "Chile" ? chileVentaLeavesSesion.local["A - Publicidad / Comisión edificios"] : comisionEdificiosHook;
  const comisionComercial =
    pais === "Chile" ? chileVentaLeavesSesion.local["A - Publicidad / Comisión comercial"] : comisionComercialHook;
  const ingresosHardwareMensual =
    pais === "Chile" ? chileVentaLeavesSesion.local["B - Hardware"] : ingresosHardwareMensualHook;
  const ingresosGestionContenidosMensual =
    pais === "Chile" ? chileVentaLeavesSesion.local["C - Gestión & Contenidos"] : ingresosGestionContenidosMensualHook;
  const ingresosBTLMensual =
    pais === "Chile" ? chileVentaLeavesSesion.local["D - BTL & otros"] : ingresosBTLMensualHook;
  const comisionEdificiosBTLMensual =
    pais === "Chile"
      ? chileVentaLeavesSesion.local["D - BTL & otros / Comisión edificios"]
      : comisionEdificiosBTLMensualHook;

  const facturacion = useMemo(
    () => sumar(facturacionA, ingresosHardwareMensual, ingresosGestionContenidosMensual, ingresosBTLMensual),
    [facturacionA, ingresosHardwareMensual, ingresosGestionContenidosMensual, ingresosBTLMensual]
  );

  // ─── MOTOR ÚNICO DE COSTOS (fase 2) ─────────────────────────
  // Todas las líneas de costo que el v1 carga a mano salen de acá. Un solo agregado
  // del que leen fila, Contribución, Costos Variables, Costos Fijos, EBITDA y YTD.
  const costos = useMemo(
    () =>
      agregarCostosER({
        facturasSII,
        facturasExternas,
        boletasHonorarios,
        ano: selectedYear,
        pais,
        convertirAMonedaLocal,
        proveedoresPorId,
      }),
    [facturasSII, facturasExternas, boletasHonorarios, selectedYear, pais, convertirAMonedaLocal, proveedoresPorId]
  );

  // ─── FUENTE DEL COSTO POR AÑO ───────────────────────────────
  // El unico delta del v2 contra el v1: desde 2026-01-01 los costos salen de las
  // facturas; 2025 y para atras siguen saliendo de las tablas manuales, con la misma
  // logica del v1 (proyeccion incluida). Una sola fuente por mes, sin doble conteo.
  const usaFacturas = (ano) => ano >= ANO_INICIO_FACTURAS;

  // Proyeccion de los conceptos manuales del bloque A (ER/index.js:1261).
  const proyectarPublicidad = useCallback((data, dataAA, campo, monthIndex) => {
    const valores = [];
    for (let i = 0; i < monthIndex; i++) {
      const v = data?.[i]?.[campo];
      if (v != null && Number(v) > 0) valores.push(Number(v));
    }
    if (dataAA?.length > 0) {
      for (let i = 11; i >= 0 && valores.length < 12; i--) {
        const v = dataAA[i]?.[campo];
        if (v != null && Number(v) > 0) valores.push(Number(v));
      }
    }
    if (valores.length === 0) return 0;
    return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
  }, []);

  const serieManualPublicidad = useCallback(
    (data, dataAA, campo) =>
      ZEROS.map((_, i) => {
        const row = data?.[i];
        if (row && row[campo] != null) return Number(row[campo]) || 0;
        return proyectarPublicidad(data, dataAA, campo, i);
      }),
    [proyectarPublicidad]
  );

  // Proyeccion de los costos fijos (ER/hooks/useCostosFijosCalcs.js:39 y :128).
  const serieManualFija = useCallback(
    (raw, data, dataAA, campo) =>
      ZEROS.map((_, i) => {
        const existe = (raw || []).some((r) => Number(r.mes) === i + 1 && r[campo] != null);
        if (existe) return Number(data?.[i]?.[campo]) || 0;
        const valores = [];
        for (let j = 0; j < i; j++) {
          const v = data?.[j]?.[campo];
          if (v > 0) valores.push(v);
        }
        if (dataAA?.length > 0) {
          for (let j = 11; j >= 0 && valores.length < 12; j--) {
            const v = dataAA[j]?.[campo];
            if (v > 0) valores.push(v);
          }
        }
        if (valores.length === 0) return 0;
        return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
      }),
    []
  );

  // Agregados POR PAÍS para el modo región. La vista de país único sigue usando `costos`
  // (la sesión); estos dos existen para que la pata de Chile del combinado tome las facturas
  // de Chile aunque se entre desde visiona.pe, donde `pais` es "Perú" (auth.js deriva el país
  // del hostname). Perú no tiene SII/SUNAT: hoy su agregado da 0, pero se arma igual en vez
  // de hardcodear ZEROS, para que el día que tenga facturas entre solo.
  const costosChile = useMemo(
    () =>
      agregarCostosER({
        facturasSII: facturasSIIChile,
        facturasExternas: facturasExternasChile,
        boletasHonorarios: boletasHonorariosChile,
        ano: selectedYear,
        pais: "Chile",
        convertirAMonedaLocal: convertirAMonedaLocalChile,
        proveedoresPorId,
      }),
    [
      facturasSIIChile,
      facturasExternasChile,
      boletasHonorariosChile,
      selectedYear,
      convertirAMonedaLocalChile,
      proveedoresPorId,
    ]
  );
  const costosPeru = useMemo(
    () =>
      agregarCostosER({
        facturasSII: facturasSIIPeru,
        facturasExternas: facturasExternasPeru,
        boletasHonorarios: boletasHonorariosPeru,
        ano: selectedYear,
        pais: "Perú",
        convertirAMonedaLocal: convertirAMonedaLocalPeru,
        proveedoresPorId,
      }),
    [
      facturasSIIPeru,
      facturasExternasPeru,
      boletasHonorariosPeru,
      selectedYear,
      convertirAMonedaLocalPeru,
      proveedoresPorId,
    ]
  );

  // Mismo motor, un anio atras: la ventana LTM necesita los costos del anio anterior.
  const costosAA = useMemo(
    () =>
      agregarCostosER({
        facturasSII,
        facturasExternas,
        boletasHonorarios,
        ano: selectedYear - 1,
        pais,
        convertirAMonedaLocal,
        proveedoresPorId,
      }),
    [facturasSII, facturasExternas, boletasHonorarios, selectedYear, pais, convertirAMonedaLocal, proveedoresPorId]
  );

  // Bloque A. Software DS del v1 = max(0, total − estimado cartelería); eliminado el
  // estimado de 10,99 USD/pantalla (spec fase 2), el término restado es 0 → A se queda
  // con las facturas de la cuenta y la fila de C, cuya única fuente era ese estimado,
  // queda en 0. Misma fórmula del v1, con el término anulado.
  // Por año: facturas desde 2026, tablas manuales antes (con la proyeccion del v1).
  const porAno = useCallback(
    (ano, desdeFacturas, desdeManual) => (usaFacturas(ano) ? desdeFacturas : desdeManual),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const manualServiciosInternet = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "servicios_internet");
  const manualMantenimiento = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "mantenimiento");
  const manualSoftwareDSTotal = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "software_ds");

  // FASE 1b (2026-10-01): gemelo de los 3 de arriba, un año atrás, para
  // `costosPublicidadDataAnoAnterior` (lo consume `calcularCostosVariablesAnoAnterior`, PASO
  // 3.7) — REUSA `serieManualPublicidad` (línea 648), misma
  // función que usa la vista del año en curso cuando `data` ya tiene el mes real (no reinventa
  // la proyección, 10.1). `data` = manual.publicidadAA (el año anterior); sin `dataAA` propio
  // de DOS años atrás (`selectedYear-2`, no se carga hoy) — no hace falta: `serieManualPublicidad`
  // primero promedia los meses YA reales del propio `data` (igual que hace la vista 2025 directa
  // con sus propios meses previos) y solo cae a `dataAA` si NINGÚN mes de `data` tiene valor, caso
  // que no se da hoy (verificado: diff-cero contra la tabla real, único hallazgo real era diciembre
  // Perú, resuelto por esta misma proyección). Si algún día esto deja de alcanzar, hace falta
  // cargar `manual.publicidadAAAA` (selectedYear-2) — anotado, no implementado.
  const manualServiciosInternetAA = serieManualPublicidad(manual.publicidadAA, VACIO, "servicios_internet");
  const manualMantenimientoAA = serieManualPublicidad(manual.publicidadAA, VACIO, "mantenimiento");
  const manualSoftwareDSTotalAA = serieManualPublicidad(manual.publicidadAA, VACIO, "software_ds");
  const costosPublicidadDataAnoAnteriorProyectado = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        servicios_internet: manualServiciosInternetAA[i],
        software_ds: manualSoftwareDSTotalAA[i],
        mantenimiento: manualMantenimientoAA[i],
      })),
    [manualServiciosInternetAA, manualSoftwareDSTotalAA, manualMantenimientoAA]
  );

  const aServiciosInternet = porAno(selectedYear, costos.porCuenta("servicios_internet"), manualServiciosInternet);
  // FASE E2c: gemelos año-anterior de Servicios internet/Mantenimiento — los necesita la
  // ventana LTM (`vServiciosInternet`/`vMantenimiento`) y el total `costosManualesAAA`, un
  // solo lugar para los dos consumidores (10.1).
  const aServiciosInternetAnoAnterior = porAno(
    selectedYear - 1,
    costosAA.porCuenta("servicios_internet"),
    manualServiciosInternetAA
  );
  const aMantenimientoAnoAnterior = porAno(
    selectedYear - 1,
    costosAA.porCuenta("mantenimiento"),
    manualMantenimientoAA
  );
  // Software DS: el v1 reparte el software entre publicidad y carteleria — C se lleva
  // el estimado de carteleria (pantallas x 10,99 USD) y A el resto. Se replica igual;
  // la UNICA diferencia es que el crudo de A sale de las facturas de la cuenta
  // software_ds en vez del monto manual (v1: useGestionCalcs getSoftwareDSValueA).
  const softwareDSFacturas = porAno(selectedYear, costos.porCuenta("software_ds"), manualSoftwareDSTotal);
  const aSoftwareDS = useMemo(
    () => ZEROS.map((_, i) => Math.max(0, (softwareDSFacturas[i] || 0) - calcularCostoSoftwareEstimado(i))),
    [softwareDSFacturas, calcularCostoSoftwareEstimado]
  );
  // FASE E2c: gemelo de `softwareDSFacturas`/`aSoftwareDS` evaluado en `selectedYear - 1` —
  // lo necesita la ventana LTM (`vSoftwareDS`, más abajo). Mismo switch porAno + mismo estimado
  // de cartelería, un año atrás (`calcularCostoSoftwareEstimadoAnoAnterior`, ya existía para el
  // header — nunca se usaba acá).
  const softwareDSFacturasAnoAnterior = porAno(
    selectedYear - 1,
    costosAA.porCuenta("software_ds"),
    manualSoftwareDSTotalAA
  );
  const aSoftwareDSAnoAnterior = useMemo(
    () =>
      ZEROS.map((_, i) =>
        Math.max(0, (softwareDSFacturasAnoAnterior[i] || 0) - calcularCostoSoftwareEstimadoAnoAnterior(i))
      ),
    [softwareDSFacturasAnoAnterior, calcularCostoSoftwareEstimadoAnoAnterior]
  );
  const aMantenimiento = porAno(selectedYear, costos.porCuenta("mantenimiento"), manualMantenimiento);

  // ─── CONTENIDOS DIGITALES — reparto proporcional A/C por pantallas ──────────
  // Screenfeed / AccuWeather / ApiFootball: el servicio corre sobre las pantallas de los DOS
  // países, pero sus facturas están todas cargadas en Chile. El costo se reparte entre
  // Publicidad (A) y Cartelería (C) en proporción al inventario de cada circuito, sumando
  // Chile + Perú en ambos denominadores.
  //
  // NO se toca el ER de Perú: de Perú entra solo su CONTEO de pantallas. Sus líneas de costo
  // quedan exactamente como estaban (su agregado de facturas no tiene esta cuenta).
  //
  // A diferencia de Software DS, el reparto es PROPORCIONAL PURO: A + C === costo del mes.
  // No se reusa `calcularCostoSoftwareEstimado` — ese es un estimado residual, otra cosa.
  const pantallasPublicidad = useMemo(
    () => contarPantallasPublicidad(edificiosChile) + contarPantallasPublicidad(edificiosPeru),
    [edificiosChile, edificiosPeru]
  );
  const pantallasCarteleria = useMemo(
    () => contarPantallasCarteleria(negociosCarteleriaChile) + contarPantallasCarteleria(negociosCarteleriaPeru),
    [negociosCarteleriaChile, negociosCarteleriaPeru]
  );

  // Pre-2026 en 0: no hay tabla manual de contenidos y el motor corta por fecha, así que la
  // paridad diff-cero contra el v1 en 2025 no se mueve.
  const contenidosDigitalesTotal = porAno(selectedYear, costos.porCuenta("contenidos_digitales"), ZEROS);
  const contenidosReparto = useMemo(
    () =>
      contenidosDigitalesTotal.map((monto) => repartirPorPantallas(monto, pantallasPublicidad, pantallasCarteleria)),
    [contenidosDigitalesTotal, pantallasPublicidad, pantallasCarteleria]
  );
  const aContenidosDigitales = useMemo(() => contenidosReparto.map((r) => r.publicidad), [contenidosReparto]);
  const cContenidosDigitales = useMemo(() => contenidosReparto.map((r) => r.carteleria), [contenidosReparto]);

  // Bloque B: se conservan las 4 filas del v1. Solo "Compra hardware" tiene cuenta que
  // la alimente (`hardware`); Instalación, Materiales y Otros costos no tienen cuenta.
  const bCompraHardware = porAno(selectedYear, costos.porCuenta("hardware"), compraHardwareMensual);
  const bInstalacion = porAno(selectedYear, ZEROS, instalacionMensual);
  const bMateriales = porAno(selectedYear, ZEROS, materialesMensual);
  const bOtrosHardware = porAno(selectedYear, ZEROS, otrosCostosMensual);

  // FASE 1b (2026-10-01): mismo switch porAno que arriba, evaluado un año atrás — lo necesita
  // `calcularCostosVariablesAnoAnterior` (PASO 3.7) para `ebitdaMensualAnoAnterior`. Hoy (año
  // anterior siempre < 2026) cae
  // siempre en la rama manual; queda correcto también si algún día año anterior >= 2026.
  const compraHardwareMensualAnoAnterior = porAno(
    selectedYear - 1,
    costosAA.porCuenta("hardware"),
    compraHardwareMensualAnoAnteriorManual
  );
  const instalacionMensualAnoAnterior = porAno(selectedYear - 1, ZEROS, instalacionMensualAnoAnteriorManual);
  const materialesMensualAnoAnterior = porAno(selectedYear - 1, ZEROS, materialesMensualAnoAnteriorManual);
  const otrosCostosMensualAnoAnterior = porAno(selectedYear - 1, ZEROS, otrosCostosMensualAnoAnteriorManual);

  // Total de costo del Bloque A. Incluye la parte de contenidos digitales que le tocó al
  // circuito de publicidad → baja la Contribución A y entra a Costos Variables.
  const costosManualesA = useMemo(
    () => sumar(aServiciosInternet, aSoftwareDS, aMantenimiento, aContenidosDigitales),
    [aServiciosInternet, aSoftwareDS, aMantenimiento, aContenidosDigitales]
  );

  const contribucionA = useMemo(
    () => restar(facturacionA, costoIntermediacion, comisionEdificios, comisionComercial, costosManualesA),
    [facturacionA, costoIntermediacion, comisionEdificios, comisionComercial, costosManualesA]
  );

  // Misma composición que el hook v1 (retirado en 3.7), líneas 116-142.
  // Software de carteleria: en el v1 entra al agregado como `softwareUsado`
  // (línea 122 del hook retirado). Aca es el estimado, porque el v2 no tiene monto manual en C.
  // sales_btl.otros_costos es carga manual: solo alimenta los meses pre-2026.
  const otrosBTL = porAno(selectedYear, ZEROS, otrosCostosBTLMensual);
  // FASE 1b (2026-10-01): gemelo de arriba, un año atrás — lo necesita
  // `calcularCostosVariablesAnoAnterior` (PASO 3.7).
  const otrosCostosBTLMensualAnoAnterior = porAno(selectedYear - 1, ZEROS, otrosCostosBTLMensualAnoAnteriorManual);

  // Software de carteleria: igual que el v1 (hook retirado en 3.7, línea 122), valor manual si
  // existe y estimado si no. Pre-2026 el manual viene de costos_gestion_contenidos;
  // desde 2026 getCostoGestionValue devuelve 0 y queda el estimado.
  const softwareCarteleria = useMemo(
    () =>
      ZEROS.map((_, i) => {
        if (i >= monthsToShow) return 0;
        const manualC = Number(getCostoGestionValue(i, "software_ds")) || 0;
        return manualC > 0 ? manualC : calcularCostoSoftwareEstimado(i);
      }),
    [calcularCostoSoftwareEstimado, getCostoGestionValue, monthsToShow]
  );
  // FASE E2c: gemelo de `softwareCarteleria`, un año atrás — lo necesita la ventana LTM
  // (`BloqueCTable` recalculaba esta fila inline con `getCostoGestionValue`/
  // `calcularCostoSoftwareEstimado` del AÑO EN CURSO sin mirar `verUltimos12Meses`: en modo
  // ventana el índice `i` es un slot, no un mes calendario). SIN el corte `i >= monthsToShow`
  // del año en curso (arriba) — el año anterior está siempre "en el pasado" completo, los 12
  // meses son válidos.
  const softwareCarteleriaAnoAnterior = useMemo(
    () =>
      ZEROS.map((_, i) => {
        const manualC = Number(getCostoGestionValueAnoAnterior(i, "software_ds")) || 0;
        return manualC > 0 ? manualC : calcularCostoSoftwareEstimadoAnoAnterior(i);
      }),
    [calcularCostoSoftwareEstimadoAnoAnterior, getCostoGestionValueAnoAnterior]
  );

  // "Otros" del bloque C: carga manual, solo pre-2026 (getCostoGestionValue ya corta).
  const otrosGestion = useMemo(
    () => ZEROS.map((_, i) => (i < monthsToShow ? Number(getCostoGestionValue(i, "otros")) || 0 : 0)),
    [getCostoGestionValue, monthsToShow]
  );
  // FASE E2c: gemelo de `otrosGestion`, un año atrás — mismo motivo que `softwareCarteleriaAnoAnterior`.
  const otrosGestionAnoAnterior = useMemo(
    () => ZEROS.map((_, i) => Number(getCostoGestionValueAnoAnterior(i, "otros")) || 0),
    [getCostoGestionValueAnoAnterior]
  );

  // Contribución del Bloque C — FASE 5b (2026-10-06, hallazgo de `fase38E1EstructuraFilasER`):
  // antes restaba sobre `margenGestionContenidosMensual` (el hook `useGestionCalcs`, que calcula
  // SU PROPIO ingreso interno — negocios/ventas directo, nunca al tanto de la vinculación SII) +
  // `cContenidosDigitales`; para Chile 2026+ eso divergía en silencio del ingreso YA corregido
  // que muestra la fila de arriba (`ingresosGestionContenidosMensual`, `chileVentaLeavesSesion`) —
  // dos ingresos distintos para la MISMA fila (10.1). Ahora se arma con las MISMAS 4 series que
  // ya se muestran en pantalla (ingreso, Software DS, Otros, Contenidos Digitales) — idéntica
  // fórmula a la que usa `construirFilasER` para esta misma fila, fuente única.
  const margenGestionContenidosAjustado = useMemo(
    () => restar(ingresosGestionContenidosMensual, softwareCarteleria, otrosGestion, cContenidosDigitales),
    [ingresosGestionContenidosMensual, softwareCarteleria, otrosGestion, cContenidosDigitales]
  );
  const ytdMargenGestionContenidosAjustado = useMemo(
    () => margenGestionContenidosAjustado.slice(0, mesesVisibles).reduce((a, b) => a + (b || 0), 0),
    [margenGestionContenidosAjustado, mesesVisibles]
  );

  const costosVariables = useMemo(
    () =>
      sumar(
        costoIntermediacion,
        comisionEdificios,
        comisionComercial,
        comisionEdificiosBTLMensual,
        costosManualesA,
        bCompraHardware,
        bInstalacion,
        bMateriales,
        bOtrosHardware,
        softwareCarteleria,
        // La pata C del reparto. La pata A ya viaja dentro de costosManualesA, así que entre
        // las dos suman el costo completo del mes — ni de más ni de menos.
        cContenidosDigitales,
        otrosGestion,
        otrosBTL
      ),
    [
      costoIntermediacion,
      comisionEdificios,
      comisionComercial,
      comisionEdificiosBTLMensual,
      costosManualesA,
      bCompraHardware,
      cContenidosDigitales,
    ]
  );

  // Costos Fijos = RRHH (nómina) + los 12 conceptos, cada uno alimentado por su cuenta
  // homónima. `camposCostosFijos` coincide 1:1 con los `value` de CUENTAS_ER.
  const costosFijosPorConcepto = useMemo(
    () =>
      camposCostosFijos.map((campo) =>
        porAno(
          selectedYear,
          costos.porCuenta(campo),
          serieManualFija(manual.rawFijos, manual.fijos, manual.fijosAA, campo)
        )
      ),
    [costos, selectedYear, porAno, serieManualFija, manual]
  );

  const costosFijos = useMemo(
    () => sumar(rrhhMensualConBeneficios, ...costosFijosPorConcepto),
    [rrhhMensualConBeneficios, costosFijosPorConcepto]
  );

  // FASE 1b (2026-10-01): gemelo de los dos de arriba, un año atrás — lo necesita
  // `calcularEbitdaMensual` (PASO 3.7) para `ebitdaMensualAnoAnterior`. Mismo switch porAno
  // (facturas SII si
  // año anterior >= 2026, tablas manuales si no) evaluado en `selectedYear - 1`; `rawFijosAA`
  // (filas crudas de hace un año, ya cargadas por useERv2Data) alimenta el mismo check
  // "existe fila real vs. proyectar" que usa serieManualFija para el año en curso. No se
  // mira dos años atrás (sin fetch de `selectedYear - 2`): no hace falta hoy — el invariante
  // de esta fase lo verifica contra el EBITDA real de 2025, y si hiciera falta se vería ahí.
  const costosFijosPorConceptoAnoAnterior = useMemo(
    () =>
      camposCostosFijos.map((campo) =>
        porAno(
          selectedYear - 1,
          costosAA.porCuenta(campo),
          serieManualFija(manual.rawFijosAA, manual.fijosAA, VACIO, campo)
        )
      ),
    [costosAA, selectedYear, porAno, serieManualFija, manual]
  );

  const costosFijosMensualAnoAnterior = useMemo(
    () => sumar(rrhhMensualConBeneficiosAnoAnterior, ...costosFijosPorConceptoAnoAnterior),
    [rrhhMensualConBeneficiosAnoAnterior, costosFijosPorConceptoAnoAnterior]
  );

  // Subfila Gratificaciones: se deja IDENTICA al v1, con su bug incluido (IMM=500.000
  // hardcodeado sobre una base que ya trae la gratificacion). El TOTAL de RRHH si es
  // correcto y coincide al peso con el v1 — solo difiere ese display. La correccion
  // real se define aparte y entra por el v1, no por una divergencia del v2.

  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";

  // Banner de ventas cerradas (100%) del mes anterior sin vincular.
  // Portado literal de ER/index.js:3294. PASO 6: el fallback por `facturanumero` se retiró —
  // `siiVinculacionesMap._byRef` ya aplica la MISMA regla que `registrosIngresoSII`
  // (useERv2Data.js:fetchVinculaciones), sin un atajo sin equivalente del lado de Facturación.
  const ventasCerradasSinVincular = useMemo(() => {
    if (!siiVinculacionesMap) return null;
    const contratos = pais === "Chile" ? contratosChile : contratosPeru;

    const tieneVinculacionSII = (refKey) => Boolean(siiVinculacionesMap._byRef[refKey]);

    const hoy = new Date();
    const mesActualIdx = hoy.getMonth();
    const mesAnteriorIdx = mesActualIdx === 0 ? 11 : mesActualIdx - 1;
    const anoMesFactura = mesActualIdx === 0 ? selectedYear - 1 : selectedYear;

    const pendientesPauta = oportunidades.filter((opp) => {
      if (opp.eliminada) return false;
      if (opp.estatus !== "100%") return false;
      if (pais === "Chile") {
        if (tieneVinculacionSII(`sales:${opp.id}:`)) return false;
      } else if (opp.facturanumero || opp.facturafecha) {
        return false;
      }
      if (opp.ano !== selectedYear) return false;
      if (!opp.mes_factura) return false;
      const partes = String(opp.mes_factura).split("-");
      if (partes.length !== 2) return false;
      const mesOpp = parseInt(partes[0], 10);
      const anoOpp = parseInt(partes[1], 10);
      if (isNaN(mesOpp) || isNaN(anoOpp)) return false;
      return mesOpp - 1 === mesAnteriorIdx && anoOpp === anoMesFactura;
    });

    const pendientesContratos = [];
    (contratos || []).forEach((c) => {
      if (c.eliminada) return;
      if (c.estatus !== "100%") return;
      (c.meses || []).forEach((mesObj, idx) => {
        if (!mesObj?.mes) return;
        const partes = String(mesObj.mes).split("-");
        if (partes.length !== 2) return;
        const mesOpp = parseInt(partes[0], 10);
        const anoOpp = parseInt(partes[1], 10);
        if (isNaN(mesOpp) || isNaN(anoOpp)) return;
        if (mesOpp - 1 !== mesAnteriorIdx || anoOpp !== anoMesFactura) return;
        if (pais === "Chile") {
          if (tieneVinculacionSII(`sales_contratos:${c.id}:${idx}`)) return;
        } else if (mesObj.facturanumero || mesObj.facturafecha) {
          return;
        }
        pendientesContratos.push({
          id: `contrato-${c.id}-${idx}`,
          importe: Number(mesObj.importe) || 0,
          moneda_dolar: c.moneda_dolar || false,
          facturafecha: mesObj.facturafecha || null,
          fecha_cierre: c.fecha_cierre || null,
          mes_cierre: c.mes_cierre || null,
        });
      });
    });

    const filtradas = [...pendientesPauta, ...pendientesContratos];
    if (filtradas.length === 0) return null;

    const total = filtradas.reduce(
      (sum, opp) => sum + getConvertedImporte(opp, getRateForOpp(opp, getDolarValue, dolarFallback[pais])),
      0
    );
    return { count: filtradas.length, total, mesIdx: mesAnteriorIdx };
  }, [
    oportunidades,
    contratosChile,
    contratosPeru,
    selectedYear,
    pais,
    getDolarValue,
    dolarFallback,
    siiVinculacionesMap,
  ]);

  const ytd = (arr) => (arr || ZEROS).slice(0, mesesVisibles).reduce((a, b) => a + (b || 0), 0);

  // ─── Helpers con la misma forma que el v1 (ER/index.js:2304, :2311, :2200) ───
  const formatPercentage = (value) => {
    if (!value || value === 0) return "0%";
    const dec = Math.abs(value) < 10 ? 1 : 0;
    return value.toLocaleString("es-CL", { minimumFractionDigits: dec, maximumFractionDigits: dec }) + "%";
  };

  const porcentajeSobreFacturacion = (valor, baseOverride = null) => {
    const base = baseOverride === null ? ytdFacturacion : baseOverride;
    return base > 0 ? formatPercentage((valor / base) * 100) : "0%";
  };

  const costosFijosYTD = ytd(costosFijos);

  // ─── AGREGADOS COMBINADOS (USD) PARA EL MODO REGIÓN ────────
  // El EBITDA de región = facturación − costos variables − costos fijos, todo en USD.
  // Sin estos arrays el hook recibia ceros y el EBITDA Run Rate salia IGUAL al de
  // Facturación (era el sintoma reportado). Cada pais se convierte con su propio TC.
  // PASO 3.6a: `aUSD`/`enUSDPorPais` (más abajo) eran la MISMA fórmula duplicada dos veces —
  // retiradas a favor de `combinarEnUSD` (estadoResultadosER.js), llamada directo en cada
  // call site con los mismos argumentos que antes capturaba el closure.

  // Costos MANUALES por pais (pre-2026). Mismos helpers que la vista de pais unico.
  const costosDePais = useCallback(
    (nombrePais, agg, hw, otrosBTLPais, negocios, dolarMensualPromedioArr) => {
      const m = manual.porPais?.[nombrePais];
      if (!m)
        return {
          variables: ZEROS,
          fijos: ZEROS,
          variablesFacturas: ZEROS,
          fijosFacturas: ZEROS,
          servInternet: ZEROS,
          softwareA: ZEROS,
          softwareAFacturas: ZEROS,
          mantenimiento: ZEROS,
          softwareC: ZEROS,
          otros: ZEROS,
          fijosPorConcepto: camposCostosFijos.map(() => ZEROS),
        };
      // Costo de carteleria del pais (v1: ER/index.js:2532). Es el termino que el v1
      // le RESTA al software del bloque A y le SUMA al del bloque C.
      // PASO 3 (Criterio 1), punto 3: usa la función ÚNICA `calcularCostoSoftwareCarteleria`
      // (antes `calcularCostoCarteleriaEstimado`, divergente de la del modo normal — unificadas,
      // ver el comentario largo en `estadoResultadosER.js`). Recibe el array de TC PROMEDIO
      // mensual (Criterio 1b, ajuste de Cowork 2026-10-06) en vez de `dolarPais`/`fbPais` crudos
      // — mismo mecanismo que modo normal.
      const costoCart = (i) => calcularCostoSoftwareCarteleria(negocios, selectedYear, i, dolarMensualPromedioArr);
      // Proyeccion de "otros" de gestion (v1: ER/index.js:2510): promedio de los
      // valores > 0 del año, completando con el anterior. Se usa cuando NO hay fila.
      // El v1 proyecta sobre el MAPA de 12 entradas que devuelve getCostosGestion
      // (utils/gestion.js:20), no sobre las filas crudas: una entrada por mes, solo
      // filas globales (negocio_id null) y la ultima gana si hubiera repetidas.
      const aMapa = (filas) => {
        const mapa = Array.from({ length: 12 }, () => ({ software_ds: 0, otros: 0 }));
        (filas || []).forEach((row) => {
          // eslint-disable-next-line eqeqeq
          if (row.negocio_id != null) return;
          const i = Number(row.mes) - 1;
          if (i >= 0 && i < 12) {
            mapa[i].software_ds = Number(row.software_ds) || 0;
            mapa[i].otros = Number(row.otros) || 0;
          }
        });
        return mapa;
      };
      const proyectarGestion = (campo) => {
        const vals = [];
        const juntar = (mapa) =>
          mapa.forEach((c) => {
            if (vals.length >= 12) return;
            const v = Number(c[campo]) || 0;
            if (v > 0) vals.push(v);
          });
        juntar(aMapa(m.gestion));
        if (vals.length < 12) juntar(aMapa(m.gestionAA));
        return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
      };
      const proyOtros = proyectarGestion("otros");

      // Split A/C del v1 (ER/index.js:2575). Detalle que importa: el v1 resta la
      // carteleria SOLO cuando el valor del mes es real; cuando no hay dato y proyecta,
      // usa la proyeccion TAL CUAL, sin restar nada.
      const softwareA = ZEROS.map((_, i) => {
        const row = m.publicidad?.[i];
        if (row && row.software_ds != null) {
          return Math.max(0, (Number(row.software_ds) || 0) - costoCart(i));
        }
        return proyectarPublicidad(m.publicidad, m.publicidadAA, "software_ds", i);
      });

      // OJO — comportamiento real del v1 en el agregado de region: su `cgData` viene de
      // getCostosGestion, que devuelve un mapa de 12 entradas {software_ds, otros} SIN
      // campo `mes` (utils/gestion.js:20). Por eso su
      // `cgData.find(c => Number(c.mes) === i+1 ...)` NUNCA matchea, y el combinado
      // termina usando SIEMPRE el estimado de carteleria y SIEMPRE la proyeccion de
      // "otros", ignorando los valores por mes. Se replica tal cual: este es el unico
      // camino donde el v1 se comporta asi (la vista de pais unico si lee el valor real).
      const softwareC = ZEROS.map((_, i) => costoCart(i));
      const variables = sumar(
        serieManualPublicidad(m.publicidad, m.publicidadAA, "servicios_internet"),
        softwareA,
        serieManualPublicidad(m.publicidad, m.publicidadAA, "mantenimiento"),
        ...hw,
        softwareC,
        ZEROS.map(() => proyOtros),
        otrosBTLPais
      );
      const fijos = sumar(...camposCostosFijos.map((c) => serieManualFija(m.rawFijos, m.fijos, m.fijosAA, c)));

      // Rama FACTURAS (>= 2026) del MISMO país, con la misma composición que la vista de país
      // único: A = servicios_internet + max(0, software_ds − cartelería) + mantenimiento,
      // B = hardware, C = estimado de cartelería. En 2026 el manual de C ya devuelve 0
      // (getCostoGestionValue corta por año), así que el estimado es el único término, igual
      // que en país único. Instalación / Materiales / Otros de B, "otros" de gestión y otros
      // de BTL no tienen cuenta que los alimente: son 0 desde 2026 por diseño.
      const softwareAFacturas = ZEROS.map((_, i) => Math.max(0, (agg.porCuenta("software_ds")[i] || 0) - costoCart(i)));
      const variablesFacturas = sumar(
        agg.porCuenta("servicios_internet"),
        softwareAFacturas,
        agg.porCuenta("mantenimiento"),
        agg.porCuenta("hardware"),
        ZEROS.map((_, i) => costoCart(i)),
        // Contenidos digitales entra ENTERO, sin repartir: el reparto A/C es proporcional
        // puro (A + C === costo), así que el total del país es el mismo se mire por bloque
        // o agregado. El split solo existe para mostrarlo en dos filas.
        agg.porCuenta("contenidos_digitales")
      );
      const fijosFacturas = sumar(...camposCostosFijos.map((c) => agg.porCuenta(c)));

      // FASE E2b: piezas GRANULARES (<2026, manual) que `variables`/`fijos` ya sumaban por
      // dentro — se exponen además de las agregadas (10.1, nunca se recalculan) para que
      // Sumar Perú pueda combinar Chile+Perú fila por fila (Servicios internet/Software DS/
      // Mantenimiento/13 cuentas), no solo el total. Las piezas >=2026 (`agg.porCuenta(...)`)
      // ya están disponibles en el caller (`costosChile`/`costosPeru`, el mismo `agg` de acá)
      // — no hace falta repetirlas.
      const servInternet = serieManualPublicidad(m.publicidad, m.publicidadAA, "servicios_internet");
      const mantenimiento = serieManualPublicidad(m.publicidad, m.publicidadAA, "mantenimiento");
      const fijosPorConcepto = camposCostosFijos.map((c) => serieManualFija(m.rawFijos, m.fijos, m.fijosAA, c));

      return {
        variables,
        fijos,
        variablesFacturas,
        fijosFacturas,
        servInternet,
        softwareA,
        softwareAFacturas,
        mantenimiento,
        softwareC,
        otros: ZEROS.map(() => proyOtros),
        fijosPorConcepto,
      };
    },
    [manual, selectedYear, serieManualPublicidad, serieManualFija]
  );

  const manualChile = costosDePais(
    "Chile",
    costosChile,
    [compraHardwareMensualChile, instalacionMensualChile, materialesMensualChile, otrosCostosMensualChile],
    otrosCostosBTLMensualChile,
    negociosCarteleriaChile,
    dolarMensualPromedioChile
  );
  const manualPeru = costosDePais(
    "Perú",
    costosPeru,
    [compraHardwareMensualPeru, instalacionMensualPeru, materialesMensualPeru, otrosCostosMensualPeru],
    otrosCostosBTLMensualPeru,
    negociosCarteleriaPeru,
    dolarMensualPromedioPeru
  );

  // Movidos acá (FASE 3 PASO 4b, punto 2): `chileLeavesUSDFijo`/`peruLeavesUSDFijo` (más abajo)
  // los necesitan temprano, antes de que
  // `opsFactoringChile`/`opsFactoringPeru` tengan datos reales (arrancan `[]`, el useEffect de
  // carga los llena después — igual que ya pasaba en la posición original, sin cambio de
  // comportamiento, solo de orden de declaración).
  const factoringChile = useMemo(
    () => interesesFactoringPorMes({ operaciones: opsFactoringChile, pais: "Chile", ano: selectedYear }),
    [opsFactoringChile, selectedYear]
  );
  const factoringPeru = useMemo(
    () => interesesFactoringPorMes({ operaciones: opsFactoringPeru, pais: "Perú", ano: selectedYear }),
    [opsFactoringPeru, selectedYear]
  );
  // ─── FASE 3 PASO 3 (criterio 1), punto 4 — US$ bottom-up por fila-hoja de A-D, SOLO Chile
  // (10.265/10.267: Perú no cambia, sigue con `aUSDHoy` más abajo). Cada fila-hoja se arma con
  // SU función de registros/criterio (ingresosER.js/costosER.js/estadoResultadosER.js, 10.1 —
  // nada de lógica nueva acá, solo wiring); se llama 2 veces (año en curso y año anterior) para
  // poder ventanear en LTM sin reconvertir ningún total. FASE 5a (2026-10-06): el PESO de las 8
  // filas "atadas a la venta" de A-D YA NO es una variable aparte (ver `chileVentaLeavesSesion`,
  // arriba) — esto construye el resto de `.usd` + `._local` de esas 8 vía `construirVentaLeavesChile`
  // (misma fuente, 10.1). `dolarFallbackPais`/`contratosSesion` ahora viven arriba (hoisted). ───
  const construirLeavesUsdCriterio1Chile = (ano, inputs) => {
    const {
      costosDelAno,
      softwareDSResidual,
      softwareDSCManual,
      manualServInternet,
      manualMantenimiento,
      manualCompraHardware,
      manualInstalacion,
      manualMateriales,
      manualOtrosHardware,
      manualOtrosGestion,
      manualOtrosBTL,
      contenidosA,
      contenidosC,
      empleadosRRHHDelAno,
      costosFijosPorConceptoDelAno,
      depreciacionDelAno,
      amortizacionDelAno,
      factoringDetalleDelAno,
      // FASE 3 PASO 4b, punto 2 — "Sumar Perú" necesita las hojas de Chile SIEMPRE (regardless
      // de la sesión activa, incluso desde una sesión Perú con "Sumar Chile"). Por defecto
      // (los 2 call sites de siempre, `chileLeavesUSD`/`chileLeavesUSDAnoAnterior`) sigue
      // leyendo las referencias de la SESIÓN — nunca cambia nada ahí. El 3er call site
      // (`chileLeavesUSDFijo`, siempre Chile) pasa las versiones `*Chile` explícitas.
      oportunidadesParaPComun = oportunidades,
      contratosParaPComun = contratosSesion,
      salesBTLParaPComun,
      negociosParaPComun = negociosCarteleria,
      getDolarValueParaPComun = getDolarValue,
      dolarFallbackPaisParaPComun = dolarFallbackPais,
      clientesParaPComun = clientes,
      intermediariosParaPComun = intermediarios,
      edificiosParaPComun = edificios,
      pctMapParaPComun = pctMap,
      snapMapParaPComun = snapMap,
      costosMapParaPComun = costosMap,
      getSueldoBaseParaRRHH = (emp, mesIndice) => getSueldoBaseVigenteParaAno(emp, mesIndice, ano),
    } = inputs;
    const dolarProm = ano === selectedYear ? dolarMensualPromedioChile : dolarMensualPromedioAnoAnterior;
    // FASE 3 PASO 4b, punto 1 — bug real encontrado al medir: `salesBTL` viene YA FILTRADO a
    // `selectedYear` (`useERv2Data.js`, `btlDelAno(sesion.salesBTL, selectedYear)`) — para el
    // llamado de año-anterior hace falta `salesBTLAnoAnterior` (el mismo fetch, filtrado a
    // `selectedYear - 1`), si no `registrosBTL` nunca encuentra ventas BTL de hace un año
    // (quedaban en 0 aunque sí existieran — medido: abril 2025, Chile, US$3.255 perdidos).
    const salesBTLDeLaSesion =
      salesBTLParaPComun !== undefined ? salesBTLParaPComun : ano === selectedYear ? salesBTL : salesBTLAnoAnterior;
    // FASE 5a+5b (2026-10-06) — las 8 filas "atadas a la venta" (ingreso ABCD + Costo
    // intermediación + Comisión edificios A + Comisión comercial A + Comisión edificios BTL)
    // salen de `construirVentaLeavesChile` (ingresosER.js, fuente ÚNICA de `.usd` Y `._local`
    // para estas filas, misma función que ya usa `chileVentaLeavesSesion` más arriba). PASO 5
    // (3.9) activo (default de la función, `usarSII: ano >= 2026`): Chile 2026+ factura desde
    // SII vinculada — `.usd` y `.local` se mueven JUNTOS, nunca por separado.
    const ventaLeaves = construirVentaLeavesChile(ano, {
      oportunidadesParaPComun,
      contratosParaPComun,
      salesBTLParaPComun: salesBTLDeLaSesion,
      negociosParaPComun,
      getDolarValueParaPComun,
      dolarFallbackPaisParaPComun,
      clientesParaPComun,
      intermediariosParaPComun,
      edificiosParaPComun,
      pctMapParaPComun,
      snapMapParaPComun,
      costosMapParaPComun,
      getUFValue,
      convertirAMonedaLocal,
      dolarMensualPeru,
      dolarFallback,
      facturasEmitidasChile,
      lineaIdATipo,
    });
    const usdCuenta = (cuenta, manualPesoPorMes) =>
      usdCuentaCostoConFallbackManual({
        costos: costosDelAno,
        cuenta,
        pais: "Chile",
        ano,
        manualPesoPorMes,
        dolarMensualPromedioArr: dolarProm,
      });
    const usdManual = (serieMensual) => usdSerieManual({ serieMensual, dolarMensualPromedioArr: dolarProm });
    const resultado = {
      ...ventaLeaves.usd,
      "A - Publicidad / Servicios internet": usdCuenta("servicios_internet", manualServInternet),
      "A - Publicidad / Software DS": usdSoftwareDSA({
        costos: costosDelAno,
        pais: "Chile",
        ano,
        negocios: negociosCarteleria,
        pesoResidualPorMes: softwareDSResidual,
        dolarMensualPromedioArr: dolarProm,
      }),
      "A - Publicidad / Mantenimiento": usdCuenta("mantenimiento", manualMantenimiento),
      "A - Publicidad / Contenidos Digitales": usdManual(contenidosA),
      "B - Hardware / Compra hardware": usdCuenta("hardware", manualCompraHardware),
      "B - Hardware / Instalación": usdManual(manualInstalacion),
      "B - Hardware / Materiales": usdManual(manualMateriales),
      "B - Hardware / Otros costos": usdManual(manualOtrosHardware),
      "C - Gestión & Contenidos / Software DS": usdSoftwareDSC({
        negocios: negociosCarteleria,
        ano,
        manualPesoPorMes: softwareDSCManual,
        dolarMensualPromedioArr: dolarProm,
      }),
      "C - Gestión & Contenidos / Otros": usdManual(manualOtrosGestion),
      "C - Gestión & Contenidos / Contenidos Digitales": usdManual(contenidosC),
      "D - BTL & otros / Otros costos": usdManual(manualOtrosBTL),
      // FASE 3 PASO 4 (criterio 1), punto 1a — RRHH (sueldo + gratificación + egresos), SIEMPRE
      // Criterio 1b (TC promedio del mes de devengo, `getSueldoBaseVigenteParaAno` ya está bound
      // a "Chile" por el cierre de la sesión cuando este bloque corre — ver gate `pais!=="Chile"`
      // más abajo). El monto en pesos (`rrhhMensualConBeneficios`/AA) no cambia.
      RRHH: usdRRHH({
        empleados: empleadosRRHHDelAno,
        pais: "Chile",
        ano,
        getSueldoBase: getSueldoBaseParaRRHH,
      }),
    };
    // FASE 3 PASO 4 (criterio 1), punto 1b — las 13 cuentas de Costos Fijos: 2026+ cada
    // factura con el TC de su propia fecha_emision (Criterio 1a, vía `costos.registros`);
    // <2026 (carga manual) TC promedio del mes (Criterio 1b) — mismo `usdCuenta` que ya usan
    // Servicios internet/Mantenimiento/Hardware arriba. Asesoría externa NO tiene camino propio
    // (ajuste de Cowork sobre el rediseño): pasa por EXACTAMENTE el mismo `usdCuenta` que las
    // otras 12, sin reactivar el código muerto de `CostosFijosTable.js` (asesoria_externa_usd/
    // _fecha, nunca poblados).
    NOMBRES_COSTOS_FIJOS_ER.forEach((nombre, idx) => {
      resultado[nombre] = usdCuenta(camposCostosFijos[idx], costosFijosPorConceptoDelAno[idx]);
    });
    // FASE 3 PASO 4 (criterio 1), punto 2a — Depreciación/Amortización: el monto en PESOS no
    // cambia (cada activo ya se convierte a local con el TC de SU fecha de compra,
    // `getDepreciacionMensual.js`, 10.185); el US$ es Criterio 1b, TC promedio del mes — ninguna
    // cuota tiene "fecha propia del ER" (es una cuota contable, no un documento del mes).
    resultado["Depreciación"] = usdManual(depreciacionDelAno);
    resultado["Amortización"] = usdManual(amortizacionDelAno);
    // FASE 3 PASO 4 (criterio 1), punto 2b/2c — Intereses (debajo del EBIT), Chile:
    //  - Holding (constante en USD): US$ = la constante, SIN ida y vuelta (`baselineUSD`,
    //    nunca se divide/multiplica por ningún TC).
    //  - Intereses REALES de factoring: Criterio 1a, TC de `fecha_operacion` (cada operación,
    //    vía `registrosInteresesFactoring`).
    //  - Factoring comisión/mora (10.277, van a Intereses, NUNCA a Costos Fijos/Variables):
    //    Criterio 1a, TC de `fecha_emision` — mismos registros que ya construye `costosER.js`
    //    (`costos.registros`, concepto∈{factoring_comision,factoring_mora}), sin manual <2026
    //    (factoring es un negocio nuevo, sin carga histórica pre-SII para estas 2 cuentas).
    const baselineUSD = interesesMensualesUSD["Chile"] || 0;
    const regsFactoringInteres = registrosInteresesFactoring({ detalle: factoringDetalleDelAno, pais: "Chile", ano });
    const regsFactoringComisionMora = (costosDelAno?.registros || []).filter(
      (r) =>
        (r.concepto === "factoring_comision" || r.concepto === "factoring_mora") && r.pais === "Chile" && r.ano === ano
    );
    const factoringUsdPorMes = agregarAMesesUSD(
      convertirRegistrosAUSD([...regsFactoringInteres, ...regsFactoringComisionMora]),
      { pais: "Chile", ano }
    );
    resultado["Intereses"] = factoringUsdPorMes.map((v) => baselineUSD + v);
    // Impuestos: mismo pipeline (fecha null), constante hoy (0) — se expone acá para que
    // `chileLeavesUSDFijo` (Sumar Perú, punto 2) no dependa de un fallback implícito a ZEROS.
    resultado["Impuestos"] = ZEROS.map(() => impuestosMensualesUSD["Chile"] || 0);
    // FASE 5a (2026-10-06) — `._local` de las 8 filas "atadas a la venta" (único lugar que lo
    // expone en este objeto; el resto de las filas no lo necesita, su `.local` sigue viniendo de
    // las variables de siempre, 10.1 — no se duplica lo que ya está bien).
    resultado._local = ventaLeaves.local;
    return resultado;
  };

  // Contenidos Digitales: las facturas SIEMPRE están en Chile (10.1, mismo origen que la vista
  // de país único) — Perú aporta 0 al costo (se pasa `ZEROS` directo en `peruLeavesUSDFijo`),
  // aunque sus pantallas YA cuenten en el denominador del reparto (`pantallasPublicidad`/
  // `pantallasCarteleria`, session-independiente).
  const contenidosTotalChileFijo = porAno(selectedYear, costosChile.porCuenta("contenidos_digitales"), ZEROS);
  const contenidosRepartoChileFijo = contenidosTotalChileFijo.map((monto) =>
    repartirPorPantallas(monto, pantallasPublicidad, pantallasCarteleria)
  );
  const aContenidosChile = contenidosRepartoChileFijo.map((r) => r.publicidad);
  const cContenidosChile = contenidosRepartoChileFijo.map((r) => r.carteleria);

  // FASE 3 PASO 4b, punto 2 — "Sumar Perú"/"Sumar Chile" SIN flags: filasCombinado arma CADA
  // fila como filas Chile (modo directo) + filas Perú (modo directo), SIEMPRE, sin mirar la
  // sesión activa (funciona igual desde una sesión Chile o una sesión Perú). Chile SIEMPRE
  // Criterio 1 (reusa `construirLeavesUsdCriterio1Chile` con inputs fijos a Chile, nunca a la
  // sesión); Perú SIEMPRE su criterio de hoy (TC del mes, último disponible) — nunca cambia.
  // Retira `publicidadCombinadoTotal`/`hardwareMensualesCombinados`/`gestionMensualesCombinados`/
  // `btlMensualesCombinados`/`costosVariablesCombinadosUSD`/`costosFijosCombinadosUSD`/
  // `interesesUSDCombinado`/`depreciacionMensualesCombinados`/`amortizacionMensualesCombinados`
  // (ningún array combinado armado en los hooks viejos).
  const chileLeavesUSDFijo = useMemo(() => {
    const costosFijosPorConceptoChileFijo = camposCostosFijos.map((campo, idx) =>
      porAno(selectedYear, costosChile.porCuenta(campo), manualChile.fijosPorConcepto[idx])
    );
    return construirLeavesUsdCriterio1Chile(selectedYear, {
      costosDelAno: costosChile,
      softwareDSResidual: porAno(selectedYear, manualChile.softwareAFacturas, manualChile.softwareA),
      softwareDSCManual: ZEROS,
      manualServInternet: manualChile.servInternet,
      manualMantenimiento: manualChile.mantenimiento,
      manualCompraHardware: compraHardwareMensualChile,
      manualInstalacion: instalacionMensualChile,
      manualMateriales: materialesMensualChile,
      manualOtrosHardware: otrosCostosMensualChile,
      manualOtrosGestion: porAno(selectedYear, ZEROS, manualChile.otros),
      manualOtrosBTL: otrosCostosBTLMensualChile,
      contenidosA: aContenidosChile,
      contenidosC: cContenidosChile,
      empleadosRRHHDelAno: empleadosRRHHChile,
      costosFijosPorConceptoDelAno: costosFijosPorConceptoChileFijo,
      depreciacionDelAno: depreciacionMensualChile,
      amortizacionDelAno: amortizacionMensualChile,
      factoringDetalleDelAno: factoringChile.detalle,
      oportunidadesParaPComun: oportunidadesChile,
      contratosParaPComun: contratosChile,
      salesBTLParaPComun: salesBTLChile,
      negociosParaPComun: negociosCarteleriaChile,
      getDolarValueParaPComun: getDolarValueChile,
      dolarFallbackPaisParaPComun: dolarFallback.Chile,
      clientesParaPComun: clientesChile,
      intermediariosParaPComun: intermediariosChile,
      edificiosParaPComun: edificiosChile,
      pctMapParaPComun: pctMapChile,
      snapMapParaPComun: snapMapChile,
      costosMapParaPComun: costosMapChile,
      // El default de `getSueldoBaseParaRRHH` (`getSueldoBaseVigenteParaAno`) usa `calcSueldoCore(...,
      // pais)` — el PAÍS DE SESIÓN, no "Chile" fijo (decide si suma gratificación, 10.281-style
      // guard por país) — con una sesión Perú activa, esto apagaba la gratificación de Chile en
      // SILENCIO (RRHH de Chile ~7% más bajo). `getSueldoBaseVigenteChileFijo` (hook
      // `useRRHHCalculations`, ya usado para `rrhhMensualChile`) ignora la sesión y siempre
      // calcula con paisCalc="Chile" — fuente única con "RRHH" del modo directo Chile (medido:
      // 0 diff vs `estructurar("Chile", ano)` en fase38E1EstructuraFilasER.test.js).
      getSueldoBaseParaRRHH: getSueldoBaseVigenteChileFijo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedYear,
    costosChile,
    manualChile,
    compraHardwareMensualChile,
    instalacionMensualChile,
    materialesMensualChile,
    otrosCostosMensualChile,
    otrosCostosBTLMensualChile,
    aContenidosChile,
    cContenidosChile,
    empleadosRRHHChile,
    getSueldoBaseVigenteChileFijo,
    depreciacionMensualChile,
    amortizacionMensualChile,
    factoringChile,
    oportunidadesChile,
    contratosChile,
    salesBTLChile,
    negociosCarteleriaChile,
    getDolarValueChile,
    dolarFallback,
    clientesChile,
    intermediariosChile,
    edificiosChile,
    pctMapChile,
    snapMapChile,
    costosMapChile,
    dolarMensualPromedioChile,
    convertirAMonedaLocal,
    dolarMensualPeru,
    getUFValue,
    facturasEmitidasChile,
    lineaIdATipo,
  ]);
  const pComunPeruFijo = {
    oportunidades: oportunidadesPeru,
    contratos: contratosPeru,
    salesBTL: salesBTLPeru,
    negocios: negociosCarteleriaPeru,
    pais: "Perú",
    getDolarValue: getDolarValuePeru,
    dolarFallbackPais: dolarFallback["Perú"],
    clientes: clientesPeru,
    intermediarios: intermediariosPeru,
    edificios: edificiosPeru,
    pctMap: pctMapPeru,
    snapMap: snapMapPeru,
    costosMap: costosMapPeru,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
  };
  // Perú SIEMPRE su criterio de hoy (10.265/10.267) — peso vía los MISMOS registros (paridad
  // probada contra las series de siempre, `ingresosER-paridad-*.test.js`), US$ con
  // `convertirSerieMensualAUSD` (TC del mes, último disponible), nunca `convertirRegistroAUSD`
  // (eso es Criterio 1, exclusivo de Chile).
  const peruLeavesUSDFijo = useMemo(() => {
    const ano = selectedYear;
    const pc = { ...pComunPeruFijo, ano };
    const localDeRegistros = (registros) => agregarAMesesLocal(registros, { pais: "Perú", ano });
    const usdHoy = (local) => convertirSerieMensualAUSD(local, dolarMensualPeru, dolarFallback["Perú"]);
    const costosFijosPorConceptoPeruFijo = camposCostosFijos.map((campo, idx) =>
      porAno(selectedYear, costosPeru.porCuenta(campo), manualPeru.fijosPorConcepto[idx])
    );
    const factoringFinancieroPeruFijo = sumar(
      factoringPeru.mensual,
      costosPeru.porCuenta("factoring_comision"),
      costosPeru.porCuenta("factoring_mora")
    );
    const interesesPeruFijo = interesesMensualER({
      pais: "Perú",
      dolarMensual: dolarMensualPeru,
      dolarFallback,
      interesesFactoringMensual: factoringFinancieroPeruFijo,
    });
    const resultado = {
      "A - Publicidad": usdHoy(localDeRegistros(registrosIngresoA(pc))),
      "A - Publicidad / Costo intermediación": usdHoy(localDeRegistros(registrosCostoIntermediacion(pc))),
      "A - Publicidad / Comisión edificios": usdHoy(localDeRegistros(registrosComisionEdificiosA(pc))),
      "A - Publicidad / Comisión comercial": usdHoy(localDeRegistros(registrosComisionComercialA(pc))),
      "A - Publicidad / Servicios internet": usdHoy(
        porAno(selectedYear, costosPeru.porCuenta("servicios_internet"), manualPeru.servInternet)
      ),
      "A - Publicidad / Software DS": usdHoy(porAno(selectedYear, manualPeru.softwareAFacturas, manualPeru.softwareA)),
      "A - Publicidad / Mantenimiento": usdHoy(
        porAno(selectedYear, costosPeru.porCuenta("mantenimiento"), manualPeru.mantenimiento)
      ),
      // Contenidos Digitales: las facturas SIEMPRE están en Chile — Perú aporta 0.
      "A - Publicidad / Contenidos Digitales": usdHoy(ZEROS),
      "B - Hardware": usdHoy(localDeRegistros(registrosHardware(pc))),
      "B - Hardware / Compra hardware": usdHoy(
        porAno(selectedYear, costosPeru.porCuenta("hardware"), compraHardwareMensualPeru)
      ),
      "B - Hardware / Instalación": usdHoy(porAno(selectedYear, ZEROS, instalacionMensualPeru)),
      "B - Hardware / Materiales": usdHoy(porAno(selectedYear, ZEROS, materialesMensualPeru)),
      "B - Hardware / Otros costos": usdHoy(porAno(selectedYear, ZEROS, otrosCostosMensualPeru)),
      "C - Gestión & Contenidos": usdHoy(localDeRegistros(registrosGestion(pc))),
      "C - Gestión & Contenidos / Software DS": usdHoy(manualPeru.softwareC),
      "C - Gestión & Contenidos / Otros": usdHoy(porAno(selectedYear, ZEROS, manualPeru.otros)),
      "C - Gestión & Contenidos / Contenidos Digitales": usdHoy(ZEROS),
      "D - BTL & otros": usdHoy(localDeRegistros(registrosBTL(pc))),
      "D - BTL & otros / Comisión edificios": usdHoy(localDeRegistros(registrosComisionEdificiosBTL(pc))),
      "D - BTL & otros / Otros costos": usdHoy(porAno(selectedYear, ZEROS, otrosCostosBTLMensualPeru)),
      RRHH: usdHoy(rrhhMensualPeru),
      Depreciación: usdHoy(depreciacionMensualPeru),
      Amortización: usdHoy(amortizacionMensualPeru),
      Intereses: interesesPeruFijo.usdMensual,
      Impuestos: Array(12).fill(impuestosMensualesUSD["Perú"] || 0),
    };
    camposCostosFijos.forEach((campo, idx) => {
      resultado[nombresCostosFijos[idx]] = usdHoy(costosFijosPorConceptoPeruFijo[idx]);
    });
    return resultado;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedYear,
    costosPeru,
    manualPeru,
    compraHardwareMensualPeru,
    instalacionMensualPeru,
    materialesMensualPeru,
    otrosCostosMensualPeru,
    otrosCostosBTLMensualPeru,
    rrhhMensualPeru,
    depreciacionMensualPeru,
    amortizacionMensualPeru,
    factoringPeru,
    dolarMensualPeru,
    dolarFallback,
    oportunidadesPeru,
    contratosPeru,
    salesBTLPeru,
    negociosCarteleriaPeru,
    getDolarValuePeru,
    clientesPeru,
    intermediariosPeru,
    edificiosPeru,
    pctMapPeru,
    snapMapPeru,
    costosMapPeru,
    getUFValue,
    convertirAMonedaLocal,
  ]);
  const filasCombinadoFijo = useMemo(() => {
    const hojas = {};
    HOJAS_ER.forEach((label) => {
      const usd = (chileLeavesUSDFijo[label] || ZEROS).map((v, i) => (v || 0) + (peruLeavesUSDFijo[label]?.[i] || 0));
      hojas[label] = { local: usd, usd };
    });
    return construirFilasER(hojas);
  }, [chileLeavesUSDFijo, peruLeavesUSDFijo]);

  // ─── KPIs DEL HEADER ────────────────────────────────────────
  // PASO 3.7: el hook v1 del EBITDA/header (congelado, archivo se borra en 3.10) se retira de
  // este import — el header y "Acumulados" pasan a leer funciones puras de
  // `estadoResultadosER.js`, con los MISMOS inputs que antes se le pasaban al hook (ver
  // docs/decisiones/2026-10-04-reingenieria-ebitda-fase3-paso-3-7.md).
  const facturacionAAmensual = useMemo(
    () => sumar(pubIngresosAA, ingresosContratosMensualAnoAnterior),
    [pubIngresosAA, ingresosContratosMensualAnoAnterior]
  );
  // Movido acá desde la sección de LTM (3.6b) — también lo necesita `ebitdaMensualAnoAnterior`
  // de abajo. Mismo array, un solo lugar (10.1).
  const facturacionAATotal = useMemo(
    () =>
      sumar(
        facturacionAAmensual,
        ingresosHardwareMensualAnoAnterior,
        ingresosGestionContenidosMensualAnoAnterior,
        ingresosBTLMensualAnoAnterior
      ),
    [
      facturacionAAmensual,
      ingresosHardwareMensualAnoAnterior,
      ingresosGestionContenidosMensualAnoAnterior,
      ingresosBTLMensualAnoAnterior,
    ]
  );

  // EBITDA del año en curso: `facturacion`/`costosVariables`/`costosFijos` (abajo) ya son,
  // término a término, idénticos a lo que el hook retirado armaba por dentro (probado en
  // 2026-10-01, ver comentario de `calcularEbitdaMensual`) — se les resta directo, sin
  // reconstruir nada.
  const ebitdaMensual = useMemo(
    () => calcularEbitdaMensual(facturacion, costosVariables, costosFijos, monthsToShow),
    [facturacion, costosVariables, costosFijos, monthsToShow]
  );

  // EBITDA del año ANTERIOR — solo lo necesitan los Run Rate de abajo (nunca se muestra en
  // una fila propia). `costosVariablesAnoAnteriorPropio` es la única pieza sin equivalente ya
  // existente en index.js (ver comentario de `calcularCostosVariablesAnoAnterior`); el resto
  // de los inputs son EXACTAMENTE los mismos valores que antes se le pasaban al hook.
  const costosVariablesAnoAnteriorPropio = useMemo(
    () =>
      calcularCostosVariablesAnoAnterior({
        costosPublicidadDataAnoAnterior: costosPublicidadDataAnoAnteriorProyectado,
        calcularCostoSoftwareEstimadoAnoAnterior,
        valoresMensualesComisionAgenciaAnoAnterior: sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior),
        valoresMensualesComisionBrokerAnoAnterior: sumar(pubBrokerAA, comisionBrokerContratosMensualAnoAnterior),
        valoresMensualesComisionesAnoAnterior: sumar(
          pubEdificiosAA,
          comisionEdificiosContratosMensualAnoAnterior,
          comisionesFijasMensualAnoAnterior
        ),
        valoresMensualesComisionComercialAnoAnterior: sumar(
          pubComercialAA,
          comisionComercialContratosMensualAnoAnterior
        ),
        valoresMensualesTakeAnoAnterior: pubTakeAA,
        valoresMensualesApxAnoAnterior: sumar(pubApxAA, comisionApxContratosMensualAnoAnterior),
        compraHardwareMensualAnoAnterior,
        instalacionMensualAnoAnterior,
        materialesMensualAnoAnterior,
        otrosCostosMensualAnoAnterior,
        getCostoGestionValueAnoAnterior,
        comisionEdificiosBTLMensualAnoAnterior,
        otrosCostosBTLMensualAnoAnterior,
      }),
    [
      costosPublicidadDataAnoAnteriorProyectado,
      calcularCostoSoftwareEstimadoAnoAnterior,
      pubAgenciaAA,
      comisionAgenciaContratosMensualAnoAnterior,
      pubBrokerAA,
      comisionBrokerContratosMensualAnoAnterior,
      pubEdificiosAA,
      comisionEdificiosContratosMensualAnoAnterior,
      comisionesFijasMensualAnoAnterior,
      pubComercialAA,
      comisionComercialContratosMensualAnoAnterior,
      pubTakeAA,
      pubApxAA,
      comisionApxContratosMensualAnoAnterior,
      compraHardwareMensualAnoAnterior,
      instalacionMensualAnoAnterior,
      materialesMensualAnoAnterior,
      otrosCostosMensualAnoAnterior,
      getCostoGestionValueAnoAnterior,
      comisionEdificiosBTLMensualAnoAnterior,
      otrosCostosBTLMensualAnoAnterior,
    ]
  );
  const ebitdaMensualAnoAnterior = useMemo(
    () =>
      calcularEbitdaMensualAnoAnterior(
        facturacionAATotal,
        costosVariablesAnoAnteriorPropio,
        costosFijosMensualAnoAnterior
      ),
    [facturacionAATotal, costosVariablesAnoAnteriorPropio, costosFijosMensualAnoAnterior]
  );

  // Run Rate / variación — país de sesión (no combinado, no US$): últimos 3/12 meses,
  // cruzando a `facturacionAAmensual`/`facturacionAATotal`/`ebitdaMensualAnoAnterior` si
  // `monthsToShow < 3`/`< 12`. Mismo primitivo para Facturación y EBITDA (`sumarUltimosNMeses`).
  const runRateAnual = Math.round(sumarUltimosNMeses(facturacion, facturacionAATotal, monthsToShow, 3) * 4);
  const facturacionUltimos12Meses = sumarUltimosNMeses(facturacion, facturacionAATotal, monthsToShow, 12);
  const ebitdaRunRateAnual = Math.round(
    sumarUltimosNMeses(ebitdaMensual, ebitdaMensualAnoAnterior, monthsToShow, 3) * 4
  );
  const ebitdaUltimos12Meses = sumarUltimosNMeses(ebitdaMensual, ebitdaMensualAnoAnterior, monthsToShow, 12);
  const variacionVsPropioRitmo = calcularVariacionPorcentual(ebitdaRunRateAnual, ebitdaUltimos12Meses);

  // Run Rate / variación en US$ (checkbox "Ver en US$", país de sesión) — cada mes convertido
  // con SU PROPIO TC antes de sumar.
  const runRateAnualUSD = Math.round(
    sumarUltimosNMesesEnUSD(
      facturacion,
      facturacionAATotal,
      dolarMensual,
      dolarMensualAnoAnterior,
      dolarFallback[pais],
      monthsToShow,
      3
    ) * 4
  );
  const ebitdaRunRateAnualUSD = Math.round(
    sumarUltimosNMesesEnUSD(
      ebitdaMensual,
      ebitdaMensualAnoAnterior,
      dolarMensual,
      dolarMensualAnoAnterior,
      dolarFallback[pais],
      monthsToShow,
      3
    ) * 4
  );

  // Run Rate / variación en modo REGIÓN (combinado) + `runRateParaMostrar`/
  // `ebitdaRunRateParaMostrar`/`variacionFactParaMostrar`/`variacionEbitdaParaMostrar`:
  // necesitan `filasCombinadoFijo.FACTURACIÓN`/`ebitdaCombinadoMensual`, definidos más abajo —
  // ver ese bloque (comentario "KPIs DEL HEADER, continuación").

  // FASE 3 PASO 4b, punto 2 — leen de `filasCombinadoFijo` (fuente única, Chile+Perú modo
  // directo, sin flags) en vez de los combinados viejos armados a mano en los hooks.
  const facturacionMostrar = modoCombinado ? filasCombinadoFijo["FACTURACIÓN"].usd : facturacion;
  const publicidadMostrar = modoCombinado ? filasCombinadoFijo["A - Publicidad"].usd : facturacionA;
  const costosVariablesMostrar = modoCombinado ? filasCombinadoFijo["Costos Variables"].usd : costosVariables;
  const beneficioBrutoMostrar = restar(facturacionMostrar, costosVariablesMostrar);
  const costosFijosMostrar = modoCombinado ? filasCombinadoFijo["Costos Fijos"].usd : costosFijos;

  // ─── Intereses (línea debajo del EBIT): FUENTE ÚNICA (./interesesER.js). ───
  // Todos los sitios que antes recomputaban `interesesMensualesUSD[pais] × TC` inline
  // (acumulados acá + fila Intereses y Beneficio Neto en EBITDAResultadoTable) leen de
  // estos arrays pre-computados. `otroPais` es para el modo región (Chile+Perú en USD).
  const otroPais = pais === "Chile" ? "Perú" : "Chile";
  // Costo financiero de factoring con recurso (NIC 23.5/23.6(a), NIIF 9 B5.4.1): comisión del
  // factor y mora, clasificadas por cuenta_er en `factoring_comision`/`factoring_mora` (fuera
  // de camposCostosFijosV2, ver clasificacionContable.js), se suman a la línea Intereses junto
  // al interés real de factoring. `costos` ya es del país de sesión; para el otro país (modo
  // región) se usa el agregado por país que ya existe (`costosChile`/`costosPeru`), mismo
  // patrón que `factoringOtro`/`dolarMensualOtroPais`.
  const costosPorPais = { Chile: costosChile, Perú: costosPeru };
  const costosOtroPais = costosPorPais[otroPais];

  // Carga de operaciones de factoring de AMBOS países (una vez). No rompe el ER si falla:
  // degrada al baseline del holding, pero deja el error visible (10.177: sin fallo silencioso).
  useEffect(() => {
    let cancelado = false;
    Promise.all([getOperacionesFactoring("Chile"), getOperacionesFactoring("Perú")])
      .then(([ch, pe]) => {
        if (cancelado) return;
        setOpsFactoringChile(ch || []);
        setOpsFactoringPeru(pe || []);
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error("ERv2: error cargando operaciones de factoring para la línea Intereses:", e))
      .finally(() => {
        if (!cancelado) setIsFactoringLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  // Intereses de factoring por mes (moneda local), por país y año del ER.
  const factoringSesion = pais === "Chile" ? factoringChile : factoringPeru;
  const factoringOtro = pais === "Chile" ? factoringPeru : factoringChile;

  // Intereses + comisión + mora de factoring, combinados ANTES de entrar a interesesMensualER
  // (que no cambia de firma: sigue recibiendo un solo array mensual en moneda local). Las tres
  // fuentes están en la MISMA moneda local (costo_financiero nativo; cuenta_er neto/exento sin
  // IVA), así que sumar elemento a elemento no requiere conversión previa.
  const factoringFinancieroSesion = useMemo(
    () => sumar(factoringSesion.mensual, costos.porCuenta("factoring_comision"), costos.porCuenta("factoring_mora")),
    [factoringSesion, costos]
  );
  const interesesER = useMemo(
    () =>
      interesesMensualER({ pais, dolarMensual, dolarFallback, interesesFactoringMensual: factoringFinancieroSesion }),
    [pais, dolarMensual, dolarFallback, factoringFinancieroSesion]
  );
  // PASO 3.6b, H4/H5: la ventana LTM mezcla meses del año en curso con meses del año
  // anterior — los de atrás necesitan SUS PROPIOS intereses/TC (año anterior), nunca los del
  // año en curso aplicados también a slots ajenos. Mismo motor que `interesesER`/`factoringSesion`
  // arriba, con año = `selectedYear - 1` (mismo patrón que `costosAA`/`facturacionAATotal`).
  // `dolarMensualAnoAnterior` ya es del país de sesión (mismo hook `useExchangeRates(pais,...)`
  // que `dolarMensual`), así que esto alcanza a Perú igual que a Chile sin ternario adicional.
  const factoringChileAA = useMemo(
    () => interesesFactoringPorMes({ operaciones: opsFactoringChile, pais: "Chile", ano: selectedYear - 1 }),
    [opsFactoringChile, selectedYear]
  );
  const factoringPeruAA = useMemo(
    () => interesesFactoringPorMes({ operaciones: opsFactoringPeru, pais: "Perú", ano: selectedYear - 1 }),
    [opsFactoringPeru, selectedYear]
  );
  const factoringSesionAA = pais === "Chile" ? factoringChileAA : factoringPeruAA;
  const factoringFinancieroSesionAA = useMemo(
    () =>
      sumar(factoringSesionAA.mensual, costosAA.porCuenta("factoring_comision"), costosAA.porCuenta("factoring_mora")),
    [factoringSesionAA, costosAA]
  );
  const interesesERAnoAnterior = useMemo(
    () =>
      interesesMensualER({
        pais,
        dolarMensual: dolarMensualAnoAnterior,
        dolarFallback,
        interesesFactoringMensual: factoringFinancieroSesionAA,
      }),
    [pais, dolarMensualAnoAnterior, dolarFallback, factoringFinancieroSesionAA]
  );

  // FASE 3 PASO 4 (criterio 1), punto 2b — pesos de "Intereses" (holding), Chile: MISMA función
  // `interesesMensualER` (el factoring se sigue sumando raw, sin conversión — nunca cambia),
  // pero con `dolarMensualPromedioChile` en vez de `dolarMensual` (el TC de hoy) — el holding
  // se convierte con el TC PROMEDIO del mes, no con el último TC disponible. Solo válido cuando
  // `pais==="Chile"` (`factoringFinancieroSesion`/AA son del país de sesión); no se usa para
  // Perú (10.265/10.267).
  const interesesLocalCriterio1Chile = useMemo(
    () =>
      interesesMensualER({
        pais: "Chile",
        dolarMensual: dolarMensualPromedioChile,
        dolarFallback,
        interesesFactoringMensual: factoringFinancieroSesion,
      }).localMensual,
    [dolarMensualPromedioChile, dolarFallback, factoringFinancieroSesion]
  );
  const interesesLocalCriterio1ChileAnoAnterior = useMemo(
    () =>
      interesesMensualER({
        pais: "Chile",
        dolarMensual: dolarMensualPromedioAnoAnterior,
        dolarFallback,
        interesesFactoringMensual: factoringFinancieroSesionAA,
      }).localMensual,
    [dolarMensualPromedioAnoAnterior, dolarFallback, factoringFinancieroSesionAA]
  );

  // ─── Drill de la línea Intereses: la celda del mes abre el modal de detalle. ───
  const onDetalleIntereses = useMemo(
    () => ({ abrir: (mesIndex, mesLabel) => setDetalleIntereses({ mesIndex, mesLabel }) }),
    []
  );

  // Conceptos del mes clickeado, EN LA MISMA UNIDAD que la celda (local × TC del mes, o USD
  // en dólares/región). Σ(monto) == valor de la celda por construcción, así el total del
  // modal cuadra. En región cada préstamo entra con el total holding (= Chile+Perú).
  // FASE 3: acá se concatenan las filas de factoring (origen: "Factoring") ya convertidas.
  const interesesConceptos = useMemo(() => {
    if (!detalleIntereses) return [];
    const i = detalleIntereses.mesIndex;
    const esRegion = modoCombinado;
    const enUSD = esRegion || verEnDolares;

    // Baseline del holding (Fase 2): USD × 1 en dólares/región; × TC del mes en local.
    const tcBaseline = enUSD ? 1 : dolarMensual[i] || dolarFallback[pais] || 0;
    const base = esRegion
      ? interesesHoldingPorPrestamo.map((p) => ({ nombre: p.nombre, montoUSD: p.totalHolding }))
      : interesesDesglosePorPais.map((p) => ({ nombre: p.nombre, montoUSD: p.montoPais }));
    const holdingRows = base.map((c) => ({ nombre: c.nombre, origen: "Holding", monto: c.montoUSD * tcBaseline }));

    // Filas de factoring: `costo_financiero` es nativo local → en local se muestra tal cual;
    // en dólares/región se divide por el TC del mes DE SU PAÍS (igual que la celda por país).
    const filasFactoring = (detallePais, tcMes) =>
      (detallePais[i] || []).map((op) => ({
        nombre: op.proveedor,
        sub: op.folio != null ? `Folio ${op.folio} · ${op.fecha}` : op.fecha,
        origen: "Factoring",
        monto: enUSD ? (tcMes ? op.interes / tcMes : 0) : op.interes,
      }));

    // Filas de comisión/mora de factoring (cuentas nuevas, fuente costosER.detalleDe): mismo
    // patrón que filasFactoring, MISMA fuente que alimenta la celda (costos.porCuenta), así que
    // Σ(conceptos) == celda se sostiene por construcción también para estas dos.
    const filasCuenta = (costosPais, cuenta, origen, tcMes) =>
      costosPais.detalleDe(cuenta, i).map((f) => ({
        nombre: f.proveedor,
        sub: f.numero != null ? `Folio ${f.numero} · ${f.fecha}` : f.fecha,
        origen,
        monto: enUSD ? (tcMes ? f.importe / tcMes : 0) : f.importe,
      }));

    const tcSesion = dolarMensual[i] || dolarFallback[pais] || 0;
    const facRows = filasFactoring(factoringSesion.detalle, tcSesion);
    facRows.push(
      ...filasCuenta(costos, "factoring_comision", "Comisión factoring", tcSesion),
      ...filasCuenta(costos, "factoring_mora", "Mora factoring", tcSesion)
    );
    if (esRegion) {
      const tcOtro = dolarMensualOtroPais[i] || dolarFallback[otroPais] || 0;
      facRows.push(
        ...filasFactoring(factoringOtro.detalle, tcOtro),
        ...filasCuenta(costosOtroPais, "factoring_comision", "Comisión factoring", tcOtro),
        ...filasCuenta(costosOtroPais, "factoring_mora", "Mora factoring", tcOtro)
      );
    }
    return [...holdingRows, ...facRows];
  }, [
    detalleIntereses,
    modoCombinado,
    verEnDolares,
    dolarMensual,
    dolarMensualOtroPais,
    dolarFallback,
    pais,
    otroPais,
    factoringSesion,
    factoringOtro,
    costos,
    costosOtroPais,
  ]);

  const interesesPrefijo = modoCombinado || verEnDolares ? "US$ " : moneda(pais);

  // ─── Acumulados (checkbox del header). ───
  // ─── Checkbox "Activos": series derivadas (ER/index.js:2689, :2701, :2841, :3229) ───
  // PASO 3.6a: `enUSDPorPais` era la MISMA fórmula que `aUSD` (arriba) duplicada otra vez —
  // retirada a favor de `combinarEnUSD`.
  const inversionMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    return combinarEnUSD(
      inversionMensualChile,
      inversionMensualPeru,
      dolarMensualChile,
      dolarMensualPeru,
      dolarFallback
    );
  }, [modoCombinado, inversionMensualChile, inversionMensualPeru, dolarMensualChile, dolarMensualPeru, dolarFallback]);
  const valorActivosCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    return combinarEnUSD(
      valorActivosMensualChile,
      valorActivosMensualPeru,
      dolarMensualChile,
      dolarMensualPeru,
      dolarFallback
    );
  }, [
    modoCombinado,
    valorActivosMensualChile,
    valorActivosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
  ]);

  // El benchmark es el 10% de la facturacion; el disponible, su acumulado menos la
  // inversion acumulada. Antes de 2026 el v1 no los muestra (null).
  const eBenchmarkInv = useMemo(
    () => (selectedYear < 2026 ? null : facturacion.map((v) => (v || 0) * 0.1)),
    [selectedYear, facturacion]
  );
  const eDisponibleInv = useMemo(() => {
    if (selectedYear < 2026) return null;
    let acumBench = 0;
    let acumInv = 0;
    return ZEROS.map((_, i) => {
      acumBench += (facturacion[i] || 0) * 0.1;
      acumInv += inversionMensual[i] || 0;
      return acumBench - acumInv;
    });
  }, [selectedYear, facturacion, inversionMensual]);

  const benchmarkInversionCombinados = useMemo(() => {
    if (!modoCombinado || selectedYear < 2026) return null;
    const facturacionCombinadaFijo = filasCombinadoFijo["FACTURACIÓN"].usd;
    return ZEROS.map((_, i) => (facturacionCombinadaFijo[i] || 0) * 0.1);
  }, [modoCombinado, selectedYear, filasCombinadoFijo]);

  const disponibleInversionCombinados = useMemo(() => {
    if (!modoCombinado || selectedYear < 2026) return null;
    const facturacionCombinadaFijo = filasCombinadoFijo["FACTURACIÓN"].usd;
    let acumBench = 0;
    let acumInv = 0;
    return ZEROS.map((_, i) => {
      acumBench += (facturacionCombinadaFijo[i] || 0) * 0.1;
      acumInv += inversionMensualesCombinados?.[i] || 0;
      return acumBench - acumInv;
    });
  }, [modoCombinado, selectedYear, filasCombinadoFijo, inversionMensualesCombinados]);

  // ─── Ventana movil de 12 meses (checkbox LTM) — ER/index.js:2870 y :2880 ───
  // Cada serie se combina con la del anio anterior; los primeros (12 - monthsToShow)
  // meses son del anio previo.
  const enVentana = useCallback(
    (actual, anterior) => (verUltimos12Meses ? windowArray(actual || ZEROS, anterior || ZEROS, monthsToShow) : actual),
    [verUltimos12Meses, monthsToShow]
  );

  const mesesWindowed = useMemo(() => {
    if (!verUltimos12Meses) return meses;
    return Array.from({ length: 12 }, (_, j) => {
      const mesReal = (monthsToShow + j) % 12;
      const anio = j < 12 - monthsToShow ? selectedYear - 1 : selectedYear;
      return `${meses[mesReal]} ${String(anio).slice(2)}`;
    });
  }, [verUltimos12Meses, monthsToShow, selectedYear]);

  // Series del anio anterior que necesita la ventana LTM. `facturacionAATotal` vive más
  // arriba (junto a `facturacionAAmensual`) desde el paso 3.7 — también la necesita el
  // `ebitdaMensualAnoAnterior` de los KPIs del header.
  // FASE E2c: usaba `costosAA.porCuenta(...)` crudo para los 3 conceptos — sin el switch
  // `porAno` (0 antes de 2026) y, para software_ds, sin restar el estimado de cartelería que el
  // año en curso SÍ resta (`aSoftwareDS`) — doble inconsistencia con `costosManualesA` (arriba).
  // Ahora reusa los MISMOS 3 gemelos año-anterior que ya alimentan `vServiciosInternet`/
  // `vSoftwareDS`/`vMantenimiento` (10.1, un solo cálculo).
  const costosManualesAAA = sumar(aServiciosInternetAnoAnterior, aSoftwareDSAnoAnterior, aMantenimientoAnoAnterior);
  // PASO 3.6b, H6: reparto de `contenidos_digitales` del año anterior (antes solo se calculaba
  // más abajo, para la ventana LTM — se sube acá para reusarlo también en `costosVariablesAA`).
  // `costosVariables` (año en curso) SÍ suma las 2 patas del reparto (A dentro de
  // `costosManualesA`, C directo); `costosVariablesAA` las tenía AFUERA — mismo patrón del bug
  // ya corregido en `ebitdaMensual` (2026-10-01).
  const repartoContenidosAA = (costosAA.porCuenta("contenidos_digitales") || ZEROS).map((monto) =>
    repartirPorPantallas(monto, pantallasPublicidad, pantallasCarteleria)
  );
  // FASE E2c: réplica TÉRMINO A TÉRMINO de `costosVariables` (año en curso, arriba) — antes le
  // faltaban Instalación/Materiales/Otros(B)/Software DS(C)/Otros(C)/Otros(D-BTL) por completo
  // (6 de 13 términos), y "Comisión edificios" sumaba solo 2 de sus 3 términos (faltaba
  // `comisionesFijasMensualAnoAnterior`, mismo gap que `vComEdificios` ya tenía). La ventana LTM
  // de "Costos Variables" venía, en los hechos, de un total DISTINTO al que suman las filas de
  // detalle que se ven en pantalla — mismo defecto que esta Fase 3 existe para eliminar.
  const costosVariablesAA = sumar(
    sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior),
    sumar(pubEdificiosAA, comisionEdificiosContratosMensualAnoAnterior, comisionesFijasMensualAnoAnterior),
    sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior),
    comisionEdificiosBTLMensualAnoAnterior,
    costosManualesAAA,
    porAno(selectedYear - 1, costosAA.porCuenta("hardware"), compraHardwareMensualAnoAnterior),
    instalacionMensualAnoAnterior,
    materialesMensualAnoAnterior,
    otrosCostosMensualAnoAnterior,
    softwareCarteleriaAnoAnterior,
    repartoContenidosAA.map((r) => r.publicidad),
    repartoContenidosAA.map((r) => r.carteleria),
    otrosGestionAnoAnterior,
    otrosCostosBTLMensualAnoAnterior
  );

  // Series finales: en LTM, ventana movil; si no, la serie del anio.
  const vFacturacion = enVentana(facturacionMostrar, facturacionAATotal);
  const vPublicidad = enVentana(publicidadMostrar, sumar(pubIngresosAA, ingresosContratosMensualAnoAnterior));
  const vHardware = enVentana(ingresosHardwareMensual, ingresosHardwareMensualAnoAnterior);
  const vGestion = enVentana(ingresosGestionContenidosMensual, ingresosGestionContenidosMensualAnoAnterior);
  const vBTL = enVentana(ingresosBTLMensual, ingresosBTLMensualAnoAnterior);
  const vIntermediacion = enVentana(
    costoIntermediacion,
    sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior)
  );
  const vComEdificios = enVentana(
    comisionEdificios,
    // FASE E2c: faltaba `comisionesFijasMensualAnoAnterior` — el año en curso suma 3 términos
    // (`comisionEdificios`, arriba), acá solo sumaba 2; mismo gap que Fase 1b ya había cerrado
    // para el header (`costosVariablesAnoAnteriorPropio`), nunca portado a esta ventana.
    sumar(pubEdificiosAA, comisionEdificiosContratosMensualAnoAnterior, comisionesFijasMensualAnoAnterior)
  );
  const vComComercial = enVentana(
    comisionComercial,
    sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior)
  );
  // FASE E2c: las 3 líneas de abajo usaban `costosAA.porCuenta(...)` crudo como año anterior —
  // esa cuenta es la rama FACTURAS, siempre 0 antes de 2026 (ver `INICIO_COSTOS_SII`); sin el
  // switch `porAno` el año anterior de LTM quedaba en 0 cualquier vez que cayera en la era
  // manual, aunque el camino directo de ESE año mostrara el dato real. Mismo switch que ya usa
  // la serie del año en curso, evaluado en `selectedYear - 1`.
  const vServiciosInternet = enVentana(aServiciosInternet, aServiciosInternetAnoAnterior);
  const vSoftwareDS = enVentana(aSoftwareDS, aSoftwareDSAnoAnterior);
  const vMantenimiento = enVentana(aMantenimiento, aMantenimientoAnoAnterior);
  // FASE E2c: ídem para las 2 filas de Bloque C que `BloqueCTable.js` recalculaba inline sin
  // mirar `verUltimos12Meses` (Software DS/Otros, ver comentario junto a su definición arriba).
  const vSoftwareCarteleria = enVentana(softwareCarteleria, softwareCarteleriaAnoAnterior);
  // FASE 3 PASO 3 (criterio 1), punto 1 — causa real del residuo LTM≠vista directa: en modo
  // directo, "Software DS" tiene un caso especial de US$ con el dólar del DÍA 1 del mes
  // (`BloqueCTable.js`, rama `verEnDolares`), no el promedio mensual del `formatCurrency`
  // genérico. Mismo fix de la Fase 3 PASO 3 punto 1 (commit 68ce6bec) — se MANTIENE para Perú
  // (10.265/10.267: Perú no adopta el pipeline de registros del punto 4, pero sigue
  // necesitando ESTE fix, que es un bug fix de la ventana LTM, no un criterio nuevo). Para
  // Chile, `vUSDChile` (más abajo) lo reemplaza con el bottom-up real.
  const vSoftwareCarteleriaUSDPeru = useMemo(() => {
    if (!verUltimos12Meses) return null;
    return vSoftwareCarteleria.map((valor, j) => {
      const mesReal = (monthsToShow + j) % 12;
      const anio = j < 12 - monthsToShow ? selectedYear - 1 : selectedYear;
      const fechaDia1 = `${anio}-${String(mesReal + 1).padStart(2, "0")}-01`;
      const dolarDia1 = getDolarValue(fechaDia1) || 0;
      return dolarDia1 > 0 ? (valor || 0) / dolarDia1 : 0;
    });
  }, [verUltimos12Meses, vSoftwareCarteleria, monthsToShow, selectedYear, getDolarValue]);
  const vOtrosGestion = enVentana(otrosGestion, otrosGestionAnoAnterior);
  // Ventana LTM del reparto: el año anterior se reparte con las MISMAS pantallas de hoy (el
  // conteo es un snapshot, no tiene serie histórica). Pre-2026 el agregado da 0 igual.
  // `repartoContenidosAA` ya se computó arriba (H6, reusado acá — misma fórmula, un solo lugar).
  const vContenidosA = enVentana(
    aContenidosDigitales,
    repartoContenidosAA.map((r) => r.publicidad)
  );
  const vContenidosC = enVentana(
    cContenidosDigitales,
    repartoContenidosAA.map((r) => r.carteleria)
  );
  // FASE E2c: mismo defecto que arriba — `costosAA.porCuenta("hardware")` crudo, sin `porAno`,
  // da 0 todo año anterior pre-2026.
  const vCompraHardware = enVentana(
    bCompraHardware,
    porAno(selectedYear - 1, costosAA.porCuenta("hardware"), compraHardwareMensualAnoAnterior)
  );
  // FASE E2c: Instalación/Materiales/Otros(B)/Otros(D) no tenían NINGÚN año anterior — el
  // segundo argumento era `ZEROS` a secas, la porción del año anterior de la ventana LTM
  // quedaba en 0 por construcción. Los 4 gemelos YA EXISTÍAN (Fase 1b, alimentaban el header)
  // pero nunca se habían conectado acá.
  const vInstalacion = enVentana(bInstalacion, instalacionMensualAnoAnterior);
  const vMateriales = enVentana(bMateriales, materialesMensualAnoAnterior);
  const vOtrosHardware = enVentana(bOtrosHardware, otrosCostosMensualAnoAnterior);
  const vComEdificiosBTL = enVentana(comisionEdificiosBTLMensual, comisionEdificiosBTLMensualAnoAnterior);
  const vOtrosBTL = enVentana(otrosBTL, otrosCostosBTLMensualAnoAnterior);
  const vRRHH = enVentana(rrhhMensualConBeneficios, rrhhMensualConBeneficiosAnoAnterior);
  const vCostosVariables = enVentana(costosVariablesMostrar, costosVariablesAA);
  // FASE E2c: `costosFijosAA` (definida más abajo) reimplementaba esta MISMA cuenta con
  // `costosAA.porCuenta(campo)` crudo, sin el switch `porAno` que `costosFijosMensualAnoAnterior`
  // (Fase 1b, ya construida para el header) sí aplica — dos caminos para el mismo hecho, uno
  // roto (10.1). Se retira `costosFijosAA`, la ventana lee directo de la fuente única.
  const vCostosFijos = enVentana(costosFijosMostrar, costosFijosMensualAnoAnterior);
  // FASE E2c: las 13 cuentas de Costos Fijos, UNA POR UNA, para que el detalle de
  // `CostosFijosTable` deje de leer el array crudo de la sesión (`costosFijosFilas[i]`,
  // indexado por mes calendario del año en curso) en modo LTM — ahí el índice `i` es un SLOT
  // de ventana (puede representar un mes del año anterior), no un mes calendario real.
  const costosFijosPorConceptoWindowed = camposCostosFijos.map((_, idx) =>
    enVentana(costosFijosPorConcepto[idx], costosFijosPorConceptoAnoAnterior[idx])
  );
  // FASE E2c: Depreciación/Amortización tampoco tenían año anterior (ZEROS) — gemelos nuevos
  // en `useERv2Data` (`depreciacionMensualAnoAnterior`/`amortizacionMensualAnoAnterior`, mismo
  // fetch `getDepreciacionYAmortizacion` ya usado para el año en curso, evaluado en año-1).
  const vDepreciacion = enVentana(depreciacionMensual, depreciacionMensualAnoAnterior);
  const vAmortizacion = enVentana(amortizacionMensual, amortizacionMensualAnoAnterior);
  const vInversion = enVentana(inversionMensual, ZEROS);
  const vValorActivos = enVentana(valorActivosMensual, ZEROS);
  const vBeneficioBruto = restar(vFacturacion, vCostosVariables);
  const vContribucionA = restar(
    vPublicidad,
    vIntermediacion,
    vComEdificios,
    vComComercial,
    sumar(vServiciosInternet, vSoftwareDS, vMantenimiento, vContenidosA)
  );
  const vContribucionB = restar(vHardware, vCompraHardware, vInstalacion, vMateriales, vOtrosHardware);
  // FASE E2c (hallazgo): "Contribución" del Bloque C nunca tuvo una ventana propia — a
  // diferencia de A/B/D (arriba), `BloqueCTable` leía directo `margenGestionContenidosAjustado`
  // (el margen de `useGestionCalcs`, el hook v1 congelado, menos `cContenidosDigitales` crudo),
  // sin mirar `verUltimos12Meses` — en LTM mostraba el margen del AÑO EN CURSO sin ventanear.
  // Réplica del mismo patrón que A/B/D: total de bloque menos sus 3 sub-filas, todas YA
  // ventaneadas.
  const vContribucionC = restar(vGestion, vSoftwareCarteleria, vOtrosGestion, vContenidosC);
  // FASE 3 PASO 3 (criterio 1), punto 1 — "Contribución C" sumada desde sus leaves YA
  // convertidos (no una reconversión del total en pesos) — mismo fix de la Fase 3 PASO 3
  // punto 1 (commit 68ce6bec), MANTENIDO para Perú (ver comentario junto a
  // `vSoftwareCarteleriaUSDPeru`). Para Chile, `vUSDChile` lo reemplaza con el bottom-up real.
  const vContribucionCUSDPeru = useMemo(() => {
    if (!verUltimos12Meses) return null;
    const aUSD = (v, i) => {
      const tc = dolarMensualParaFormato[i] || 0;
      return tc > 0 ? (v || 0) / tc : 0;
    };
    return vGestion.map(
      (_, i) =>
        aUSD(vGestion[i], i) -
        (vSoftwareCarteleriaUSDPeru?.[i] || 0) -
        aUSD(vOtrosGestion[i], i) -
        aUSD(vContenidosC[i], i)
    );
  }, [verUltimos12Meses, vGestion, vSoftwareCarteleriaUSDPeru, vOtrosGestion, vContenidosC, dolarMensualParaFormato]);
  const vContribucionD = restar(vBTL, vComEdificiosBTL, vOtrosBTL);
  const vEbitda = restar(vFacturacion, vCostosVariables, vCostosFijos);

  const rateArrPais = pais === "Chile" ? dolarMensual : dolarMensualPeru;
  const fbPais = pais === "Chile" ? dolarFallback.Chile : dolarFallback["Perú"];
  const impUSDPais = impuestosMensualesUSD[pais] || 0;

  // ─── FASE E2a (2026-10-05) — estructura `filas` (modo normal, sin LTM/Sumar Perú): cada fila
  // hoja llega con su `.local` (el mismo valor que hoy usa la pantalla, sin tocar) y su `.usd`
  // construido con `convertirSerieMensualAUSD` (la misma función que ya usa `acumularEnUSD`
  // para Acumulados, 10.1 — nunca una conversión genérica nueva). Las filas suma salen TODAS de
  // `construirFilasER`, nunca recalculadas acá. "Impuestos" usa el fallback de 2 capas
  // `rateArrPais[i] || fbPais` — el mismo que ya usa la celda mensual de EBITDAResultadoTable
  // (la 3ª capa `getDolarFallback(pais)` del viejo YTD era defensiva, nunca disparada con datos
  // reales — ver docs/decisiones/2026-10-05-...-e2a.md si algún día dispara en regresión).
  const aUSDHoy = useCallback((local) => convertirSerieMensualAUSD(local, rateArrPais, fbPais), [rateArrPais, fbPais]);
  const impuestosLocalLeaf = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => impUSDPais * (rateArrPais[i] || fbPais || 0)),
    [impUSDPais, rateArrPais, fbPais]
  );
  const impuestosUsdLeaf = useMemo(() => Array(12).fill(impUSDPais), [impUSDPais]);
  // FASE 3 PASO 4 (criterio 1), punto 2d — Impuestos, Chile: mismo pipeline (fecha null -> TC
  // PROMEDIO del mes) que RRHH/Depreciación/Amortización — sin efecto numérico hoy (el
  // constante vale 0), pero estructuralmente listo para cuando se popule (10.1, nunca dos
  // mecanismos para el mismo problema). El US$ ya es la constante sin TC (`impuestosUsdLeaf`),
  // igual en los dos países — no cambia.
  const impuestosLocalCriterio1Chile = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => impUSDPais * (dolarMensualPromedioChile[i] || 0)),
    [impUSDPais, dolarMensualPromedioChile]
  );
  const impuestosLocalCriterio1ChileAnoAnterior = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => impUSDPais * (dolarMensualPromedioAnoAnterior[i] || 0)),
    [impUSDPais, dolarMensualPromedioAnoAnterior]
  );

  const manualOtrosGestionActual = ZEROS.map((_, i) => Number(getCostoGestionValue(i, "otros")) || 0);
  const manualSoftwareDSCManual = ZEROS.map((_, i) => Number(getCostoGestionValue(i, "software_ds")) || 0);
  const manualOtrosGestionAA = ZEROS.map((_, i) => Number(getCostoGestionValueAnoAnterior(i, "otros")) || 0);
  const manualSoftwareDSCManualAA = ZEROS.map((_, i) => Number(getCostoGestionValueAnoAnterior(i, "software_ds")) || 0);

  const chileLeavesUSD = useMemo(() => {
    if (pais !== "Chile") return null;
    return construirLeavesUsdCriterio1Chile(selectedYear, {
      costosDelAno: costos,
      softwareDSResidual: aSoftwareDS,
      softwareDSCManual: manualSoftwareDSCManual,
      manualServInternet: manualServiciosInternet,
      manualMantenimiento,
      manualCompraHardware: compraHardwareMensual,
      manualInstalacion: instalacionMensual,
      manualMateriales: materialesMensual,
      manualOtrosHardware: otrosCostosMensual,
      manualOtrosGestion: manualOtrosGestionActual,
      manualOtrosBTL: otrosCostosBTLMensual,
      contenidosA: aContenidosDigitales,
      contenidosC: cContenidosDigitales,
      empleadosRRHHDelAno: empleadosRRHH,
      costosFijosPorConceptoDelAno: costosFijosPorConcepto,
      depreciacionDelAno: depreciacionMensual,
      amortizacionDelAno: amortizacionMensual,
      factoringDetalleDelAno: factoringChile.detalle,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    empleadosRRHH,
    getSueldoBaseVigenteParaAno,
    costosFijosPorConcepto,
    depreciacionMensual,
    amortizacionMensual,
    factoringChile,
    pais,
    selectedYear,
    costos,
    aSoftwareDS,
    manualSoftwareDSCManual,
    manualServiciosInternet,
    manualMantenimiento,
    compraHardwareMensual,
    instalacionMensual,
    materialesMensual,
    otrosCostosMensual,
    manualOtrosGestionActual,
    otrosCostosBTLMensual,
    aContenidosDigitales,
    cContenidosDigitales,
    dolarMensualPromedioChile,
    oportunidades,
    contratosSesion,
    salesBTL,
    salesBTLAnoAnterior,
    negociosCarteleria,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    edificios,
    pctMap,
    snapMap,
    costosMap,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
    facturasEmitidasChile,
    lineaIdATipo,
  ]);

  const chileLeavesUSDAnoAnterior = useMemo(() => {
    if (pais !== "Chile") return null;
    return construirLeavesUsdCriterio1Chile(selectedYear - 1, {
      costosDelAno: costosAA,
      softwareDSResidual: aSoftwareDSAnoAnterior,
      softwareDSCManual: manualSoftwareDSCManualAA,
      manualServInternet: manualServiciosInternetAA,
      manualMantenimiento: manualMantenimientoAA,
      manualCompraHardware: compraHardwareMensualAnoAnterior,
      manualInstalacion: instalacionMensualAnoAnterior,
      manualMateriales: materialesMensualAnoAnterior,
      manualOtrosHardware: otrosCostosMensualAnoAnterior,
      manualOtrosGestion: manualOtrosGestionAA,
      manualOtrosBTL: otrosCostosBTLMensualAnoAnterior,
      contenidosA: repartoContenidosAA.map((r) => r.publicidad),
      contenidosC: repartoContenidosAA.map((r) => r.carteleria),
      empleadosRRHHDelAno: empleadosRRHH,
      costosFijosPorConceptoDelAno: costosFijosPorConceptoAnoAnterior,
      depreciacionDelAno: depreciacionMensualAnoAnterior,
      amortizacionDelAno: amortizacionMensualAnoAnterior,
      factoringDetalleDelAno: factoringChileAA.detalle,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    empleadosRRHH,
    getSueldoBaseVigenteParaAno,
    costosFijosPorConceptoAnoAnterior,
    depreciacionMensualAnoAnterior,
    amortizacionMensualAnoAnterior,
    factoringChileAA,
    pais,
    selectedYear,
    costosAA,
    aSoftwareDSAnoAnterior,
    manualSoftwareDSCManualAA,
    manualServiciosInternetAA,
    manualMantenimientoAA,
    compraHardwareMensualAnoAnterior,
    instalacionMensualAnoAnterior,
    materialesMensualAnoAnterior,
    otrosCostosMensualAnoAnterior,
    manualOtrosGestionAA,
    otrosCostosBTLMensualAnoAnterior,
    repartoContenidosAA,
    dolarMensualPromedioAnoAnterior,
    oportunidades,
    contratosSesion,
    salesBTL,
    salesBTLAnoAnterior,
    negociosCarteleria,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    edificios,
    pctMap,
    snapMap,
    costosMap,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
    facturasEmitidasChile,
    lineaIdATipo,
  ]);

  const filas = useMemo(() => {
    // FASE 3 PASO 3 (criterio 1), punto 4: para Chile, las 16 filas-hoja de A-D usan el US$
    // bottom-up de `chileLeavesUSD` (registros/Criterio 1, estadoResultadosER.js/ingresosER.js)
    // en vez de `aUSDHoy` (el criterio de hoy, un solo TC por mes). Perú no cambia (10.265/
    // 10.267): `chileLeavesUSD` es `null` ahí, cae siempre a `leaf`. El PESO (`local`) es el
    // MISMO de siempre en los dos países — esto solo reemplaza el `.usd`.
    const leafA = (local, label) => ({ local, usd: chileLeavesUSD?.[label] ?? aUSDHoy(local) });
    const hojas = {
      "A - Publicidad": leafA(facturacionA, "A - Publicidad"),
      "A - Publicidad / Costo intermediación": leafA(costoIntermediacion, "A - Publicidad / Costo intermediación"),
      "A - Publicidad / Comisión edificios": leafA(comisionEdificios, "A - Publicidad / Comisión edificios"),
      "A - Publicidad / Comisión comercial": leafA(comisionComercial, "A - Publicidad / Comisión comercial"),
      "A - Publicidad / Servicios internet": leafA(aServiciosInternet, "A - Publicidad / Servicios internet"),
      "A - Publicidad / Software DS": leafA(aSoftwareDS, "A - Publicidad / Software DS"),
      "A - Publicidad / Mantenimiento": leafA(aMantenimiento, "A - Publicidad / Mantenimiento"),
      "A - Publicidad / Contenidos Digitales": leafA(aContenidosDigitales, "A - Publicidad / Contenidos Digitales"),
      "B - Hardware": leafA(ingresosHardwareMensual, "B - Hardware"),
      "B - Hardware / Compra hardware": leafA(bCompraHardware, "B - Hardware / Compra hardware"),
      "B - Hardware / Instalación": leafA(bInstalacion, "B - Hardware / Instalación"),
      "B - Hardware / Materiales": leafA(bMateriales, "B - Hardware / Materiales"),
      "B - Hardware / Otros costos": leafA(bOtrosHardware, "B - Hardware / Otros costos"),
      "C - Gestión & Contenidos": leafA(ingresosGestionContenidosMensual, "C - Gestión & Contenidos"),
      "C - Gestión & Contenidos / Software DS": leafA(softwareCarteleria, "C - Gestión & Contenidos / Software DS"),
      "C - Gestión & Contenidos / Otros": leafA(otrosGestion, "C - Gestión & Contenidos / Otros"),
      "C - Gestión & Contenidos / Contenidos Digitales": leafA(
        cContenidosDigitales,
        "C - Gestión & Contenidos / Contenidos Digitales"
      ),
      "D - BTL & otros": leafA(ingresosBTLMensual, "D - BTL & otros"),
      "D - BTL & otros / Comisión edificios": leafA(
        comisionEdificiosBTLMensual,
        "D - BTL & otros / Comisión edificios"
      ),
      "D - BTL & otros / Otros costos": leafA(otrosBTL, "D - BTL & otros / Otros costos"),
      // FASE 3 PASO 4 (criterio 1), punto 1a: RRHH usa el bottom-up Criterio 1b (TC promedio del
      // mes de devengo) de `chileLeavesUSD`, igual que las filas A-D. Perú no cambia.
      RRHH: leafA(rrhhMensualConBeneficios, "RRHH"),
      // FASE 3 PASO 4 (criterio 1), punto 2a: Depreciación/Amortización, Chile — peso sin
      // cambio, US$ = TC promedio del mes (Criterio 1b).
      Depreciación: leafA(depreciacionMensual, "Depreciación"),
      Amortización: leafA(amortizacionMensual, "Amortización"),
      // FASE 3 PASO 4 (criterio 1), punto 2b/2c: Intereses, Chile — holding con TC promedio
      // (peso) + factoring real/comisión/mora con su propia fecha (US$, `chileLeavesUSD`).
      Intereses:
        pais === "Chile"
          ? { local: interesesLocalCriterio1Chile, usd: chileLeavesUSD?.Intereses ?? interesesER.usdMensual }
          : { local: interesesER.localMensual, usd: interesesER.usdMensual },
      // FASE 3 PASO 4 (criterio 1), punto 2d: Impuestos, Chile — mismo pipeline, TC promedio.
      Impuestos:
        pais === "Chile"
          ? { local: impuestosLocalCriterio1Chile, usd: impuestosUsdLeaf }
          : { local: impuestosLocalLeaf, usd: impuestosUsdLeaf },
    };
    // FASE 3 PASO 4 (criterio 1), punto 1b: las 13 cuentas de Costos Fijos usan el bottom-up
    // de `chileLeavesUSD` (2026+ factura con su fecha_emision, <2026 TC promedio) — Perú no
    // cambia (`chileLeavesUSD` es null ahí, cae a `leaf`/`aUSDHoy` de siempre).
    NOMBRES_COSTOS_FIJOS_ER.forEach((nombre, idx) => {
      hojas[nombre] = leafA(costosFijosPorConcepto[idx], nombre);
    });
    return construirFilasER(hojas);
  }, [
    aUSDHoy,
    chileLeavesUSD,
    facturacionA,
    costoIntermediacion,
    comisionEdificios,
    comisionComercial,
    aServiciosInternet,
    aSoftwareDS,
    aMantenimiento,
    aContenidosDigitales,
    ingresosHardwareMensual,
    bCompraHardware,
    bInstalacion,
    bMateriales,
    bOtrosHardware,
    ingresosGestionContenidosMensual,
    softwareCarteleria,
    otrosGestion,
    cContenidosDigitales,
    ingresosBTLMensual,
    comisionEdificiosBTLMensual,
    otrosBTL,
    rrhhMensualConBeneficios,
    depreciacionMensual,
    amortizacionMensual,
    interesesER,
    interesesLocalCriterio1Chile,
    impuestosLocalLeaf,
    impuestosLocalCriterio1Chile,
    impuestosUsdLeaf,
    costosFijosPorConcepto,
    pais,
  ]);

  // ─── FASE 3 PASO 3 (criterio 1), punto 4 — réplica de `filas`, un año atrás, SOLO para poder
  // ventanear en US$ bottom-up (LTM) las filas que sí están en alcance: Costos Variables/
  // Beneficio Bruto/EBITDA/Contribución A-D/Software DS A-C. Las hojas "debajo del EBITDA"
  // (RRHH/Depreciación/Amortización/Intereses/Impuestos/Costos Fijos) van con `aUSDHoy` tal
  // cual — son estructuralmente obligatorias para `construirFilasER` pero esta ventana NUNCA
  // lee su EBIT/Beneficio Neto derivado (4f: quedan en el criterio de hoy, sin cambio). ───
  const filasAnoAnterior = useMemo(() => {
    // BUG encontrado y corregido en este mismo punto 4: las hojas "debajo del EBITDA" (RRHH/
    // Depreciación/Amortización/Intereses/Impuestos/Costos Fijos) NO pueden convertirse con
    // `aUSDHoy` — ese helper usa el TC del AÑO EN CURSO (`rateArrPais`/`fbPais`), y estas hojas
    // son pesos de `selectedYear - 1`. Usar el TC de hoy sobre pesos de hace un año inflaba el
    // residuo de EBITDA/EBIT/Beneficio Neto en la porción año-anterior de la ventana LTM (hasta
    // ±555, meses 1-3) — mismo patrón H4/H5 (TC del período equivocado), ahora en año-anterior
    // en vez de en el slot de ventana.
    const aUSDHoyAnoAnterior = (local) => convertirSerieMensualAUSD(local, dolarMensualAnoAnterior, fbPais);
    const leaf = (local) => ({ local, usd: aUSDHoyAnoAnterior(local) });
    const leafA = (local, label) => ({
      local,
      usd: chileLeavesUSDAnoAnterior?.[label] ?? aUSDHoyAnoAnterior(local),
    });
    const costoIntermediacionAA = sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior);
    const comisionEdificiosAAPeso = sumar(
      pubEdificiosAA,
      comisionEdificiosContratosMensualAnoAnterior,
      comisionesFijasMensualAnoAnterior
    );
    const comisionComercialAAPeso = sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior);
    const hojas = {
      // FASE 3 PASO 4b, punto 1 — bug real encontrado al medir: esta hoja usaba
      // `facturacionAATotal` (la SUMA de A+B+C+D, variable del header/KPI de año anterior,
      // ver su propia definición) en vez de `facturacionAAmensual` ("A - Publicidad" sola,
      // MISMA fórmula que `facturacionA` del año en curso: pubIngresos+ingresosContratosMensual).
      // Al pasar por `construirFilasER` ("FACTURACIÓN" = sumarFilas(A,B,C,D)), esto duplicaba
      // B+C+D dentro de "A - Publicidad" año-anterior y los volvía a sumar — EBITDA/EBIT/
      // Beneficio Neto de año-anterior quedaban inflados en la suma de B+C+D de cada mes
      // (medido: hasta +$7,4M en un mes, Chile). Causa raíz de la divergencia filasAnoAnterior
      // vs vista directa — ver doc de cierre, punto 1a.
      "A - Publicidad": leafA(facturacionAAmensual, "A - Publicidad"),
      "A - Publicidad / Costo intermediación": leafA(costoIntermediacionAA, "A - Publicidad / Costo intermediación"),
      "A - Publicidad / Comisión edificios": leafA(comisionEdificiosAAPeso, "A - Publicidad / Comisión edificios"),
      "A - Publicidad / Comisión comercial": leafA(comisionComercialAAPeso, "A - Publicidad / Comisión comercial"),
      "A - Publicidad / Servicios internet": leafA(
        aServiciosInternetAnoAnterior,
        "A - Publicidad / Servicios internet"
      ),
      "A - Publicidad / Software DS": leafA(aSoftwareDSAnoAnterior, "A - Publicidad / Software DS"),
      "A - Publicidad / Mantenimiento": leafA(aMantenimientoAnoAnterior, "A - Publicidad / Mantenimiento"),
      "A - Publicidad / Contenidos Digitales": leafA(
        repartoContenidosAA.map((r) => r.publicidad),
        "A - Publicidad / Contenidos Digitales"
      ),
      "B - Hardware": leafA(ingresosHardwareMensualAnoAnterior, "B - Hardware"),
      "B - Hardware / Compra hardware": leafA(
        porAno(selectedYear - 1, costosAA.porCuenta("hardware"), compraHardwareMensualAnoAnterior),
        "B - Hardware / Compra hardware"
      ),
      "B - Hardware / Instalación": leafA(instalacionMensualAnoAnterior, "B - Hardware / Instalación"),
      "B - Hardware / Materiales": leafA(materialesMensualAnoAnterior, "B - Hardware / Materiales"),
      "B - Hardware / Otros costos": leafA(otrosCostosMensualAnoAnterior, "B - Hardware / Otros costos"),
      "C - Gestión & Contenidos": leafA(ingresosGestionContenidosMensualAnoAnterior, "C - Gestión & Contenidos"),
      "C - Gestión & Contenidos / Software DS": leafA(
        softwareCarteleriaAnoAnterior,
        "C - Gestión & Contenidos / Software DS"
      ),
      "C - Gestión & Contenidos / Otros": leafA(otrosGestionAnoAnterior, "C - Gestión & Contenidos / Otros"),
      "C - Gestión & Contenidos / Contenidos Digitales": leafA(
        repartoContenidosAA.map((r) => r.carteleria),
        "C - Gestión & Contenidos / Contenidos Digitales"
      ),
      "D - BTL & otros": leafA(ingresosBTLMensualAnoAnterior, "D - BTL & otros"),
      "D - BTL & otros / Comisión edificios": leafA(
        comisionEdificiosBTLMensualAnoAnterior,
        "D - BTL & otros / Comisión edificios"
      ),
      "D - BTL & otros / Otros costos": leafA(otrosCostosBTLMensualAnoAnterior, "D - BTL & otros / Otros costos"),
      RRHH: leafA(rrhhMensualConBeneficiosAnoAnterior, "RRHH"),
      Depreciación: leafA(depreciacionMensualAnoAnterior, "Depreciación"),
      Amortización: leafA(amortizacionMensualAnoAnterior, "Amortización"),
      Intereses:
        pais === "Chile"
          ? {
              local: interesesLocalCriterio1ChileAnoAnterior,
              usd: chileLeavesUSDAnoAnterior?.Intereses ?? interesesERAnoAnterior.usdMensual,
            }
          : leaf(interesesERAnoAnterior.localMensual),
      Impuestos:
        pais === "Chile"
          ? { local: impuestosLocalCriterio1ChileAnoAnterior, usd: impuestosUsdLeaf }
          : { local: impuestosLocalLeaf, usd: impuestosUsdLeaf },
    };
    NOMBRES_COSTOS_FIJOS_ER.forEach((nombre, idx) => {
      hojas[nombre] = leafA(costosFijosPorConceptoAnoAnterior[idx], nombre);
    });
    return construirFilasER(hojas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dolarMensualAnoAnterior,
    fbPais,
    chileLeavesUSDAnoAnterior,
    facturacionAAmensual,
    pubAgenciaAA,
    comisionAgenciaContratosMensualAnoAnterior,
    pubEdificiosAA,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionesFijasMensualAnoAnterior,
    pubComercialAA,
    comisionComercialContratosMensualAnoAnterior,
    aServiciosInternetAnoAnterior,
    aSoftwareDSAnoAnterior,
    aMantenimientoAnoAnterior,
    repartoContenidosAA,
    ingresosHardwareMensualAnoAnterior,
    selectedYear,
    costosAA,
    compraHardwareMensualAnoAnterior,
    instalacionMensualAnoAnterior,
    materialesMensualAnoAnterior,
    otrosCostosMensualAnoAnterior,
    ingresosGestionContenidosMensualAnoAnterior,
    softwareCarteleriaAnoAnterior,
    otrosGestionAnoAnterior,
    ingresosBTLMensualAnoAnterior,
    comisionEdificiosBTLMensualAnoAnterior,
    otrosCostosBTLMensualAnoAnterior,
    rrhhMensualConBeneficiosAnoAnterior,
    depreciacionMensualAnoAnterior,
    amortizacionMensualAnoAnterior,
    interesesERAnoAnterior,
    interesesLocalCriterio1ChileAnoAnterior,
    impuestosLocalLeaf,
    impuestosLocalCriterio1ChileAnoAnterior,
    impuestosUsdLeaf,
    costosFijosPorConceptoAnoAnterior,
    pais,
  ]);

  // FASE 3 PASO 3 (criterio 1), punto 4 — US$ de una fila de `filas` (`construirFilasER`),
  // SOLO Chile, SIEMPRE bottom-up (nunca reconvierte un total en pesos): en modo directo, el
  // `.usd` que `filas` ya construyó arriba; en LTM, ese mismo `.usd` ventaneado contra
  // `filasAnoAnterior` (misma estructura, un año atrás) — nunca un TC único por celda. Para
  // Perú devuelve `null`: el renderer cae a su camino de siempre, sin cambio (10.265/10.267).
  // Con esto "no quedan dos caminos" para las filas en alcance (punto 4 del encargo) — ver el
  // retiro de los `vSoftwareCarteleriaUSD`/`vContribucionCUSD` ad-hoc, abajo.
  const vUSDChile = useCallback(
    (row) => {
      if (pais !== "Chile") return null;
      if (!verUltimos12Meses) return filas[row]?.usd ?? null;
      return windowArray(filas[row]?.usd || ZEROS, filasAnoAnterior[row]?.usd || ZEROS, monthsToShow);
    },
    [pais, verUltimos12Meses, filas, filasAnoAnterior, monthsToShow]
  );
  const vCostosVariablesUSD = vUSDChile("Costos Variables");
  const vBeneficioBrutoUSD = vUSDChile("Beneficio Bruto");
  const vEbitdaUSD = vUSDChile("EBITDA");
  const vPublicidadUSD = vUSDChile("A - Publicidad");
  const vIntermediacionUSD = vUSDChile("A - Publicidad / Costo intermediación");
  const vComEdificiosUSD = vUSDChile("A - Publicidad / Comisión edificios");
  const vComComercialUSD = vUSDChile("A - Publicidad / Comisión comercial");
  const vServiciosInternetUSD = vUSDChile("A - Publicidad / Servicios internet");
  const vMantenimientoUSD = vUSDChile("A - Publicidad / Mantenimiento");
  const vContenidosAUSD = vUSDChile("A - Publicidad / Contenidos Digitales");
  const vHardwareUSD = vUSDChile("B - Hardware");
  const vCompraHardwareUSD = vUSDChile("B - Hardware / Compra hardware");
  const vInstalacionUSD = vUSDChile("B - Hardware / Instalación");
  const vMaterialesUSD = vUSDChile("B - Hardware / Materiales");
  const vOtrosHardwareUSD = vUSDChile("B - Hardware / Otros costos");
  const vGestionUSD = vUSDChile("C - Gestión & Contenidos");
  const vOtrosGestionUSD = vUSDChile("C - Gestión & Contenidos / Otros");
  const vContenidosCUSD = vUSDChile("C - Gestión & Contenidos / Contenidos Digitales");
  const vBTLUSD = vUSDChile("D - BTL & otros");
  const vComEdificiosBTLUSD = vUSDChile("D - BTL & otros / Comisión edificios");
  const vOtrosBTLUSD = vUSDChile("D - BTL & otros / Otros costos");
  const vFacturacionUSD = vUSDChile("FACTURACIÓN");
  const vContribucionAUSD = vUSDChile("A - Publicidad / Contribución");
  const vContribucionBUSD = vUSDChile("B - Hardware / Contribución");
  // "Contribución C" y "Software DS (C)": para Perú, cae al fix de la Fase 3 PASO 3 punto 1
  // (día-1, `vSoftwareCarteleriaUSDPeru`/`vContribucionCUSDPeru`) — nunca `null` (eso dejaría la
  // fila en 0 en LTM+US$ para Perú, una regresión real del punto 1 que este punto 4 no toca).
  const vContribucionCUSD =
    pais === "Chile" ? vUSDChile("C - Gestión & Contenidos / Contribución") : vContribucionCUSDPeru;
  const vContribucionDUSD = vUSDChile("D - BTL & otros / Contribución");
  const vSoftwareDSAUSD = vUSDChile("A - Publicidad / Software DS");
  const vSoftwareCarteleriaUSD =
    pais === "Chile" ? vUSDChile("C - Gestión & Contenidos / Software DS") : vSoftwareCarteleriaUSDPeru;
  // "Beneficio Operativo | EBIT"/"Beneficio Neto": bottom-up = EBITDA (nuevo) − Depreciación/
  // Amortización/Intereses/Impuestos (criterio de hoy, SIN cambio — 4f). `construirFilasER` ya
  // hace esta resta por dentro (misma fuente única); acá solo se ventanea para LTM.
  // FASE 3 PASO 4 (criterio 1), punto 1a — RRHH (Chile): bottom-up, ya ventaneado para LTM.
  const vRRHHUSD = vUSDChile("RRHH");
  // FASE 3 PASO 4 (criterio 1) — "Costos Fijos" (total): SOLO sumando filas ya convertidas
  // (`construirFilasER`), nunca una reconversión genérica del total en pesos — si no, el
  // total deja de cuadrar con RRHH apenas ESA fila entra en Criterio 1 (10.1, "no quedan dos
  // caminos" de PASO 3 punto 4, hallazgo fase38E1EstructuraFilasER).
  const vCostosFijosUSD = vUSDChile("Costos Fijos");
  // FASE 3 PASO 4 (criterio 1), punto 1b — las 13 cuentas de Costos Fijos (Oficina...Servicios
  // Online), cada una bottom-up (2026+ factura con su fecha_emision, <2026 TC promedio).
  const vCuentasFijasUSD = useMemo(() => {
    const map = {};
    NOMBRES_COSTOS_FIJOS_ER.forEach((nombre) => {
      map[nombre] = vUSDChile(nombre);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vUSDChile]);
  // FASE 3 PASO 4 (criterio 1), punto 2a — Depreciación/Amortización (Chile): bottom-up,
  // ya ventaneado para LTM.
  const vDepreciacionUSD = vUSDChile("Depreciación");
  const vAmortizacionUSD = vUSDChile("Amortización");
  // FASE 3 PASO 4 (criterio 1), punto 2b/2c — Intereses (Chile): US$ bottom-up, ya ventaneado
  // para LTM; peso (holding a TC promedio) ventaneado con el mismo `enVentana` que ya usa
  // `vInteresesLocal` (hoy) — ventana propia para no mezclar criterios entre slots.
  const vInteresesUSD = vUSDChile("Intereses");
  const vInteresesLocalCriterio1Chile = enVentana(
    interesesLocalCriterio1Chile,
    interesesLocalCriterio1ChileAnoAnterior
  );

  // ─── FASE 3 PASO 4b, punto 2 — "Sumar Perú"/"Sumar Chile" SIN flags. ───
  // `filasCombinado` = `filasCombinadoFijo` (arriba, antes de `filas`): cada fila es
  // filasChileFijo[label].usd + filasPeruFijo[label].usd, siempre, sin mirar la sesión activa.
  // Gate `modoCombinado` preservado (los consumidores de abajo asumen `null` cuando está
  // apagado — nunca se calculaba solo por eso).
  const filasCombinado = modoCombinado ? filasCombinadoFijo : null;

  const ebitdaCombinadoMensual = useMemo(
    () => (modoCombinado ? filasCombinado.EBITDA.usd : null),
    [modoCombinado, filasCombinado]
  );

  // ─── KPIs DEL HEADER, continuación — Run Rate/variación en modo REGIÓN. ───
  // FASE E2b: pasa a leer de `filasCombinado.FACTURACIÓN.usd` (fuente única, punto 2 del
  // encargo) — mismo valor que `facturacionCombinada` (la MISMA suma de las 4 filas de
  // ingreso), así que el delta es CERO; la diferencia es solo de dónde sale el número.
  const mesesTranscurridosRegion = selectedYear === currentYear ? monthsToShow : 12;
  const facturacionRegionUSD = modoCombinado ? filasCombinado.FACTURACIÓN.usd : ZEROS;
  const runRateAnualRegion = modoCombinado
    ? Math.round(sumarUltimosNMesesSimple(facturacionRegionUSD, monthsToShow, 3) * 4)
    : 0;
  const ytdFacturacionRegionAnualizado = calcularYtdAnualizado(
    facturacionRegionUSD,
    monthsToShow,
    mesesTranscurridosRegion
  );
  const variacionRunRateRegion = modoCombinado
    ? calcularVariacionRatio(runRateAnualRegion, ytdFacturacionRegionAnualizado)
    : null;
  const ebitdaRunRateAnualRegion = modoCombinado
    ? Math.round(sumarUltimosNMesesSimple(ebitdaCombinadoMensual || ZEROS, monthsToShow, 3) * 4)
    : 0;
  const ytdEbitdaRegionAnualizado = calcularYtdAnualizado(
    ebitdaCombinadoMensual || ZEROS,
    monthsToShow,
    mesesTranscurridosRegion
  );
  const variacionEbitdaRunRateRegion = modoCombinado
    ? calcularVariacionPorcentual(ebitdaRunRateAnualRegion, ytdEbitdaRegionAnualizado)
    : null;

  // Valores a mostrar según modo (región o país individual) — misma precedencia que el hook
  // retirado: región > US$ > local.
  const runRateParaMostrar = modoCombinado ? runRateAnualRegion : verEnDolares ? runRateAnualUSD : runRateAnual;
  const ebitdaRunRateParaMostrar = modoCombinado
    ? ebitdaRunRateAnualRegion
    : verEnDolares
      ? ebitdaRunRateAnualUSD
      : ebitdaRunRateAnual;
  const variacionFactParaMostrar = modoCombinado ? variacionRunRateRegion : null;
  const variacionEbitdaParaMostrar = modoCombinado ? variacionEbitdaRunRateRegion : variacionVsPropioRitmo;

  // FASE E2b: EBIT/Beneficio Neto combinados pasan a leer de `filasCombinado` (fuente única) —
  // la fila ya es, por construcción, `EBITDA − Depreciación − Amortización` /
  // `EBIT − Intereses − Impuestos` sumando Chile+Perú fila por fila (`construirFilasER`),
  // nunca una resta aparte con los arrays combinados sueltos.
  const ebitCombinadoMensual = useMemo(
    () => (modoCombinado ? filasCombinado["Beneficio Operativo | EBIT"].usd : null),
    [modoCombinado, filasCombinado]
  );
  const resultadoCombinadoMensual = useMemo(
    () => (modoCombinado ? filasCombinado["Beneficio Neto"].usd : null),
    [modoCombinado, filasCombinado]
  );

  // PASO 3.6b, H4/H5: intereses y TC windowed — los slots de la ventana que caen en el año
  // anterior usan `interesesERAnoAnterior`/`dolarMensualAnoAnterior`, no los del año en curso.
  const vInteresesLocal = enVentana(interesesER.localMensual, interesesERAnoAnterior.localMensual);
  const vTC = enVentana(dolarMensual, dolarMensualAnoAnterior);
  // FASE 3 PASO 4b, punto 1c — EBIT/Beneficio Neto en LTM (pesos y US$) leen SOLO de la ventana
  // sobre `filas`/`filasAnoAnterior` (fuente única, `construirFilasER`) — nunca un cálculo top-
  // down aparte (`calcularEbitMensual`/`calcularResultadoMensual` sobre EBITDA/Depreciación/
  // Amortización/Intereses windowed por separado). Genérico para los 2 países (nada de gate por
  // `pais==="Chile"`: en Perú, `filas[row].usd` ya cae al criterio de hoy por dentro —
  // `chileLeavesUSD` es `null` ahí). Retira `ebitWindowedMensual`/`resultadoWindowedMensual`/
  // `resultadoWindowedMensualCriterio1Chile` (bug real encontrado al intentar esto antes:
  // `filasAnoAnterior` divergía del top-down — corregido en la causa, punto 1a/1b de este mismo
  // paso, así que ya no hay riesgo de heredar ese bug).
  const vFilaLocal = useCallback(
    (row) => enVentana(filas[row]?.local, filasAnoAnterior[row]?.local),
    [enVentana, filas, filasAnoAnterior]
  );
  const vFilaUSD = useCallback(
    (row) => {
      if (!verUltimos12Meses) return filas[row]?.usd ?? null;
      return windowArray(filas[row]?.usd || ZEROS, filasAnoAnterior[row]?.usd || ZEROS, monthsToShow);
    },
    [verUltimos12Meses, filas, filasAnoAnterior, monthsToShow]
  );
  const vEbitLocalTodos = vFilaLocal("Beneficio Operativo | EBIT");
  const vEbitUSDTodos = vFilaUSD("Beneficio Operativo | EBIT");
  const vBeneficioNetoLocalTodos = vFilaLocal("Beneficio Neto");
  const vBeneficioNetoUSDTodos = vFilaUSD("Beneficio Neto");
  // EBITDA no entra en el fix de H4/H5 (no depende de intereses/TC en su cálculo local) pero sí
  // necesita su propio US$ windowed para el Acumulados en LTM de abajo (H3).
  const ebitdaWindowedMensualUSD = useMemo(
    () => convertirSerieMensualAUSD(vEbitda, vTC, fbPais),
    [vEbitda, vTC, fbPais]
  );
  // FASE E2c (hallazgo): la fila "Intereses" de `EBITDAResultadoTable.js` nunca tuvo rama
  // `verUltimos12Meses` propia (a diferencia de EBIT/Beneficio Neto, que sí la tienen desde
  // 3.6b) — en LTM mostraba `filas.Intereses` (modo normal, sin ventana) aunque el TOTAL ya
  // restaba `vInteresesLocal` correctamente por dentro. Mismo patrón H5: US$ se precomputa acá
  // con `vTC` (el TC real de cada slot), nunca con `formatCurrency(valor, i)` y el TC crudo.
  const interesesWindowedMensualUSD = useMemo(
    () => convertirSerieMensualAUSD(vInteresesLocal, vTC, fbPais),
    [vInteresesLocal, vTC, fbPais]
  );

  // ─── Acumulados (checkbox del header). ───
  // PASO 3.6b, fix H1+H2+H3: antes el único camino (calendario, local, sin mirar modoCombinado
  // ni verUltimos12Meses) servía los 3 modos por igual — región mostraba el año en curso del
  // país de sesión (H1) y la celda en US$ dividía el acumulado YA sumado por el TC de un solo
  // mes en vez de sumar cada mes ya convertido (H2); con LTM prendido, Acumulados seguía
  // mostrando el calendario completo, desalineado de la ventana (H3). Mismo orden de precedencia
  // que el resto de la tabla (10.1): región > LTM > normal.
  const acumuladosCombinadoUSD = useMemo(
    () =>
      modoCombinado && ebitdaCombinadoMensual
        ? {
            ebitdaAcum: acumular(ebitdaCombinadoMensual, monthsToShow),
            ebitAcum: acumular(ebitCombinadoMensual, monthsToShow),
            resultadoAcum: acumular(resultadoCombinadoMensual, monthsToShow),
          }
        : null,
    [modoCombinado, ebitdaCombinadoMensual, ebitCombinadoMensual, resultadoCombinadoMensual, monthsToShow]
  );
  const acumuladosLTM = useMemo(
    () => ({
      local: {
        ebitdaAcum: acumular(vEbitda, monthsToShow),
        ebitAcum: acumular(vEbitLocalTodos, monthsToShow),
        resultadoAcum: acumular(vBeneficioNetoLocalTodos, monthsToShow),
      },
      usd: {
        ebitdaAcum: acumular(ebitdaWindowedMensualUSD, monthsToShow),
        ebitAcum: acumular(vEbitUSDTodos, monthsToShow),
        resultadoAcum: acumular(vBeneficioNetoUSDTodos, monthsToShow),
      },
    }),
    [
      vEbitda,
      vEbitLocalTodos,
      vBeneficioNetoLocalTodos,
      ebitdaWindowedMensualUSD,
      vEbitUSDTodos,
      vBeneficioNetoUSDTodos,
      monthsToShow,
    ]
  );
  // FASE E2a: modo normal lee SOLO de `filas` (10.1) — `filas.EBITDA.local` etc. son, término a
  // término, los mismos `ebitdaMensual`/`ebitMensual`/`resultadoMensual` de siempre (mismos
  // leaves de entrada); `.usd` ya viene de `convertirSerieMensualAUSD` (la misma función que
  // `acumularEnUSD` usaba internamente) — acumular acá es la MISMA cuenta, una fuente menos.
  const acumuladosNormal = useMemo(
    () => ({
      local: {
        ebitdaAcum: acumular(filas.EBITDA.local, monthsToShow),
        ebitAcum: acumular(filas["Beneficio Operativo | EBIT"].local, monthsToShow),
        resultadoAcum: acumular(filas["Beneficio Neto"].local, monthsToShow),
      },
      usd: {
        ebitdaAcum: acumular(filas.EBITDA.usd, monthsToShow),
        ebitAcum: acumular(filas["Beneficio Operativo | EBIT"].usd, monthsToShow),
        resultadoAcum: acumular(filas["Beneficio Neto"].usd, monthsToShow),
      },
    }),
    [filas, monthsToShow]
  );
  const acumulados = modoCombinado
    ? acumuladosCombinadoUSD
    : verUltimos12Meses
      ? verEnDolares
        ? acumuladosLTM.usd
        : acumuladosLTM.local
      : verEnDolares
        ? acumuladosNormal.usd
        : acumuladosNormal.local;
  const acumuladosYaEnUSD = modoCombinado || verEnDolares;

  // ─── Detalle de facturas por celda ──────────────────────────
  // Solo se habilita donde el valor es suma DIRECTA de facturas: año >= 2026, vista de
  // pais unico y sin ventana LTM (que mezcla meses de dos años en un mismo slot).
  // Quedan fuera a proposito: las celdas pre-2026 (carga manual), el Software DS de
  // carteleria (es un estimado, y el split A/C impide garantizar total == celda), las DOS
  // filas de Contenidos Digitales (celdas REPARTIDAS: el modal mostraria las facturas
  // enteras y no cuadraria con la celda) y las lineas OP computadas (comisiones, RRHH,
  // depreciacion).
  const detalleDisponible = useCallback(
    (i) => selectedYear >= ANO_INICIO_FACTURAS && !modoCombinado && !verUltimos12Meses && i < monthsToShow,
    [selectedYear, modoCombinado, verUltimos12Meses, monthsToShow]
  );

  const onDetalleFactura = useMemo(
    () => ({
      disponible: detalleDisponible,
      abrir: (cuenta, titulo, mesIndex) => setDetalleFacturas({ cuenta, titulo, mesIndex }),
    }),
    [detalleDisponible]
  );

  // Software DS (A) = max(0, facturas software_ds − estimado cartelería). El modal muestra
  // las facturas + la línea del estimado (restando) para que el total cierre con la celda; si
  // el resultado se clampeó a 0, se agrega la línea que lleva el subtotal negativo hasta 0.
  const onDetalleSoftwareDS = useMemo(
    () => ({
      disponible: detalleDisponible,
      abrir: (i) => {
        const facturas = costos.detalleDe("software_ds", i);
        const sumaFacturas = facturas.reduce((s, f) => s + (f.importe || 0), 0);
        const estimado = calcularCostoSoftwareEstimado(i);
        const pantallasEst = (detalleGestionContenidosPorMes[i] || []).reduce((s, it) => s + (it.pantallas || 0), 0);
        const ajustes = [{ label: `Estimado cartelería (${pantallasEst} pantallas × US$10,99)`, importe: -estimado }];
        // aSoftwareDS[i] = max(0, sumaFacturas − estimado). Cuando el max(0,…) recorta, el
        // subtotal (facturas − estimado) es negativo: esta línea lo sube hasta la celda (0).
        const clamp = aSoftwareDS[i] - (sumaFacturas - estimado);
        if (Math.abs(clamp) > 0.5) ajustes.push({ label: "Ajustado a 0 (mínimo)", importe: clamp });
        setDetalleFacturas({ cuenta: "software_ds", titulo: "Software DS", mesIndex: i, ajustes });
      },
    }),
    [detalleDisponible, costos, calcularCostoSoftwareEstimado, detalleGestionContenidosPorMes, aSoftwareDS]
  );

  // Contenidos Digitales (A) = facturas contenidos_digitales × (pantallasPublicidad / total).
  // El modal muestra las facturas + la parte cartelería (restando) para llegar a la celda.
  const onDetalleContenidos = useMemo(
    () => ({
      disponible: detalleDisponible,
      abrir: (i) => {
        const denom = pantallasPublicidad + pantallasCarteleria;
        const pctCart = denom > 0 ? (pantallasCarteleria / denom) * 100 : 0;
        const ajustes = [
          {
            label: `Parte cartelería (${pctCart.toFixed(1)}% por pantallas)`,
            importe: -(cContenidosDigitales[i] || 0),
          },
        ];
        setDetalleFacturas({ cuenta: "contenidos_digitales", titulo: "Contenidos Digitales", mesIndex: i, ajustes });
      },
    }),
    [detalleDisponible, pantallasPublicidad, pantallasCarteleria, cContenidosDigitales]
  );

  // Contenidos Digitales (C) = facturas contenidos_digitales × (pantallasCarteleria / total).
  // Espejo de la pata A: el modal muestra las facturas + la parte PUBLICIDAD (restando) para
  // llegar a la celda de cartelería. Total = T − aContenidos[i] = cContenidosDigitales[i].
  const onDetalleContenidosC = useMemo(
    () => ({
      disponible: detalleDisponible,
      abrir: (i) => {
        const denom = pantallasPublicidad + pantallasCarteleria;
        const pctPub = denom > 0 ? (pantallasPublicidad / denom) * 100 : 0;
        const ajustes = [
          {
            label: `Parte publicidad (${pctPub.toFixed(1)}% por pantallas)`,
            importe: -(aContenidosDigitales[i] || 0),
          },
        ];
        setDetalleFacturas({ cuenta: "contenidos_digitales", titulo: "Contenidos Digitales", mesIndex: i, ajustes });
      },
    }),
    [detalleDisponible, pantallasPublicidad, pantallasCarteleria, aContenidosDigitales]
  );

  // Nombre a mostrar de un negocio de cartelería (réplica del helper del v1, ER/index.js:2079).
  const getNombreNegocio = useCallback(
    (n) => {
      if (n.tipo_vertical === "TD") return clientes[n.referencia_id]?.nombre || `Cliente ${n.referencia_id}`;
      if (n.tipo_vertical === "OT")
        return clientes[n.referencia_id]?.nombre || edificios[n.referencia_id]?.edificio || `Ref ${n.referencia_id}`;
      return edificios[n.referencia_id]?.edificio || `Edificio ${n.referencia_id}`;
    },
    [clientes, edificios]
  );

  // PASO 6 (2026-10-07) — fuente ÚNICA de "B - Hardware" (ingreso): los MISMOS registros que
  // arman la celda (`registrosHardware`/`registrosIngresoSII` bloque B) — para Chile, los de
  // `chileVentaLeavesSesion`; para Perú (no migra su celda) la misma función país-agnóstica.
  // Antes el modal armaba su PROPIA lista vía `resolverNegociosHardwareDelMes` (proyección sobre
  // `negocios_carteleria` cruda, documentada en `estadoResultadosER.js:380-388` como NO
  // equivalente a `registrosHardware`) y recalculaba cada total con `convertirAMonedaLocal` por su
  // cuenta — divergía de la celda en Chile 2026+ (confirmado con datos reales, feb-2026: celda
  // $1.830.343 vs. suma de filas vieja $1.854.093; ver doc de cierre). `resolverNegociosHardwareDelMes`
  // sigue viva en `estadoResultadosER.js` (con sus propios tests, P2.115) — no se retira, solo deja
  // de ser la fuente de ESTE modal.
  const hardwareRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["B - Hardware"] || [];
    return registrosHardware({
      negocios: negociosCarteleria,
      pais,
      ano: selectedYear,
      getUFValue,
      getDolarValue,
      costosGestion: costosGestionData,
    });
  }, [pais, chileVentaLeavesSesion, negociosCarteleria, selectedYear, getUFValue, getDolarValue, costosGestionData]);

  const hardwareRegistrosPorMes = useMemo(() => {
    const resultado = Array.from({ length: 12 }, () => []);
    hardwareRegistrosModal.forEach((r) => {
      if (r.ano === selectedYear && r.mesIndice >= 0 && r.mesIndice < 12) resultado[r.mesIndice].push(r);
    });
    return resultado;
  }, [hardwareRegistrosModal, selectedYear]);

  // Resuelve nombre/pantallas/modalidad/monto-original/fecha de un registro de hardware a partir
  // del negocio de origen (y, en el camino SII, del tramo real por posición — `ventaRefIndex`
  // indexa `detalle_pagos_hardware` para `negocios_carteleria_hw`, o `meses_facturacion` para
  // OPEX bajo `negocios_carteleria`, mismo índice que resuelve `getLineaIdPorPosicion`, 10.1).
  const resolverFilaHardware = useCallback(
    (registro) => {
      const { meta } = registro;
      const negocioId = meta.facturaId == null ? meta.id : meta.ventaRefId;
      // `venta_ref_id` (vinculaciones, camino SII) llega numérico; `negocio.id` puede llegar como
      // string (según el loader) — comparar por `String(...)`, nunca por ID numérico crudo (10.7
      // exige comparar por ID, no dice que ambos lados tengan el mismo tipo primitivo).
      const negocio = (negociosCarteleria || []).find((n) => String(n.id) === String(negocioId));
      if (!negocio) return null;
      const pantallas = Number(negocio.cantidad_pantallas) || 1;
      const monedaDefault = pais === "Chile" ? "UF" : "PEN";
      let montoOriginal = meta.montoOriginal ?? null;
      let fecha = registro.fecha;
      if (meta.facturaId != null) {
        if (meta.origen === "negocios_carteleria_hw") {
          const tramo = (negocio.detalle_pagos_hardware || [])[meta.ventaRefIndex];
          montoOriginal = tramo ? Number(tramo.monto) : null;
          fecha = tramo ? tramo.facturafecha || tramo.fecha_factura || fecha : fecha;
        } else {
          montoOriginal = (Number(negocio.cuota_hardware_opex) || 0) * pantallas;
          const entrada = (negocio.meses_facturacion || [])[meta.ventaRefIndex];
          fecha = entrada?.facturafecha || fecha;
        }
      }
      return {
        id: negocio.id,
        nombre: getNombreNegocio(negocio),
        pantallas,
        modalidad: negocio.modalidad,
        moneda: meta.moneda || (negocio.moneda_hardware || monedaDefault).toUpperCase(),
        montoOriginal,
        fecha,
      };
    },
    [negociosCarteleria, getNombreNegocio, pais]
  );

  const hardwareDelMes = useMemo(() => {
    const i = detalleHardwareIngresos?.monthIndex;
    if (i == null) return [];
    const porNegocio = new Map();
    (hardwareRegistrosPorMes[i] || []).forEach((r) => {
      const base = resolverFilaHardware(r);
      if (!base) return;
      if (!porNegocio.has(base.id)) porNegocio.set(base.id, { ...base, totalLocal: 0, montoOriginal: 0 });
      const acc = porNegocio.get(base.id);
      acc.totalLocal += r.montoLocal;
      acc.montoOriginal += base.montoOriginal || 0;
      if (!acc.fecha) acc.fecha = base.fecha;
    });
    return [...porNegocio.values()];
  }, [detalleHardwareIngresos, hardwareRegistrosPorMes, resolverFilaHardware]);

  const abrirHardwareIngresos = useCallback((monthIndex) => setDetalleHardwareIngresos({ monthIndex }), []);

  // PASO 6 (2026-10-07) — resuelve los campos de display (cliente/factura/importe) de un
  // registro de ingreso del Bloque A. SOLO lookup (clientes/oportunidades/contratos ya
  // cargados) — ningún cálculo de monto: el `montoLocal` del registro ya es el correcto, se usa
  // tal cual. `registrosVentas`/`registrosContratos` (2025 y Perú) ya traen estos campos
  // enriquecidos en su propia `meta` (ingresosER.js) — acá solo se completa el único caso que NO
  // puede saberlos por sí solo: `registrosIngresoSII` (Chile 2026+, viene de la factura
  // vinculada, no de la venta — `meta.folio` lo distingue) necesita un lookup contra la
  // venta/contrato referenciada para el nombre del cliente.
  const resolverFilaIngresoA = useCallback(
    (registro) => {
      const { meta, montoLocal } = registro;
      if (meta.folio == null) {
        // Ya enriquecido por registrosVentas/registrosContratos — sin lookup.
        return {
          facturanumero: meta.facturanumero,
          cliente: meta.cliente,
          importe: meta.importe,
          importeOriginalMoneda: meta.importeOriginalMoneda,
          moneda_dolar: !!meta.moneda_dolar,
        };
      }
      // `registrosIngresoSII`: el monto YA es neto local (PARTE 1 de PASO 5 — `monto_aplicado`
      // nunca es la moneda nativa ni en exportación), así que no hay "importe original en
      // dólares" que mostrar — el folio es el REAL de la factura SII, mejor que cualquier
      // `facturanumero` de la venta.
      if (meta.origen === "sales") {
        // `venta_ref_id` (vinculaciones) llega numérico; `oportunidades[].id` puede llegar como
        // string (mismo patrón ya resuelto en `ordenesMedio.js:192` para esta misma tabla) —
        // comparar por `String(...)`, nunca por `===` crudo entre tipos distintos.
        const opp = (oportunidades || []).find((o) => String(o.id) === String(meta.ventaRefId));
        return {
          facturanumero: meta.folio,
          cliente: opp?.cliente,
          importe: montoLocal,
          importeOriginalMoneda: montoLocal,
          moneda_dolar: false,
        };
      }
      if (meta.origen === "sales_contratos") {
        const c = (contratosSesion || []).find((x) => String(x.id) === String(meta.ventaRefId));
        return {
          facturanumero: meta.folio,
          cliente: c?.cliente,
          importe: montoLocal,
          importeOriginalMoneda: montoLocal,
          moneda_dolar: false,
        };
      }
      return {
        facturanumero: meta.folio,
        cliente: null,
        importe: montoLocal,
        importeOriginalMoneda: montoLocal,
        moneda_dolar: false,
      };
    },
    [oportunidades, contratosSesion]
  );

  // ─── Drill de facturación en la fila "A - Publicidad" (Chile y Perú) ───────
  // PASO 6 (2026-10-07) — fuente ÚNICA: para Chile, los MISMOS registros que arman la celda
  // (`chileVentaLeavesSesion.registros`, Criterio SII 2026+/ventas antes, por año — 10.1). Perú
  // no migra su celda (sigue en `usePublicidadCalcs`/`useContratosCalcs`) pero el modal pasa a
  // leer `registrosIngresoA` (país-agnóstica) con los MISMOS argumentos — mismo núcleo
  // (`getConvertedImporte`/`getRateForOpp`), no puede divergir. Antes de este paso, el modal leía
  // `facturasPorMes`/`facturasPorMesContratos` (hooks viejos) — ventas sin vincular en Chile
  // 2026+ aparecían en el modal pero no en la celda SII, y viceversa con el mes.
  // No exige año >= 2026: la facturación de ventas existe para todos los años (Chile <2026 y
  // Perú usan la regla de siempre, ventas-based, sin SII).
  const facturacionRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["A - Publicidad"] || [];
    return registrosIngresoA({
      oportunidades,
      contratos: contratosSesion,
      pais,
      ano: selectedYear,
      getDolarValue,
      dolarFallbackPais,
    });
  }, [pais, chileVentaLeavesSesion, oportunidades, contratosSesion, selectedYear, getDolarValue, dolarFallbackPais]);

  const facturacionDisponible = useCallback(
    (i) =>
      !modoCombinado &&
      !verUltimos12Meses &&
      i < monthsToShow &&
      facturacionRegistrosModal.some((r) => r.mesIndice === i && r.ano === selectedYear),
    [modoCombinado, verUltimos12Meses, monthsToShow, facturacionRegistrosModal, selectedYear]
  );

  const onDetalleFacturacion = useMemo(
    () => ({
      disponible: facturacionDisponible,
      abrir: (mesIndex) => setDetalleFacturacion({ mesIndex }),
    }),
    [facturacionDisponible]
  );

  // Facturas (pauta + contratos) del mes clickeado — Σ importe == valor de la celda por
  // construcción (mismos registros, agregados dos veces).
  const facturacionDelMes = useMemo(() => {
    if (!detalleFacturacion) return [];
    const i = detalleFacturacion.mesIndex;
    return facturacionRegistrosModal
      .filter((r) => r.mesIndice === i && r.ano === selectedYear)
      .map((r) => resolverFilaIngresoA(r));
  }, [detalleFacturacion, facturacionRegistrosModal, selectedYear, resolverFilaIngresoA]);

  // ─── Drills de las otras filas del Bloque A (mismo patrón que facturación) ───
  // Base común: país único, sin LTM, mes ya transcurrido. Son datos comerciales
  // (oportunidades/contratos), así que NO aplica el corte 2026 del detalle de costos SII.
  const puedeDetalleMes = useCallback(
    (i) => !modoCombinado && !verUltimos12Meses && i < monthsToShow,
    [modoCombinado, verUltimos12Meses, monthsToShow]
  );

  // PASO 6 (2026-10-07) — fuente ÚNICA de "Costo intermediación": los MISMOS registros que
  // arman la celda (`registrosCostoIntermediacion` = agencia+broker+take+apx, ventas+contratos,
  // ya tagueados por `concepto`) — para Chile, los de `chileVentaLeavesSesion` (gateados a
  // venta vinculada en 2026+); para Perú (no migra su celda) la misma función país-agnóstica.
  // Antes el modal leía 7 listas de los hooks viejos (comisionesAgenciaPorMes/...), separadas de
  // lo que sumaba la celda en Chile 2026+.
  const intermediacionRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["A - Publicidad / Costo intermediación"] || [];
    const pc = {
      oportunidades,
      contratos: contratosSesion,
      pais,
      ano: selectedYear,
      getDolarValue,
      dolarFallbackPais,
      clientes,
      intermediarios,
      costosMap,
      pctMap,
    };
    return registrosCostoIntermediacion(pc);
  }, [
    pais,
    chileVentaLeavesSesion,
    oportunidades,
    contratosSesion,
    selectedYear,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    costosMap,
    pctMap,
  ]);

  const filaIntermediacion = (registro, valorKey) => ({ ...registro.meta, [valorKey]: registro.montoLocal });

  const intermediacionDelMes = useMemo(() => {
    const i = detalleIntermediacion?.mesIndex;
    if (i == null) return null;
    const delMes = intermediacionRegistrosModal.filter((r) => r.mesIndice === i && r.ano === selectedYear);
    return {
      comisiones: delMes.filter((r) => r.concepto === "comision_agencia").map((r) => filaIntermediacion(r, "comision")),
      comisionesBroker: delMes
        .filter((r) => r.concepto === "comision_broker")
        .map((r) => filaIntermediacion(r, "comisionBroker")),
      costosProgramaticos: delMes.filter((r) => r.concepto === "take").map((r) => filaIntermediacion(r, "takeLocal")),
      apx: delMes.filter((r) => r.concepto === "apx").map((r) => filaIntermediacion(r, "apxLocal")),
    };
  }, [detalleIntermediacion, intermediacionRegistrosModal, selectedYear]);

  const onDetalleIntermediacion = useMemo(() => {
    const hayDatos = (i) => intermediacionRegistrosModal.some((r) => r.mesIndice === i && r.ano === selectedYear);
    return {
      disponible: (i) => puedeDetalleMes(i) && hayDatos(i),
      abrir: (mesIndex) => setDetalleIntermediacion({ mesIndex }),
    };
  }, [puedeDetalleMes, intermediacionRegistrosModal, selectedYear]);

  // PASO 6 (2026-10-07) — fuente ÚNICA de "Comisión edificios" (A): los MISMOS registros que
  // arman la celda (`registrosComisionEdificiosA` = variable ventas+contratos + fija por
  // edificio, ya tagueados por `meta.origen`) — para Chile, los de `chileVentaLeavesSesion`; para
  // Perú (no migra su celda) la misma función país-agnóstica. Antes el modal combinaba 3 fuentes
  // independientes (`comisionesEdificiosPorMes`/`comisionesEdificiosPorMesContratos` de los hooks
  // viejos + `comisionesFijasPorMes` recalculado acá) sin pasar por el gate de vinculación SII
  // que la celda ya usa en Chile 2026+.
  const comisionEdificiosRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["A - Publicidad / Comisión edificios"] || [];
    const pc = {
      oportunidades,
      contratos: contratosSesion,
      pais,
      ano: selectedYear,
      getDolarValue,
      dolarFallbackPais,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      costosMap,
      pctMap,
    };
    return registrosComisionEdificiosA(pc);
  }, [
    pais,
    chileVentaLeavesSesion,
    oportunidades,
    contratosSesion,
    selectedYear,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    costosMap,
    pctMap,
  ]);

  const comisionEdificiosDelMes = useMemo(() => {
    const i = detalleComisionEdificios?.mesIndex;
    if (i == null) return null;
    const delMes = comisionEdificiosRegistrosModal.filter((r) => r.mesIndice === i && r.ano === selectedYear);
    return {
      comisiones: delMes.filter((r) => r.meta.origen !== "edificios_comision_fija").map((r) => ({ ...r.meta })),
      comisionesFijas: delMes.filter((r) => r.meta.origen === "edificios_comision_fija").map((r) => ({ ...r.meta })),
    };
  }, [detalleComisionEdificios, comisionEdificiosRegistrosModal, selectedYear]);

  const onDetalleComisionEdificios = useMemo(() => {
    const hayDatos = (i) => comisionEdificiosRegistrosModal.some((r) => r.mesIndice === i && r.ano === selectedYear);
    return {
      disponible: (i) => puedeDetalleMes(i) && hayDatos(i),
      abrir: (mesIndex) => setDetalleComisionEdificios({ mesIndex }),
    };
  }, [puedeDetalleMes, comisionEdificiosRegistrosModal, selectedYear]);

  // PASO 6 (2026-10-07) — fuente ÚNICA de "C - Gestión & Contenidos" (ingreso): los MISMOS
  // registros que arman la celda (`registrosGestion`/`registrosIngresoSII` bloque C) — para
  // Chile, los de `chileVentaLeavesSesion`; para Perú (no migra su celda) la misma función
  // país-agnóstica. Antes el modal (y el gate de la celda) leían `detalleGestionContenidosPorMes`
  // (hook `useGestionCalcs`, proyección sobre `negocios_carteleria` cruda) — esa variable SIGUE
  // viva sin cambios para su otro consumidor (el modal de "Software DS" en Bloque A, línea de
  // `pantallasEst`, fuera de alcance de este paso); acá se introduce una fuente NUEVA y separada,
  // solo para el gate+contenido de ESTE modal.
  const gestionRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["C - Gestión & Contenidos"] || [];
    return registrosGestion({
      negocios: negociosCarteleria,
      pais,
      ano: selectedYear,
      convertirAMonedaLocal,
      dolarMensualPeru,
      dolarFallback,
    });
  }, [
    pais,
    chileVentaLeavesSesion,
    negociosCarteleria,
    selectedYear,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
  ]);

  const gestionRegistrosPorMes = useMemo(() => {
    const resultado = Array.from({ length: 12 }, () => []);
    gestionRegistrosModal.forEach((r) => {
      if (r.ano === selectedYear && r.mesIndice >= 0 && r.mesIndice < 12) resultado[r.mesIndice].push(r);
    });
    return resultado;
  }, [gestionRegistrosModal, selectedYear]);

  // Resuelve nombre/tipo/pantallas de un registro de gestión a partir del negocio de origen —
  // mismo lookup que `getNombreNegocio` (10.1), con el fallback de 3 verticales que ya usaba
  // `detalleGestionContenidosPorMes` (`useGestionCalcs.js:73-78`: TD→cliente, EO→edificio, si no
  // "ID <referencia_id>"). El id del negocio sale de `meta.id` (camino viejo/Perú) o
  // `meta.ventaRefId` (camino SII, Chile 2026+, `venta_tipo='negocios_carteleria'`).
  const resolverFilaGestion = useCallback(
    (registro) => {
      const negocioId = registro.meta.facturaId == null ? registro.meta.id : registro.meta.ventaRefId;
      // Mismo cuidado que `resolverFilaHardware`: `venta_ref_id` (camino SII) llega numérico,
      // `negocio.id` puede llegar como string — comparar por `String(...)`.
      const negocio = (negociosCarteleria || []).find((n) => String(n.id) === String(negocioId));
      if (!negocio) return { id: negocioId, nombre: `ID ${negocioId}`, tipo: null, pantallas: 1 };
      let nombre = `ID ${negocio.referencia_id}`;
      if (negocio.tipo_vertical === "TD" && clientes[negocio.referencia_id]) {
        nombre = clientes[negocio.referencia_id].nombre;
      } else if (negocio.tipo_vertical === "EO" && edificios[negocio.referencia_id]) {
        nombre = edificios[negocio.referencia_id]?.edificio;
      }
      return {
        id: negocio.id,
        nombre,
        tipo: negocio.tipo_vertical,
        pantallas: parseInt(negocio.cantidad_pantallas) || 1,
      };
    },
    [negociosCarteleria, clientes, edificios]
  );

  const gestionDelMes = useMemo(() => {
    const i = detalleGestion?.mesIndex;
    if (i == null) return [];
    const porNegocio = new Map();
    (gestionRegistrosPorMes[i] || []).forEach((r) => {
      const base = resolverFilaGestion(r);
      if (!porNegocio.has(base.id)) porNegocio.set(base.id, { ...base, totalLocal: 0 });
      porNegocio.get(base.id).totalLocal += r.montoLocal;
    });
    return [...porNegocio.values()];
  }, [detalleGestion, gestionRegistrosPorMes, resolverFilaGestion]);

  // PASO 6 (reingeniería del EBITDA, 2026-10-07) — fuente ÚNICA del modal "Comisión comercial":
  // para Chile, los MISMOS registros que ya arman la celda (`chileVentaLeavesSesion.registros`,
  // ya gateados a venta vinculada para 2026+, 10.1 — nunca una lista aparte). Perú NO migra su
  // celda (sigue en `usePublicidadCalcs`/`useContratosCalcs`, sin tocar) pero el modal SÍ pasa a
  // leer las mismas 2 funciones de registros (`registrosComisionComercialVentas`/`Contratos`,
  // país-agnósticas) con los MISMOS argumentos que ya usa el hook para su total (`pctMap`
  // incluido) — matemáticamente no puede divergir del total del hook (mismo núcleo
  // `desgloseComisionComercial`, mismos inputs), verificado con assert duro (punto 3a).
  const comisionComercialRegistrosModal = useMemo(() => {
    if (pais === "Chile") return chileVentaLeavesSesion?.registros?.["A - Publicidad / Comisión comercial"] || [];
    const pc = {
      oportunidades,
      contratos: contratosSesion,
      pais,
      ano: selectedYear,
      getDolarValue,
      dolarFallbackPais,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      costosMap,
      pctMap,
    };
    return [...registrosComisionComercialVentas(pc), ...registrosComisionComercialContratos(pc)];
  }, [
    pais,
    chileVentaLeavesSesion,
    oportunidades,
    contratosSesion,
    selectedYear,
    getDolarValue,
    dolarFallbackPais,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    costosMap,
    pctMap,
  ]);

  const comisionComercialDelMes = useMemo(() => {
    const i = detalleComisionComercial?.mesIndex;
    if (i == null) return [];
    return comisionComercialRegistrosModal
      .filter((r) => r.mesIndice === i && r.ano === selectedYear)
      .map((r) => ({ id: r.meta.id, ...r.meta, totalComision: r.montoLocal }));
  }, [detalleComisionComercial, comisionComercialRegistrosModal, selectedYear]);

  // Comisión comercial: el gate lee de los MISMOS registros que alimentan el modal (arriba) — ya
  // no reimplementa el filtro de estatus/mes/año por su cuenta.
  const onDetalleComisionComercial = useMemo(() => {
    const hayRegistros = (i) =>
      comisionComercialRegistrosModal.some((r) => r.mesIndice === i && r.ano === selectedYear);
    return {
      disponible: (i) => puedeDetalleMes(i) && hayRegistros(i),
      abrir: (mesIndex) => setDetalleComisionComercial({ mesIndex }),
    };
  }, [puedeDetalleMes, comisionComercialRegistrosModal, selectedYear]);

  const ytdFacturacion = ytd(vFacturacion);
  const ytdPublicidad = ytd(vPublicidad);

  // "vs YTD" y "vs AA": mismas fórmulas que el v1 (ER/index.js:3008 y :3042).
  const facturacionAnoAnteriorTotal = useMemo(
    () =>
      facturacionAAmensual.reduce((a, b) => a + b, 0) +
      ingresosHardwareMensualAnoAnterior.reduce((a, b) => a + b, 0) +
      ingresosGestionContenidosMensualAnoAnterior.reduce((a, b) => a + b, 0) +
      ingresosBTLMensualAnoAnterior.reduce((a, b) => a + b, 0),
    [
      facturacionAAmensual,
      ingresosHardwareMensualAnoAnterior,
      ingresosGestionContenidosMensualAnoAnterior,
      ingresosBTLMensualAnoAnterior,
    ]
  );

  const ytdAnoAnterior = useMemo(() => {
    if (monthsToShow === 12) return facturacionAnoAnteriorTotal;
    const corte = (arr) => arr.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
    return (
      corte(facturacionAAmensual) +
      corte(ingresosHardwareMensualAnoAnterior) +
      corte(ingresosGestionContenidosMensualAnoAnterior) +
      corte(ingresosBTLMensualAnoAnterior)
    );
  }, [
    monthsToShow,
    facturacionAnoAnteriorTotal,
    facturacionAAmensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensualAnoAnterior,
  ]);

  // Variantes de región (modo combinado) de las dos YoY. El v1 las deriva de
  // `historiaER` (su caché en localStorage) y de constantes historicas de 2024, que el
  // v2 no tiene. Se dejan en null a proposito: el ERHeader muestra "—" cuando lo son,
  // igual que el v1 cuando no puede calcularlas. Portarlas requiere primero decidir de
  // donde sale la facturacion historica de region, y va en su propia pasada.
  const varYTDvsYTDRegion = null;
  const varYTDvsFullRegion = null;

  const varYTDvsYTD = ytdAnoAnterior > 0 ? Number(((ytdFacturacion / ytdAnoAnterior - 1) * 100).toFixed(1)) : null;
  const varYTDvsFull =
    facturacionAnoAnteriorTotal > 0
      ? Number(((ytdFacturacion / facturacionAnoAnteriorTotal - 1) * 100).toFixed(1))
      : null;

  // Base de las sub-filas del bloque A: el YTD de Publicidad, no el de Facturación
  // (v1: ER/index.js:3620, :3660, :3761, :3780).
  const pctSobrePublicidad = (valor) => (ytdPublicidad > 0 ? formatPercentage((valor / ytdPublicidad) * 100) : "0%");

  const margenPorcentaje = (margen, ingresos) => {
    if (!ingresos || ingresos === 0) return "0%";
    return formatPercentage((margen / ingresos) * 100);
  };

  // CostosFijosTable lee `costosFijosData` / `rawCostosFijos` (las tablas manuales del
  // v1). Se le sintetizan esas filas desde el motor único, con TODOS los campos
  // presentes: así `registroExiste` es siempre true y el componente nunca proyecta
  // (v2 no proyecta) ni pinta la celda de amarillo.
  // Filas sinteticas que consume CostosFijosTable. Se arman desde costosFijosPorConcepto,
  // que YA aplica el switch por año (facturas desde 2026, tablas manuales antes), asi que
  // el componente no vuelve a decidir la fuente. Todos los campos presentes => nunca
  // proyecta por su cuenta: la proyeccion pre-2026 ya viene resuelta en la serie.
  const costosFijosFilas = useMemo(
    () =>
      ZEROS.map((_, i) => {
        const fila = { mes: i + 1 };
        camposCostosFijos.forEach((campo, idx) => {
          fila[campo] = costosFijosPorConcepto[idx]?.[i] || 0;
        });
        return fila;
      }),
    [costosFijosPorConcepto]
  );

  const noop = useCallback(() => {}, []);

  // Fila genérica: 12 celdas + YTD. Los meses futuros muestran "-" como en el v1.
  // Fila genérica. La celda YTD usa `totalCell` (sin estilo propio → fuente normal),
  // igual que el v1; sólo FACTURACIÓN lleva `totalCellYTD`, que es la de 1.3rem. Pasar
  // por la clase equivocada era lo que agrandaba el alto de esas filas.
  const Row = ({
    label,
    values = ZEROS,
    className,
    conceptClassName,
    conceptStyle,
    onToggle,
    caret,
    porcentaje = null,
    totalGrande = false,
    yaEnUSD = false,
    cuentaDetalle = null,
    onDetalle = null,
  }) => (
    <tr
      className={className}
      onClick={onToggle}
      style={onToggle ? { cursor: "pointer", userSelect: "none" } : undefined}
    >
      <td className={conceptClassName || styles.conceptCell} style={conceptStyle}>
        {label}
        {caret ? ` ${caret}` : ""}
      </td>
      {ZEROS.map((_, i) => {
        const clickableCosto = Boolean(cuentaDetalle) && detalleDisponible(i);
        const clickableDetalle = Boolean(onDetalle) && onDetalle.disponible(i);
        // Fila con drill de detalle comercial (A - Publicidad y las 3 líneas del Bloque A):
        // el click SIEMPRE frena la propagación (para no togglear el collapse, igual que el
        // v1), y abre el modal sólo cuando hay datos ese mes.
        let onCellClick;
        if (onDetalle) {
          onCellClick = (e) => {
            e.stopPropagation();
            if (clickableDetalle) onDetalle.abrir(i);
          };
        } else if (clickableCosto) {
          onCellClick = (e) => {
            e.stopPropagation();
            onDetalleFactura.abrir(cuentaDetalle, label, i);
          };
        }
        const cursorPointer = clickableCosto || clickableDetalle;
        return (
          <td
            key={i}
            className={styles.valueCell}
            style={cursorPointer ? { cursor: "pointer" } : undefined}
            onClick={onCellClick}
          >
            {i < mesesVisibles ? formatCurrency(values[i] || 0, i, null, yaEnUSD) : "-"}
          </td>
        );
      })}
      <td className={totalGrande ? styles.totalCellYTD : styles.totalCell}>
        {formatCurrency(ytd(values), null, yaEnUSD ? null : values, yaEnUSD)}
        {porcentaje !== null ? ` | ${porcentaje}` : ""}
      </td>
    </tr>
  );

  // FASE 3 PASO 3 (criterio 1), punto 4 — helper de las filas de A-D: Chile + "Ver en US$" usa
  // el bottom-up ya construido (`vXxxUSD`); Perú y el modo peso siguen con el camino de
  // siempre, sin cambio (10.265/10.267). `yaEnUSDChile` es la MISMA condición para las dos
  // partes de cada `<Row>` (values + yaEnUSD) — nunca se evalúan por separado.
  const yaEnUSDChile = pais === "Chile" && verEnDolares;
  const vUSD = (valorChileUSD, valorPeso) => (yaEnUSDChile ? valorChileUSD : valorPeso);

  // Gate interno: MISMA fuente que la ruta (alsoAllow en App.js) → no pueden divergir.
  if (!puedeVerERv2({ rol, id: userId, pais })) return null;

  // No mostrar la tabla ni el header con datos hasta que TODAS las fuentes async terminen:
  // useERv2Data (isLoading), RRHH (isEmpleadosRRHHLoading), factoring (isFactoringLoading) y
  // tipos de cambio (exchangeRatesLoaded). Sin esto la tabla se renderiza en $0/a medio
  // calcular y después "salta" a los números reales.
  const isDataLoading = isLoading || isEmpleadosRRHHLoading || isFactoringLoading || !exchangeRatesLoaded;
  if (isDataLoading) return <Loader fullPage />;

  return (
    <div className={styles.ERWrapper}>
      <ERHeader
        logo={pais === "Chile" ? wecastLogo : visionaLogo}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        verEnDolares={verEnDolares}
        setVerEnDolares={setVerEnDolares}
        verChileYPeru={verChileYPeru}
        setVerChileYPeru={setVerChileYPeru}
        modoCombinado={modoCombinado}
        pais={pais}
        moneda={moneda}
        isOportunidadesLoading={isLoading}
        varYTDvsYTD={varYTDvsYTD}
        varYTDvsFull={varYTDvsFull}
        runRateParaMostrar={runRateParaMostrar}
        runRateAnual={runRateAnual}
        facturacionUltimos12Meses={facturacionUltimos12Meses}
        variacionFactParaMostrar={variacionFactParaMostrar}
        ebitdaRunRateParaMostrar={ebitdaRunRateParaMostrar}
        variacionEbitdaParaMostrar={variacionEbitdaParaMostrar}
        varYTDvsYTDRegion={varYTDvsYTDRegion}
        varYTDvsFullRegion={varYTDvsFullRegion}
        es2026Habilitado={true}
        verUltimos12Meses={verUltimos12Meses}
        setVerUltimos12Meses={setVerUltimos12Meses}
        monthsToShow={monthsToShow}
        onOpenChart={() => {}}
        verAcumulados={verAcumulados}
        setVerAcumulados={setVerAcumulados}
        verUnitEconomics={verUnitEconomics}
        setVerUnitEconomics={setVerUnitEconomics}
        unitEconomicsReady={false}
        verInversionActivos={verInversionActivos}
        setVerInversionActivos={setVerInversionActivos}
      />
      {/* Aviso informativo permanente (hasta que Ignacio pida sacarlo). Sin condición por
          rol/id: lo ven todos los que entran al ER v2. Sin botón de cerrar. */}
      <div className={styles.avisoProcesoAutomatizado}>
        <strong>Estado de Resultados v2</strong> | Proceso automatizado: la información se calcula a partir de los
        documentos electrónicos obtenidos directamente del Servicio de Impuestos Internos y de proveedores del exterior,
        sin intervención manual. Aún en fase final de afinamiento técnico.
      </div>
      {seriesTCRespaldo.length > 0 && (
        <div className={styles.avisoTCRespaldo}>
          <strong>Tipos de cambio de respaldo</strong> — no se pudo obtener el tipo de cambio online
          (wecast.cl/visiona.pe) para{" "}
          {seriesTCRespaldo
            .map(({ serie, hasta }) => {
              const nombre = { uf: "UF", dolarChile: "dólar Chile", dolarPeru: "dólar Perú" }[serie] || serie;
              return `${nombre} (datos hasta ${hasta || "sin dato"})`;
            })
            .join(", ")}
          . Los montos en esta pantalla pueden no reflejar el tipo de cambio real de hoy.
        </div>
      )}
      <div className={styles.ER}>
        <table className={styles.tableER}>
          <thead>
            <tr>
              <th className={styles.conceptCell}>Concepto</th>
              {mesesWindowed.map((m, i) => (
                <th key={i} className={styles.valueCell}>
                  {m}
                </th>
              ))}
              <th className={styles.totalCellHead}>{verUltimos12Meses ? "LTM" : "YTD"}</th>
            </tr>
          </thead>
          <tbody>
            <Row
              label="FACTURACIÓN"
              values={modoCombinado ? vFacturacion : vUSD(vFacturacionUSD, vFacturacion)}
              className={styles.facturacionRow}
              totalGrande={true}
              yaEnUSD={modoCombinado || yaEnUSDChile}
            />

            {/* === BLOQUE A — Publicidad === */}
            <Row
              label="A - Publicidad"
              values={modoCombinado ? vPublicidad : vUSD(vPublicidadUSD, vPublicidad)}
              yaEnUSD={modoCombinado || yaEnUSDChile}
              porcentaje={porcentajeSobreFacturacion(ytdPublicidad)}
              className={styles.sectionHeader}
              caret={bloqueACollapsed ? ">" : ""}
              onToggle={() => setBloqueACollapsed(!bloqueACollapsed)}
              onDetalle={onDetalleFacturacion}
            />
            {!bloqueACollapsed && (
              <>
                <Row
                  label="Costo intermediación"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Costo intermediación"].usd
                      : vUSD(vIntermediacionUSD, vIntermediacion)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  porcentaje={pctSobrePublicidad(ytd(costoIntermediacion))}
                  onDetalle={modoCombinado ? null : onDetalleIntermediacion}
                />
                <Row
                  label="Comisión edificios"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Comisión edificios"].usd
                      : vUSD(vComEdificiosUSD, vComEdificios)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  porcentaje={pctSobrePublicidad(ytd(comisionEdificios))}
                  onDetalle={modoCombinado ? null : onDetalleComisionEdificios}
                />
                <Row
                  label="Comisión comercial"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Comisión comercial"].usd
                      : vUSD(vComComercialUSD, vComComercial)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  porcentaje={pctSobrePublicidad(ytd(comisionComercial))}
                  onDetalle={modoCombinado ? null : onDetalleComisionComercial}
                />
                <Row
                  label="Servicios internet"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Servicios internet"].usd
                      : vUSD(vServiciosInternetUSD, vServiciosInternet)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  cuentaDetalle={modoCombinado ? null : "servicios_internet"}
                  porcentaje={pctSobrePublicidad(ytd(aServiciosInternet))}
                />
                <Row
                  label="Software DS"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Software DS"].usd
                      : vUSD(vSoftwareDSAUSD, vSoftwareDS)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  porcentaje={pctSobrePublicidad(ytd(aSoftwareDS))}
                  onDetalle={modoCombinado ? null : onDetalleSoftwareDS}
                />
                <Row
                  label="Mantenimiento"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Mantenimiento"].usd
                      : vUSD(vMantenimientoUSD, vMantenimiento)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  cuentaDetalle={modoCombinado ? null : "mantenimiento"}
                  porcentaje={pctSobrePublicidad(ytd(aMantenimiento))}
                />
                <Row
                  label="Contenidos Digitales"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Contenidos Digitales"].usd
                      : vUSD(vContenidosAUSD, vContenidosA)
                  }
                  yaEnUSD={modoCombinado || yaEnUSDChile}
                  porcentaje={pctSobrePublicidad(ytd(aContenidosDigitales))}
                  onDetalle={modoCombinado ? null : onDetalleContenidos}
                />
                <Row
                  label="Contribución"
                  values={
                    modoCombinado
                      ? filasCombinado["A - Publicidad / Contribución"].usd
                      : vUSD(vContribucionAUSD, vContribucionA)
                  }
                  yaEnUSD={modoCombinado || (pais === "Chile" && verEnDolares)}
                  porcentaje={margenPorcentaje(ytd(contribucionA), ytdPublicidad)}
                  className={styles.marginRow}
                  conceptClassName={styles.conceptCellMargen}
                />
              </>
            )}

            {/* === BLOQUE B — Hardware === */}
            <BloqueBTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              bloqueBCollapsed={bloqueBCollapsed}
              setBloqueBCollapsed={setBloqueBCollapsed}
              hardwareMensualesCombinados={filasCombinado ? filasCombinado["B - Hardware"].usd : ZEROS}
              ingresosHardwareMensual={vHardware}
              ingresosHardwareMensualUSD={vHardwareUSD}
              monthsToShow={mesesVisibles}
              openHardwareIngresosModal={abrirHardwareIngresos}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              ytdIngresosHardware={ytd(vHardware)}
              compraHardwareMensual={vCompraHardware}
              compraHardwareMensualUSD={vCompraHardwareUSD}
              instalacionMensual={vInstalacion}
              instalacionMensualUSD={vInstalacionUSD}
              materialesMensual={vMateriales}
              materialesMensualUSD={vMaterialesUSD}
              otrosCostosMensual={vOtrosHardware}
              otrosCostosMensualUSD={vOtrosHardwareUSD}
              openHardwareFlexModal={noop}
              onDetalleFactura={onDetalleFactura}
              ytdCompraHardware={ytd(vCompraHardware)}
              ytdInstalacion={ytd(vInstalacion)}
              ytdMateriales={ytd(vMateriales)}
              ytdOtrosCostosHardware={ytd(vOtrosHardware)}
              ytdMargenHardware={ytd(vContribucionB)}
              margenPorcentaje={margenPorcentaje}
              margenHardwareMensualUSD={vContribucionBUSD}
              pais={pais}
              verEnDolares={verEnDolares}
              filasCombinado={filasCombinado}
            />

            {/* === BLOQUE C — Gestión & Contenidos === */}
            <BloqueCTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              softwareCarteleriaWindowed={vSoftwareCarteleria}
              softwareCarteleriaWindowedUSD={vSoftwareCarteleriaUSD}
              softwareCarteleriaDirectoUSD={vSoftwareCarteleriaUSD}
              otrosGestionWindowed={vOtrosGestion}
              otrosGestionDirectoUSD={vOtrosGestionUSD}
              contenidosDigitalesCDirectoUSD={vContenidosCUSD}
              margenGestionContenidosWindowed={vContribucionC}
              margenGestionContenidosWindowedUSD={vContribucionCUSD}
              margenGestionContenidosDirectoUSD={vContribucionCUSD}
              ingresosGestionContenidosMensualUSD={vGestionUSD}
              bloqueCCollapsed={bloqueCCollapsed}
              setBloqueCCollapsed={setBloqueCCollapsed}
              gestionMensualesCombinados={filasCombinado ? filasCombinado["C - Gestión & Contenidos"].usd : ZEROS}
              ingresosGestionContenidosMensual={vGestion}
              detalleGestionContenidosPorMes={gestionRegistrosPorMes}
              showGestionContenidosModal={(i) => setDetalleGestion({ mesIndex: i })}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              ytdIngresosGestionContenidos={ytdIngresosGestionContenidos}
              getCostoGestionValue={getCostoGestionValue}
              calcularCostoSoftwareEstimado={calcularCostoSoftwareEstimado}
              verEnDolares={verEnDolares}
              selectedYear={selectedYear}
              getDolarValue={getDolarValue}
              localString={localString}
              pais={pais}
              puedeEditar={false}
              openSoftwareDSModalC={noop}
              openEditGestion={noop}
              ytdSoftwareDS={ytdSoftwareDS}
              ytdOtros={ytdOtros}
              margenGestionContenidosMensual={margenGestionContenidosAjustado}
              ytdMargenGestionContenidos={ytdMargenGestionContenidosAjustado}
              contenidosDigitales={vContenidosC}
              ytdContenidosDigitales={ytd(vContenidosC)}
              onDetalleContenidosC={onDetalleContenidosC}
              margenPorcentaje={margenPorcentaje}
              monthsToShow={mesesVisibles}
              filasCombinado={filasCombinado}
            />

            {/* === BLOQUE D — BTL & otros === */}
            <BloqueDTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              bloqueDCollapsed={bloqueDCollapsed}
              setBloqueDCollapsed={setBloqueDCollapsed}
              btlMensualesCombinados={filasCombinado ? filasCombinado["D - BTL & otros"].usd : ZEROS}
              ingresosBTLMensual={vBTL}
              ingresosBTLMensualUSD={vBTLUSD}
              monthsToShow={mesesVisibles}
              openBTLModal={noop}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              ytdIngresosBTL={ytd(vBTL)}
              comisionEdificiosBTLMensual={vComEdificiosBTL}
              comisionEdificiosBTLMensualUSD={vComEdificiosBTLUSD}
              ytdComisionEdificiosBTL={ytd(vComEdificiosBTL)}
              otrosCostosBTLMensual={vOtrosBTL}
              otrosCostosBTLMensualUSD={vOtrosBTLUSD}
              ytdOtrosCostosBTL={ytd(vOtrosBTL)}
              margenBTLMensual={vContribucionD}
              margenBTLMensualUSD={vContribucionDUSD}
              ytdMargenBTL={ytd(vContribucionD)}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              margenPorcentaje={margenPorcentaje}
              pais={pais}
              verEnDolares={verEnDolares}
              filasCombinado={filasCombinado}
            />

            {/* Mismas clases que el v1 (ER/index.js:3889 y :3910): bordó y azul, no verde. */}
            <Row
              label="Costos Variables"
              // FASE 3 PASO 4 (criterio 1), punto 3 — Sumar Perú: `filasCombinado` (construirFilasER,
              // SOLO restando/sumando filas ya convertidas), nunca `vCostosVariables` (un total
              // aparte, recalculado con el criterio de hoy) — si no, deja de cuadrar con los
              // sub-costos de A-D apenas esos entran en Criterio 1 (10.1, "no quedan dos caminos").
              values={
                modoCombinado
                  ? filasCombinado["Costos Variables"].usd
                  : pais === "Chile" && verEnDolares
                    ? vCostosVariablesUSD
                    : vCostosVariables
              }
              yaEnUSD={modoCombinado || (pais === "Chile" && verEnDolares)}
              porcentaje={porcentajeSobreFacturacion(ytd(costosVariablesMostrar))}
              className={styles.totalCostosRow}
            />
            <Row
              label="Beneficio Bruto"
              values={
                modoCombinado
                  ? filasCombinado["Beneficio Bruto"].usd
                  : pais === "Chile" && verEnDolares
                    ? vBeneficioBrutoUSD
                    : vBeneficioBruto
              }
              yaEnUSD={modoCombinado || (pais === "Chile" && verEnDolares)}
              porcentaje={porcentajeSobreFacturacion(ytd(beneficioBrutoMostrar))}
              className={styles.margenFinalRow}
              conceptClassName={styles.conceptCellMargen}
            />

            {/* === COSTOS FIJOS === */}
            <CostosFijosTable
              modoCombinado={modoCombinado}
              costosFijosCollapsed={costosFijosCollapsed}
              setCostosFijosCollapsed={setCostosFijosCollapsed}
              costosFijosMensualesCombinados={filasCombinado ? filasCombinado["Costos Fijos"].usd : ZEROS}
              monthsToShow={mesesVisibles}
              sumarCostosFijosMes={(i) => vCostosFijos[i] || 0}
              formatCurrency={formatCurrency}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              costosFijosYTD={costosFijosYTD}
              rrhhCollapsed={rrhhCollapsed}
              setRrhhCollapsed={setRrhhCollapsed}
              isEmpleadosRRHHLoading={isEmpleadosRRHHLoading}
              rrhhMensualConBeneficios={vRRHH}
              empleadosRRHH={empleadosRRHH}
              getSueldoBaseVigente={getSueldoBaseVigente}
              selectedYear={selectedYear}
              pais={pais}
              puedeEditar={false}
              nombresCostosFijos={nombresCostosFijos}
              camposCostosFijos={camposCostosFijos}
              meses={meses}
              rawCostosFijos={costosFijosFilas}
              costosFijosData={costosFijosFilas}
              getProjectedCostoFijo={() => 0}
              verEnDolares={verEnDolares}
              getDolarValue={getDolarValue}
              dolarMensual={dolarMensual}
              localString={localString}
              openEditModal={noop}
              onDetalleFactura={onDetalleFactura}
              verUltimos12Meses={verUltimos12Meses}
              costosFijosMensualWindowed={vCostosFijos}
              costosFijosPorConceptoWindowed={costosFijosPorConceptoWindowed}
              filasCombinado={filasCombinado}
              vRRHHUSD={vRRHHUSD}
              vCostosFijosUSD={vCostosFijosUSD}
              vCuentasFijasUSD={vCuentasFijasUSD}
              dolarMensualPromedioChile={dolarMensualPromedioChile}
            />

            {/* === EBITDA → RESULTADO === componente clonado del v1: trae los colapsos de
                EBIT (Depreciación/Amortización) y Beneficio Neto (Intereses/Impuestos),
                sus flechas, los colores y la columna YTD con %. */}
            <EBITDAResultadoTable
              modoCombinado={modoCombinado}
              monthsToShow={mesesVisibles}
              formatCurrency={formatCurrency}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              ebitCollapsed={ebitCollapsed}
              setEbitCollapsed={setEbitCollapsed}
              depreciacionMensual={vDepreciacion}
              amortizacionMensual={vAmortizacion}
              meses={mesesWindowed}
              resultadoCollapsed={resultadoCollapsed}
              setResultadoCollapsed={setResultadoCollapsed}
              vInteresesLocal={vInteresesLocal}
              interesesWindowedMensualUSD={interesesWindowedMensualUSD}
              onDetalleIntereses={onDetalleIntereses}
              verChileYPeru={modoCombinado}
              verEnDolares={verEnDolares}
              pais={pais}
              moneda={moneda}
              verUltimos12Meses={verUltimos12Meses}
              ebitdaMensualWindowed={vEbitda}
              vEbitdaUSD={vEbitdaUSD}
              vEbitLocalTodos={vEbitLocalTodos}
              vEbitUSDTodos={vEbitUSDTodos}
              vBeneficioNetoLocalTodos={vBeneficioNetoLocalTodos}
              vBeneficioNetoUSDTodos={vBeneficioNetoUSDTodos}
              vDepreciacionUSD={vDepreciacionUSD}
              vAmortizacionUSD={vAmortizacionUSD}
              vInteresesUSD={vInteresesUSD}
              vInteresesLocalCriterio1Chile={vInteresesLocalCriterio1Chile}
              facturacionMensualWindowed={vFacturacion}
              filas={filas}
              filasCombinado={filasCombinado}
            />
            <InversionActivosTable
              verChileYPeru={modoCombinado}
              verUnitEconomics={verUnitEconomics}
              verInversionActivos={verInversionActivos}
              selectedYear={selectedYear}
              inversionCollapsed={inversionCollapsed}
              setInversionCollapsed={setInversionCollapsed}
              mesesWindowed={mesesWindowed}
              eMs={mesesVisibles}
              formatCurrency={formatCurrency}
              eBenchmarkInv={eBenchmarkInv}
              eInversion={vInversion}
              eDisponibleInv={eDisponibleInv}
              eValorActivos={vValorActivos}
              inversionMensualesCombinados={inversionMensualesCombinados}
              disponibleInversionCombinados={disponibleInversionCombinados}
              valorActivosCombinados={valorActivosCombinados}
              benchmarkInversionCombinados={benchmarkInversionCombinados}
              verUltimos12Meses={verUltimos12Meses}
              meses={meses}
              monthsToShow={monthsToShow}
            />
            {verAcumulados && (
              <>
                <tr style={{ height: "4px", backgroundColor: "transparent" }}>
                  <td colSpan={14} style={{ padding: 0, border: "none" }} />
                </tr>
                {[
                  { label: "EBITDA", data: acumulados.ebitdaAcum, color: "#27ae60" },
                  { label: "EBIT", data: acumulados.ebitAcum, color: "#2980b9" },
                  { label: "Beneficio Neto", data: acumulados.resultadoAcum, color: "#bb6bd9" },
                ].map(({ label, data, color }) => (
                  <tr key={label} style={{ backgroundColor: "#1a1a2e" }}>
                    <td
                      className={styles.conceptCell}
                      style={{ textAlign: "left", color, fontWeight: "600", fontSize: "0.85rem" }}
                    >
                      {label}
                    </td>
                    {meses.map((_, index) => {
                      const val = data[index];
                      return (
                        <td
                          key={index}
                          className={styles.valueCell}
                          style={{
                            fontSize: "0.8rem",
                            color: val !== null && val < 0 ? "#e74c3c" : "#ccc",
                            fontWeight: "500",
                            padding: "6px 4px",
                            textAlign: "right",
                          }}
                        >
                          {val === null ? "-" : formatCurrency(val, index, null, acumuladosYaEnUSD)}
                        </td>
                      );
                    })}
                    <td
                      className={styles.valueCell}
                      style={{
                        fontSize: "0.8rem",
                        color: data[Math.min(monthsToShow, 12) - 1] < 0 ? "#e74c3c" : color,
                        fontWeight: "600",
                        padding: "6px 4px",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(
                        data[Math.min(monthsToShow, 12) - 1] || 0,
                        Math.min(monthsToShow, 12) - 1,
                        null,
                        acumuladosYaEnUSD
                      )}
                    </td>
                  </tr>
                ))}
              </>
            )}
            <TipoCambioTable
              verEnDolares={verEnDolares}
              verChileYPeru={modoCombinado}
              mesesWindowed={mesesWindowed}
              eMs={mesesVisibles}
              eDolar={enVentana(dolarMensual, dolarMensualAnoAnterior)}
              moneda={moneda}
              localString={localString}
              pais={pais}
              meses={meses}
              monthsToShow={mesesVisibles}
              dolarMensual={dolarMensual}
              dolarMensualOtroPais={dolarMensualOtroPais}
            />
          </tbody>
        </table>
        <DetalleFacturasModal
          isOpen={Boolean(detalleFacturas)}
          titulo={detalleFacturas?.titulo || ""}
          mes={detalleFacturas ? mesesCompletos[detalleFacturas.mesIndex] : ""}
          facturas={detalleFacturas ? costos.detalleDe(detalleFacturas.cuenta, detalleFacturas.mesIndex) : []}
          ajustes={detalleFacturas?.ajustes || []}
          formatCurrency={formatCurrency}
          onClose={() => setDetalleFacturas(null)}
        />
        <DetalleFacturacionModal
          isOpen={Boolean(detalleFacturacion)}
          mes={detalleFacturacion ? mesesCompletos[detalleFacturacion.mesIndex] : ""}
          facturas={facturacionDelMes}
          formatCurrency={formatCurrency}
          pais={pais}
          onClose={() => setDetalleFacturacion(null)}
        />
        <DetalleGestionContenidosModal
          isOpen={Boolean(detalleGestion)}
          mes={detalleGestion ? mesesCompletos[detalleGestion.mesIndex] : ""}
          mesIndex={detalleGestion ? detalleGestion.mesIndex : null}
          items={gestionDelMes}
          formatCurrency={formatCurrency}
          onClose={() => setDetalleGestion(null)}
        />
        <DetalleHardwareIngresosModal
          isOpen={Boolean(detalleHardwareIngresos)}
          monthIndex={detalleHardwareIngresos ? detalleHardwareIngresos.monthIndex : null}
          mesesCompletos={mesesCompletos}
          pais={pais}
          moneda={moneda}
          localString={localString}
          formatCurrency={formatCurrency}
          filas={hardwareDelMes}
          onClose={() => setDetalleHardwareIngresos(null)}
        />
        <DetalleIntermediacionModal
          isOpen={Boolean(detalleIntermediacion)}
          mes={detalleIntermediacion ? mesesCompletos[detalleIntermediacion.mesIndex] : ""}
          comisiones={intermediacionDelMes?.comisiones || []}
          comisionesBroker={intermediacionDelMes?.comisionesBroker || []}
          costosProgramaticos={intermediacionDelMes?.costosProgramaticos || []}
          apx={intermediacionDelMes?.apx || []}
          oportunidades={oportunidades}
          formatCurrency={formatCurrency}
          pais={pais}
          onClose={() => setDetalleIntermediacion(null)}
        />
        <DetalleComisionEdificiosModal
          isOpen={Boolean(detalleComisionEdificios)}
          mes={detalleComisionEdificios ? mesesCompletos[detalleComisionEdificios.mesIndex] : ""}
          comisiones={comisionEdificiosDelMes?.comisiones || []}
          comisionesFijas={comisionEdificiosDelMes?.comisionesFijas || []}
          moneda={moneda(pais)}
          formatCurrency={formatCurrency}
          onClose={() => setDetalleComisionEdificios(null)}
        />
        <DetalleComisionComercialModal
          isOpen={Boolean(detalleComisionComercial)}
          mes={detalleComisionComercial ? mesesCompletos[detalleComisionComercial.mesIndex] : ""}
          filas={comisionComercialDelMes}
          users={users}
          moneda={moneda(pais)}
          formatCurrency={formatCurrency}
          pais={pais}
          onClose={() => setDetalleComisionComercial(null)}
        />
        <InteresesModal
          isOpen={Boolean(detalleIntereses)}
          mes={detalleIntereses?.mesLabel || ""}
          conceptos={interesesConceptos}
          prefijo={interesesPrefijo}
          onClose={() => setDetalleIntereses(null)}
        />
        {dtesSinVincular > 0 && (
          <Link
            to="/admin/facturacion"
            style={{
              display: "inline-block",
              textAlign: "left",
              padding: "4px 8px",
              margin: "24px 0 0",
              background: "#ffecd2",
              borderRadius: 4,
              color: "#e67e22",
              fontSize: "0.7rem",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {dtesSinVincular} factura{dtesSinVincular === 1 ? "" : "s"} del {enteFiscal} sin clasificar para{" "}
            {selectedYear} → no entran al ER hasta asignarles cuenta
          </Link>
        )}
        {ventasCerradasSinVincular && (
          <div style={{ marginTop: 6 }}>
            <span
              style={{
                display: "inline-block",
                textAlign: "left",
                padding: "4px 8px",
                background: "#e8f4fd",
                borderRadius: 4,
                color: "#2980b9",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              {ventasCerradasSinVincular.count} venta{ventasCerradasSinVincular.count === 1 ? "" : "s"} pendiente
              {ventasCerradasSinVincular.count === 1 ? "" : "s"} de vincular en{" "}
              {mesesCompletos[ventasCerradasSinVincular.mesIdx]} por {formatCurrency(ventasCerradasSinVincular.total)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
